import { useState, useEffect, useCallback, useRef, Component, type ReactNode } from 'react'
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.ts'
import { useModulesStore } from '@/stores/modules.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  getTenants, reorderTenants, createTenant, getTenant, updateTenant,
  getTenantSettings, updateTenantSettings, syncCompanyFromSap, getTenantAdapters, saveTenantAdapter,
  deleteTenantAdapter, getTenantTanssCategories, updateTenantTanssCategories,
  getTanssCompanyCategoryTypes,
} from '@/api/tenantAdapter.ts'
import {
  getUserSettings, updateUserSettings, getSystemSettings, updateSystemSettings,
  getDevModeModules, setDevModeModule,
} from '@/api/settingsAdapter.ts'
import {
  getInternalUsers, createUser,
  updateUser as updateUserApi, deleteUser as deleteUserApi,
  getUserModules, setUserModules, deleteUserModule,
  getPermissions, setPermissionOverride, getPermissionGroups, setUserPermissionGroups,
  getUserTenantMemberships, addUserTenantMembership, updateUserTenantMembership,
  deleteUserTenantMembership, setUserTanssCredentials,
} from '@/api/usersAdapter.ts'
import { getDealsPhaseMapping, getDealsTagsConfig, saveDealsPhaseMapping } from '@/api/dealsAdapter.ts'
import {
  getScoringConfig, saveScoringConfig, recalculateScoring,
  getGeoZones, createGeoZone,
  deleteGeoZone as deleteGeoZoneApi,
  updateGeoZone as updateGeoZoneApi,
  getPainPoints,
  generatePainPoints as generatePainPointsApi,
  togglePainPoint as togglePainPointApi,
  redistributeTasks,
} from '@/api/acquisitionAdapter.ts'
import {
  getCalendarSyncStatus, runCalendarSyncNow, restoreDeletedAppointments, restoreOutlookTitles,
} from '@/api/calendarSyncAdapter.ts'
import { testAlsoConnection } from '@/api/alsoAdapter.ts'
import { testEsetConnection } from '@/api/esetAdapter.ts'
import { getAiModels } from '@/api/aiAdapter.ts'
import {
  getBankAccounts, deleteBankAccount, importBankAccountsFromSap, createBankAccount,
  finalizeRequisition, createRequisition, getInstitutions, importBatchTransactions,
} from '@/api/buchhaltungAdapter.ts'
import { getGlobalAdapters, saveGlobalAdapter, deleteGlobalAdapter } from '@/api/globalAdaptersAdapter.ts'
import { getNinjaAutoSync, setNinjaAutoSync, getNinjaStatus, type NinjaStatus } from '@/api/ninjaAdapter.ts'
import { testEgisConnection } from '@/api/egisAdapter.ts'
import {
  searchTanssCompanies, getKatTagGroups, getTeamsBridgeStartMessage,
  previewTeamsBridgeStartMessage, saveTeamsBridgeStartMessage,
  getUserTanssCredentials, updateUserTanssCredentials,
  testTanssPortalConnection,
  type UserTanssCredentials,
  type TanssPortalTestResult,
} from '@/api/tanssAdapter.ts'
import { getSapPriceLists, getSapManufacturers } from '@/api/sapAdapter.ts'
import {
  useTanssSapSettings,
  useUpdateTanssSapSettings,
  type TanssSapAiMode,
  type TanssSapSettings,
} from '../abrechnung/tanssSapApi.ts'
import { useInvoiceTemplates } from '../abrechnung/InvoiceTemplatesTab.tsx'
import { AswTenantSettingsSection } from '../abrechnung/AswSettingsSection.tsx'
import { cn } from '@/lib/utils.ts'
import {
  ArrowDown, ArrowUp,
  UserCheck, UserX, Trash2, Shield, Sun, Moon, Save,
  Users, User, Building2, Link, KeyRound, Plus, X, Boxes, Settings, Lock,
  Code2, AlertTriangle, Bot, Receipt, ChevronDown, Target, MapPin,
  RefreshCw, TrendingUp, Globe, Clock, Loader2, Calendar, ClipboardList,
  Building, Trash, Database,
} from 'lucide-react'
import { getModulesByCategory } from '@shared/types'
import type { ModuleStatus, Tenant, ModuleCategory } from '@shared/types'
import { ScheduledJobsTab } from './ScheduledJobsTab.tsx'
import { PermissionGroupsTab } from './PermissionGroupsTab.tsx'
import { WissensspeicherTab } from './WissensspeicherTab.tsx'
import { SharedMailboxesTab } from './SharedMailboxesTab.tsx'
import { EmailTemplatesTab } from './EmailTemplatesTab.tsx'
import { ModuleLocksTab } from './ModuleLocksTab.tsx'
import { KiStatistikTab } from './KiStatistikTab.tsx'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractErrorMessage(err: unknown): string {
  const axErr = err as { response?: { data?: unknown; status?: number }; message?: string }
  const data = axErr?.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
  }
  if (typeof axErr?.message === 'string') return axErr.message
  return ''
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface ManagedUser {
  id: string
  email: string
  name: string
  role: string
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
}

const SAP_ACCESS_MODE_LABELS: Record<'none' | 'tenant' | 'own', string> = {
  none: 'Kein Zugriff',
  tenant: 'Zentrale Zugangsdaten',
  own: 'Eigene Zugangsdaten',
}

interface UserSettings {
  theme?: 'light' | 'dark'
  sapUser?: string
  sapPassword?: string
  _decryptWarning?: string[]
}

type SystemSettings = Record<string, string>

type Tab = 'user' | 'users' | 'allgemein' | 'ki' | 'adapters' | 'tenant' | 'modules' | 'jobs' | 'locks'
type UserSubTab = 'general' | 'credentials'
type TenantSubTab = 'overview' | 'stammdaten' | 'invoices' | 'vertrieb' | 'adapters' | 'banks' | 'customerPortal' | 'finanzen'
type KiSubTab = 'ai-models' | 'wissensspeicher' | 'statistik'
type AdaptersSubTab = 'adapters' | 'shared-mailboxes' | 'email-templates'
type UsersSubTab = 'users' | 'groups'

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  technician: 'Techniker',
  sales: 'Vertrieb',
  accounting: 'Buchhaltung',
  viewer: 'Nur Lesen',
}

const ROLES = ['admin', 'technician', 'sales', 'accounting', 'viewer'] as const

// ─── Tab ↔ URL slug mappings ────────────────────────────────────────────────

const TAB_SLUGS: Record<Tab, string> = {
  user: 'meine-einstellungen',
  allgemein: 'allgemein',
  ki: 'ki',
  adapters: 'adapter',
  modules: 'module',
  users: 'benutzer',
  jobs: 'geplante-aufgaben',
  locks: 'sperren',
  tenant: 'firma',
}

const SLUG_TO_TAB: Record<string, Tab> = Object.fromEntries(
  (Object.entries(TAB_SLUGS) as Array<[Tab, string]>).map(([k, v]) => [v, k]),
)

const TENANT_SUB_SLUGS: Record<TenantSubTab, string> = {
  overview: 'uebersicht',
  stammdaten: 'stammdaten',
  adapters: 'adapter',
  banks: 'bankkonten',
  invoices: 'rechnungseingang',
  vertrieb: 'vertrieb',
  customerPortal: 'kundenportal',
  finanzen: 'finanzen',
}
const SLUG_TO_TENANT_SUB: Record<string, TenantSubTab> = Object.fromEntries(
  (Object.entries(TENANT_SUB_SLUGS) as Array<[TenantSubTab, string]>).map(([k, v]) => [v, k]),
)

const USER_SUB_SLUGS: Record<UserSubTab, string> = {
  general: 'allgemein',
  credentials: 'zugangsdaten',
}
const SLUG_TO_USER_SUB: Record<string, UserSubTab> = Object.fromEntries(
  (Object.entries(USER_SUB_SLUGS) as Array<[UserSubTab, string]>).map(([k, v]) => [v, k]),
)

const KI_SUB_SLUGS: Record<KiSubTab, string> = {
  'ai-models': 'ki-modelle',
  wissensspeicher: 'wissensspeicher',
  statistik: 'statistik',
}
const SLUG_TO_KI_SUB: Record<string, KiSubTab> = Object.fromEntries(
  (Object.entries(KI_SUB_SLUGS) as Array<[KiSubTab, string]>).map(([k, v]) => [v, k]),
)

const ADAPTERS_SUB_SLUGS: Record<AdaptersSubTab, string> = {
  adapters: 'adapter',
  'shared-mailboxes': 'postfaecher',
  'email-templates': 'email-vorlagen',
}
const SLUG_TO_ADAPTERS_SUB: Record<string, AdaptersSubTab> = Object.fromEntries(
  (Object.entries(ADAPTERS_SUB_SLUGS) as Array<[AdaptersSubTab, string]>).map(([k, v]) => [v, k]),
)

const USERS_SUB_SLUGS: Record<UsersSubTab, string> = {
  users: 'benutzer',
  groups: 'gruppen',
}
const SLUG_TO_USERS_SUB: Record<string, UsersSubTab> = Object.fromEntries(
  (Object.entries(USERS_SUB_SLUGS) as Array<[UsersSubTab, string]>).map(([k, v]) => [v, k]),
)

type VertriebSection = 'aufgaben' | 'scoring' | 'geo' | 'painpoints' | 'prompts' | 'kalender'
const VERTRIEB_SECTION_SLUGS: Record<VertriebSection, string> = {
  aufgaben: 'aufgaben',
  scoring: 'icp-scoring',
  geo: 'geo-zonen',
  painpoints: 'pain-points',
  prompts: 'brief-prompts',
  kalender: 'kalender',
}
const SLUG_TO_VERTRIEB_SECTION: Record<string, VertriebSection> = Object.fromEntries(
  (Object.entries(VERTRIEB_SECTION_SLUGS) as Array<[VertriebSection, string]>).map(([k, v]) => [v, k]),
)

const MODULE_CATEGORY_SLUGS: Record<ModuleCategory, string> = {
  'Mein Arbeitsplatz': 'arbeitsplatz',
  'Kunden': 'kunden',
  'Lieferanten': 'lieferanten',
  'Vertrieb': 'vertrieb',
  'Finanzen': 'finanzen',
  'Stammdaten': 'stammdaten',
  'Disposition': 'disposition',
  'Administration': 'administration',
}
const SLUG_TO_MODULE_CATEGORY: Record<string, ModuleCategory> = Object.fromEntries(
  (Object.entries(MODULE_CATEGORY_SLUGS) as Array<[ModuleCategory, string]>).map(([k, v]) => [v, k]),
)

/** Parse `/einstellungen/{s0}/{s1}/{s2}` path into up to three segments. */
function useSettingsSegments(): [string | undefined, string | undefined, string | undefined] {
  const { pathname } = useLocation()
  const after = pathname.replace(/^\/einstellungen\/?/, '')
  const segs = after ? after.split('/').filter(Boolean) : []
  return [segs[0], segs[1], segs[2]]
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function SettingsPage() {
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'
  const navigate = useNavigate()
  const location = useLocation()

  const [seg0] = useSettingsSegments()

  // Derive active tab from URL path
  const activeTab: Tab = (seg0 ? SLUG_TO_TAB[seg0] : undefined) ?? 'user'

  const [tenantList, setTenantList] = useState<Tenant[]>([])
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)

  // Fetch all tenants (including inactive) for admin settings
  useEffect(() => {
    if (isAdmin) {
      getTenants().then(({ data }) => setTenantList(data)).catch(() => {})
    }
  }, [isAdmin])

  // Auto-select primary tenant
  useEffect(() => {
    if (!selectedTenantId && tenantList.length > 0) {
      const primary = tenantList.find((t: Tenant) => t.isPrimary) ?? tenantList[0]
      if (primary) setSelectedTenantId(primary.id)
    }
  }, [tenantList, selectedTenantId])

  // Redirect legacy ?tab= query param URLs to the new path-based structure
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const oldTab = params.get('tab')
    if (!oldTab) return
    const oldSubTab = params.get('subtab') ?? ''
    const legacyMap: Record<string, string> = {
      user: '/einstellungen/meine-einstellungen',
      allgemein: '/einstellungen/allgemein',
      ki: '/einstellungen/ki',
      adapters: '/einstellungen/adapter',
      modules: '/einstellungen/module',
      users: '/einstellungen/benutzer',
      jobs: '/einstellungen/geplante-aufgaben',
      locks: '/einstellungen/sperren',
      tenant: '/einstellungen/firma',
      // legacy keys
      wissensspeicher: '/einstellungen/ki/wissensspeicher',
      groups: '/einstellungen/benutzer/gruppen',
      'shared-mailboxes': '/einstellungen/adapter/postfaecher',
    }
    if (oldTab === 'allgemein' && (oldSubTab === 'wissensspeicher' || oldSubTab === 'ai-models')) {
      navigate(`/einstellungen/ki/${KI_SUB_SLUGS[oldSubTab as KiSubTab] ?? oldSubTab}`, { replace: true })
      return
    }
    const base = legacyMap[oldTab] ?? '/einstellungen/meine-einstellungen'
    navigate(base, { replace: true })
  }, [location.search, navigate])

  const selectedTenant = tenantList.find((t: Tenant) => t.id === selectedTenantId)

  const [tenantError, setTenantError] = useState('')

  // Save tenant order centrally via PUT /tenants/reorder.
  // The new order is rendered optimistically immediately; on error
  // we reload the list from the server.
  const moveTenant = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= tenantList.length) return
    const next = [...tenantList]
    const [moved] = next.splice(index, 1)
    if (!moved) return
    next.splice(target, 0, moved)
    setTenantList(next)
    try {
      await reorderTenants(next.map((t) => t.id))
    } catch (err) {
      setTenantError('Reihenfolge konnte nicht gespeichert werden: ' + extractErrorMessage(err))
      getTenants().then(({ data }) => setTenantList(data)).catch(() => {})
    }
  }

  const handleCreateTenant = async () => {
    setTenantError('')
    try {
      const { data } = await createTenant({
        code: `FIRMA${tenantList.length + 1}`,
        name: `Neue Firma ${tenantList.length + 1}`,
        isPrimary: tenantList.length === 0,
      })
      setTenantList((prev) => [...prev, data])
      setSelectedTenantId(data.id)
      navigate('/einstellungen/firma')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setTenantError(`Firma konnte nicht angelegt werden.${msg ? ' ' + msg : ''}`)
    }
  }

  const staticTabs: { key: Tab; label: string; icon: typeof User; adminOnly?: boolean }[] = [
    { key: 'user', label: 'Meine Einstellungen', icon: User },
    { key: 'allgemein', label: 'Allgemein', icon: Settings, adminOnly: true },
    { key: 'ki', label: 'KI', icon: Bot, adminOnly: true },
    { key: 'adapters', label: 'Adapter & Postfächer', icon: Settings, adminOnly: true },
    { key: 'modules', label: 'Module', icon: Boxes, adminOnly: true },
    { key: 'users', label: 'Benutzer & Rechte', icon: Users, adminOnly: true },
    { key: 'jobs', label: 'Geplante Aufgaben', icon: Clock, adminOnly: true },
    { key: 'locks', label: 'Modul-Sperren', icon: Lock },
  ]

  const visibleTabs = staticTabs.filter((t) => !t.adminOnly || isAdmin)

  return (
    <div className="p-6">
      <PageHeader title="Einstellungen" />

      <Tabs value={activeTab} onValueChange={(v) => navigate('/einstellungen/' + TAB_SLUGS[v as Tab])}>
        <TabsList className="mb-6">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key}>
              <Icon className="w-4 h-4" />
              {label}
            </TabsTrigger>
          ))}

          {/* Company tab with dropdown */}
          {isAdmin && (
            <Dropdown>
              <DropdownTrigger asChild>
                <TabsTrigger
                  value="tenant"
                  onClick={(e) => {
                    // If not yet on tenant tab, switch first (don't open dropdown)
                    if (activeTab !== 'tenant') {
                      e.preventDefault()
                      navigate('/einstellungen/firma')
                    }
                  }}
                >
                  <Building2 className="w-4 h-4" />
                  {tenantList.length > 0
                    ? `Firma: ${selectedTenant?.name ?? '...'}`
                    : 'Firmen'}
                  <ChevronDown className="w-3 h-3" />
                </TabsTrigger>
              </DropdownTrigger>
              {activeTab === 'tenant' && (
                <DropdownContent align="start">
                  {tenantList.map((t: Tenant, index: number) => (
                    <DropdownItem
                      key={t.id}
                      onSelect={() => setSelectedTenantId(t.id)}
                      className={cn(
                        'gap-2',
                        t.id === selectedTenantId && 'bg-brand-50 text-brand-700',
                      )}
                    >
                      <span className="flex-1">{t.name}</span>
                      <span className="flex items-center gap-1">
                        {t.isPrimary && (
                          <Badge variant="brand" size="sm">Primär</Badge>
                        )}
                        {!t.isActive && (
                          <Badge variant="danger" size="sm">Inaktiv</Badge>
                        )}
                        <button
                          type="button"
                          aria-label="Nach oben"
                          disabled={index === 0}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            void moveTenant(index, -1)
                          }}
                          className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ArrowUp className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Nach unten"
                          disabled={index === tenantList.length - 1}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            void moveTenant(index, 1)
                          }}
                          className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ArrowDown className="size-3.5" />
                        </button>
                      </span>
                    </DropdownItem>
                  ))}
                  <DropdownSeparator />
                  <DropdownItem
                    onSelect={() => {
                      handleCreateTenant()
                    }}
                    className="text-brand-600"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Neue Firma anlegen
                  </DropdownItem>
                </DropdownContent>
              )}
            </Dropdown>
          )}
        </TabsList>

        <TabsContent value="user">
          <UserSettingsTab />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="allgemein">
            <AllgemeinTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="ki">
            <KiTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="adapters">
            <AdaptersTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="modules">
            <ModulesSettingsTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="users">
            <UsersTab currentUserId={currentUser!.id} />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="jobs">
            <ScheduledJobsTab />
          </TabsContent>
        )}
        <TabsContent value="locks">
          <ModuleLocksTab />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="tenant">
            {selectedTenantId ? (
              <TenantSettingsTab tenantId={selectedTenantId} />
            ) : (
              <EmptyState
                title="Noch keine Firma angelegt"
                description="Erstellen Sie die erste Firma, um firma-spezifische Einstellungen (SAP, M365, KI, Rechnungseingang, Adapter, etc.) zu konfigurieren."
                icon={<Building2 />}
                action={
                  <>
                    {tenantError && (
                      <StatusBanner type="error" text={tenantError} />
                    )}
                    <Button
                      leadingIcon={<Plus className="w-4 h-4" />}
                      onClick={handleCreateTenant}
                    >
                      Erste Firma anlegen
                    </Button>
                  </>
                }
              />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

// ─── User Settings Tab ───────────────────────────────────────────────────────

const USER_SUB_TABS: { key: UserSubTab; label: string; icon: typeof User }[] = [
  { key: 'general', label: 'Allgemein', icon: Settings },
  { key: 'credentials', label: 'Persönliche Zugangsdaten', icon: KeyRound },
]

function UserSettingsTab() {
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const subTab: UserSubTab = (seg1 ? SLUG_TO_USER_SUB[seg1] : undefined) ?? 'general'

  return (
    <Tabs value={subTab} onValueChange={(v) => navigate('/einstellungen/meine-einstellungen/' + USER_SUB_SLUGS[v as UserSubTab])}>
      <TabsList className="mb-6">
        {USER_SUB_TABS.map(({ key, label, icon: Icon }) => (
          <TabsTrigger key={key} value={key}>
            <Icon className="w-3.5 h-3.5" />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="general">
        <UserGeneralSection />
      </TabsContent>
      <TabsContent value="credentials">
        <UserCredentialsSection />
      </TabsContent>
    </Tabs>
  )
}

function UserGeneralSection() {
  const [settings, setSettings] = useState<UserSettings>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    getUserSettings()
      .then(({ data }) => setSettings(data))
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`Einstellungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const payload: Record<string, unknown> = {}
      if (settings.theme !== undefined) payload.theme = settings.theme
      await updateUserSettings(payload)
      const { data } = await getUserSettings()
      setSettings(data)
      setMessage('Einstellungen gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-8">
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <SettingsSection title="Darstellung" icon={Sun}>
        <div className="flex gap-3">
          {(['light', 'dark'] as const).map((theme) => (
            <Button
              key={theme}
              variant={settings.theme === theme ? 'primary' : 'secondary'}
              leadingIcon={theme === 'light' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              onClick={() => setSettings((s) => ({ ...s, theme }))}
              size="lg"
            >
              {theme === 'light' ? 'Hell' : 'Dunkel'}
            </Button>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Outlook-Kalender-Sync" icon={Calendar}>
        <CalendarSyncPanel />
      </SettingsSection>

      <div className="pt-2 flex items-center gap-3">
        <Button
          onClick={save}
          loading={saving}
          leadingIcon={<Save className="w-4 h-4" />}
          size="lg"
        >
          Speichern
        </Button>
      </div>
    </div>
  )
}

// ─── User Credentials Section ────────────────────────────────────────────────

function TanssCredentialsSection() {
  const [creds, setCreds] = useState<UserTanssCredentials>({ tanssUser: '', tanssPassword: '', tanss2faCode: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [legacySource, setLegacySource] = useState(false)

  useEffect(() => {
    getUserTanssCredentials()
      .then(({ data }) => {
        setCreds(data)
        setLegacySource(!!data._legacySource)
      })
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`TANSS-Zugangsdaten konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await updateUserTanssCredentials({ tanssUser: creds.tanssUser, tanssPassword: creds.tanssPassword, tanss2faCode: creds.tanss2faCode })
      const { data } = await getUserTanssCredentials()
      setCreds(data)
      setLegacySource(!!data._legacySource)
      setMessage('TANSS-Zugangsdaten gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <SettingsSection title="TANSS Zugangsdaten" icon={KeyRound}>
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}
      {legacySource && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Zugangsdaten werden aus dem alten Speicherort gelesen. Bitte einmal speichern um sie in das neue Format zu übernehmen.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SettingsInput
          label="Benutzername"
          value={creds.tanssUser}
          onChange={(v) => setCreds((s) => ({ ...s, tanssUser: v }))}
        />
        <SettingsInput
          label="Passwort"
          type="password"
          value={creds.tanssPassword}
          onChange={(v) => setCreds((s) => ({ ...s, tanssPassword: v }))}
        />
        <SettingsInput
          label="2FA-Secret (optional)"
          type="password"
          value={creds.tanss2faCode}
          onChange={(v) => setCreds((s) => ({ ...s, tanss2faCode: v }))}
        />
      </div>
      <div className="pt-2">
        <Button onClick={save} loading={saving} leadingIcon={<Save className="w-4 h-4" />} size="lg">
          Speichern
        </Button>
      </div>
    </SettingsSection>
  )
}

function UserCredentialsSection() {
  const [settings, setSettings] = useState<UserSettings>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [debugInfo, setDebugInfo] = useState<{ serverResponse?: unknown; putStatus?: number; putError?: string; putPayload?: unknown } | null>(null)
  const [showDebug, setShowDebug] = useState(false)

  useEffect(() => {
    getUserSettings()
      .then(({ data }) => {
        setSettings(data)
        setDebugInfo((prev) => ({ ...prev, serverResponse: data }))
      })
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`Einstellungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const payload: Record<string, unknown> = {}
      if (settings.sapUser !== undefined)       payload.sapUser       = settings.sapUser
      if (settings.sapPassword !== undefined)   payload.sapPassword   = settings.sapPassword
      setDebugInfo((prev) => ({ ...prev, putPayload: payload }))
      const putRes = await updateUserSettings(payload)
      setDebugInfo((prev) => ({ ...prev, putStatus: putRes.status, putError: undefined }))
      const { data } = await getUserSettings()
      setSettings(data)
      setDebugInfo((prev) => ({ ...prev, serverResponse: data }))
      setMessage('Einstellungen gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      const axErr = err as { response?: { status?: number } }
      setDebugInfo((prev) => ({ ...prev, putStatus: axErr?.response?.status, putError: msg }))
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-8">
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <TanssCredentialsSection />

      <SettingsSection title="SAP Zugangsdaten" icon={KeyRound}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingsInput
            label="Benutzername"
            value={settings.sapUser || ''}
            onChange={(v) => setSettings((s) => ({ ...s, sapUser: v }))}
          />
          <SettingsInput
            label="Passwort"
            type="password"
            value={settings.sapPassword || ''}
            onChange={(v) => setSettings((s) => ({ ...s, sapPassword: v }))}
          />
        </div>
      </SettingsSection>

      <div className="pt-2 flex items-center gap-3">
        <Button
          onClick={save}
          loading={saving}
          leadingIcon={<Save className="w-4 h-4" />}
          size="lg"
        >
          Speichern
        </Button>
        <Button variant="link" size="xs" onClick={() => setShowDebug((v) => !v)}>
          {showDebug ? 'Debug ausblenden' : 'Debug anzeigen'}
        </Button>
      </div>

      {showDebug && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono text-gray-600 space-y-2">
          <p className="font-semibold text-gray-700">Debug – Server-Antwort (GET /settings/user):</p>
          {debugInfo?.putPayload !== undefined && (
            <>
              <p className="text-gray-500 mt-1">Gesendeter PUT-Payload:</p>
              <pre className="whitespace-pre-wrap break-all text-blue-700">{JSON.stringify(debugInfo.putPayload, null, 2)}</pre>
            </>
          )}
          {debugInfo?.putStatus !== undefined && (
            <p className={debugInfo.putStatus === 200 ? 'text-green-700' : 'text-red-700'}>
              PUT Status: {debugInfo.putStatus}{debugInfo.putError ? ` – ${debugInfo.putError}` : ''}
            </p>
          )}
          <p className="text-gray-500 mt-1">Felder nach Reload (GET):</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>sapUser: {JSON.stringify(settings.sapUser ?? '(nicht gesetzt)')}</li>
            <li>sapPassword: {settings.sapPassword ? `"${settings.sapPassword}"` : '(nicht gesetzt)'}</li>
            <li>_decryptWarning: {JSON.stringify(settings._decryptWarning ?? null)}</li>
          </ul>
          <p className="text-gray-500 mt-2">Rohes Server-Objekt (GET):</p>
          <pre className="whitespace-pre-wrap break-all">{JSON.stringify(debugInfo?.serverResponse, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}

// ─── Shared hook for system settings loading ─────────────────────────────────

function useSystemSettings() {
  const [settings, setSettings] = useState<SystemSettings>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const set = (key: string, value: string) => setSettings((s) => ({ ...s, [key]: value }))

  useEffect(() => {
    getSystemSettings()
      .then(({ data }) => setSettings(data))
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`Systemeinstellungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await updateSystemSettings(settings)
      setMessage('Einstellungen gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  return { settings, set, save, saving, loading, message, error }
}

// ─── Allgemein Tab ────────────────────────────────────────────────────────────

function AllgemeinTab() {
  const { settings, set, save, saving, loading, message, error } = useSystemSettings()

  if (loading) return <LoadingState />

  return (
    <div className="space-y-4">
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}
      <SystemGeneralSection settings={settings} set={set} save={save} saving={saving} />
    </div>
  )
}

// ─── KI Tab ──────────────────────────────────────────────────────────────────

function KiTab() {
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const subTab: KiSubTab = (seg1 ? SLUG_TO_KI_SUB[seg1] : undefined) ?? 'ai-models'
  const { settings, set, save, saving } = useSystemSettings()

  return (
    <Tabs value={subTab} onValueChange={(v) => navigate('/einstellungen/ki/' + KI_SUB_SLUGS[v as KiSubTab])}>
      <TabsList className="mb-6">
        <TabsTrigger value="ai-models">
          <Bot className="w-3.5 h-3.5" />
          KI-Modelle
        </TabsTrigger>
        <TabsTrigger value="wissensspeicher">
          <Database className="w-3.5 h-3.5" />
          KI-Wissensspeicher
        </TabsTrigger>
        <TabsTrigger value="statistik">
          <TrendingUp className="w-3.5 h-3.5" />
          Statistik
        </TabsTrigger>
      </TabsList>

      <TabsContent value="ai-models">
        <GlobalAISection settings={settings} set={set} save={save} saving={saving} />
      </TabsContent>
      <TabsContent value="wissensspeicher">
        <WissensspeicherTab />
      </TabsContent>
      <TabsContent value="statistik">
        <KiStatistikTab />
      </TabsContent>
    </Tabs>
  )
}

// ─── Adapters Tab ────────────────────────────────────────────────────────────

function AdaptersTab() {
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const subTab: AdaptersSubTab = (seg1 ? SLUG_TO_ADAPTERS_SUB[seg1] : undefined) ?? 'adapters'

  return (
    <Tabs value={subTab} onValueChange={(v) => navigate('/einstellungen/adapter/' + ADAPTERS_SUB_SLUGS[v as AdaptersSubTab])}>
      <TabsList className="mb-6">
        <TabsTrigger value="adapters">
          <Settings className="w-3.5 h-3.5" />
          Adapter
        </TabsTrigger>
        <TabsTrigger value="shared-mailboxes">
          <Building className="w-3.5 h-3.5" />
          Zentrale Postfächer
        </TabsTrigger>
        <TabsTrigger value="email-templates">
          <Receipt className="w-3.5 h-3.5" />
          E-Mail-Vorlagen
        </TabsTrigger>
      </TabsList>
      <TabsContent value="adapters">
        <div className="space-y-8">
          <GlobalAdaptersOverview />
          <NinjaAutoSyncSection />
        </div>
      </TabsContent>
      <TabsContent value="shared-mailboxes">
        <SharedMailboxesTab />
      </TabsContent>
      <TabsContent value="email-templates">
        <EmailTemplatesTab />
      </TabsContent>
    </Tabs>
  )
}

// ─── Users Tab ───────────────────────────────────────────────────────────────

function UsersTab({ currentUserId }: { currentUserId: string }) {
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const subTab: UsersSubTab = (seg1 ? SLUG_TO_USERS_SUB[seg1] : undefined) ?? 'users'

  return (
    <Tabs value={subTab} onValueChange={(v) => navigate('/einstellungen/benutzer/' + USERS_SUB_SLUGS[v as UsersSubTab])}>
      <TabsList className="mb-6">
        <TabsTrigger value="users">
          <Users className="w-3.5 h-3.5" />
          Benutzer
        </TabsTrigger>
        <TabsTrigger value="groups">
          <Shield className="w-3.5 h-3.5" />
          Rechte-Gruppen
        </TabsTrigger>
      </TabsList>
      <TabsContent value="users">
        <UserManagementSection currentUserId={currentUserId} />
      </TabsContent>
      <TabsContent value="groups">
        <PermissionGroupsTab />
      </TabsContent>
    </Tabs>
  )
}

// ─── Tenant Settings Tab ─────────────────────────────────────────────────────

const TENANT_SUB_TABS: { key: TenantSubTab; label: string; icon: typeof Settings }[] = [
  { key: 'overview', label: 'Übersicht', icon: Building2 },
  { key: 'stammdaten', label: 'Firmenstammdaten', icon: Building },
  { key: 'adapters', label: 'Adapter', icon: Settings },
  { key: 'banks', label: 'Bankkonten', icon: KeyRound },
  { key: 'invoices', label: 'Rechnungseingang', icon: Receipt },
  { key: 'vertrieb', label: 'Vertrieb', icon: TrendingUp },
  { key: 'customerPortal', label: 'Kundenportal', icon: Globe },
  { key: 'finanzen', label: 'Finanzen', icon: Database },
]

function TenantSettingsTab({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const subTab: TenantSubTab = (seg1 ? SLUG_TO_TENANT_SUB[seg1] : undefined) ?? 'overview'
  const [tenantSettings, setTenantSettings] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const set = (key: string, value: string) => setTenantSettings((s) => ({ ...s, [key]: value }))

  useEffect(() => {
    setLoading(true)
    setMessage('')
    setError('')
    getTenantSettings(tenantId)
      .then(({ data }) => setTenantSettings(data))
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`Einstellungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [tenantId])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await updateTenantSettings(tenantId, tenantSettings)
      setMessage('Einstellungen gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-6">
      <Tabs value={subTab} onValueChange={(v) => { navigate('/einstellungen/firma/' + TENANT_SUB_SLUGS[v as TenantSubTab]); setMessage(''); setError('') }}>
        <TabsList>
          {TENANT_SUB_TABS.map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {error && <StatusBanner type="error" text={error} />}
        {message && <StatusBanner type="success" text={message} />}

        <SettingsErrorBoundary fallbackMessage="Fehler beim Laden dieser Sektion.">
          <TabsContent value="overview">
            <TenantOverviewSection tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="stammdaten">
            <FirmenstammdatenSection tenantId={tenantId} settings={tenantSettings} set={set} save={save} saving={saving} setSettings={setTenantSettings} />
          </TabsContent>
          <TabsContent value="adapters">
            <TenantAdaptersOverview tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="banks">
            <TenantBankAccountsSection tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="invoices">
            <TenantInvoiceSection settings={tenantSettings} set={set} save={save} saving={saving} />
          </TabsContent>
          <TabsContent value="vertrieb">
            <VertriebSettingsSection tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="customerPortal">
            <TenantCustomerPortalSection tenantId={tenantId} settings={tenantSettings} set={set} save={save} saving={saving} />
          </TabsContent>
          <TabsContent value="finanzen">
            <TenantFinanzenSection />
          </TabsContent>
        </SettingsErrorBoundary>
      </Tabs>
    </div>
  )
}

// ─── Firmenstammdaten Section ────────────────────────────────────────────────

function FirmenstammdatenSection({ tenantId, settings, set, save, saving, setSettings }: {
  tenantId: string
  settings: Record<string, string>
  set: (key: string, value: string) => void
  save: () => void
  saving: boolean
  setSettings: React.Dispatch<React.SetStateAction<Record<string, string>>>
}) {
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState('')
  const [importError, setImportError] = useState('')

  const importFromSap = async () => {
    setImporting(true)
    setImportMsg('')
    setImportError('')
    try {
      const { data } = await syncCompanyFromSap(tenantId)
      if (data.imported.length === 0) {
        setImportMsg('Alle Felder bereits befüllt — keine neuen Daten aus SAP importiert.')
      } else {
        setImportMsg(`${data.imported.length} Felder aus SAP importiert und gespeichert.`)
        setSettings(data.settings)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'SAP-Import fehlgeschlagen'
      setImportError(msg)
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <SettingsSection title="Firmenstammdaten" icon={Building}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-500">
            Kontakt- und Bankdaten dieser Firma. Werden als Rechnungsabsender auf allen Ausgangsrechnungen verwendet.
          </p>
          <Button variant="secondary" size="sm" onClick={importFromSap} disabled={importing}
            leadingIcon={importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}>
            Aus SAP importieren
          </Button>
        </div>
        {importMsg && <p className="text-xs text-green-600 mb-3">{importMsg}</p>}
        {importError && <p className="text-xs text-red-600 mb-3">{importError}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <SettingsInput
              label="Firmenname"
              value={settings['company.name'] || ''}
              onChange={(v) => set('company.name', v)}
              placeholder="z.B. implec Weser GmbH"
            />
          </div>
          <div className="col-span-2">
            <SettingsInput
              label="Straße"
              value={settings['company.street'] || ''}
              onChange={(v) => set('company.street', v)}
              placeholder="z.B. Musterstraße 1"
            />
          </div>
          <SettingsInput
            label="PLZ"
            value={settings['company.zip'] || ''}
            onChange={(v) => set('company.zip', v)}
            placeholder="z.B. 26919"
          />
          <SettingsInput
            label="Ort"
            value={settings['company.city'] || ''}
            onChange={(v) => set('company.city', v)}
            placeholder="z.B. Brake"
          />
          <SettingsInput
            label="Telefon"
            value={settings['company.phone'] || ''}
            onChange={(v) => set('company.phone', v)}
            placeholder="z.B. 04401/9876-0"
          />
          <SettingsInput
            label="Fax"
            value={settings['company.fax'] || ''}
            onChange={(v) => set('company.fax', v)}
            placeholder="z.B. 04401/9876-99"
          />
          <div className="col-span-2">
            <SettingsInput
              label="E-Mail"
              value={settings['company.email'] || ''}
              onChange={(v) => set('company.email', v)}
              placeholder="z.B. info@implec-weser.de"
            />
          </div>
          <div className="col-span-2">
            <SettingsInput
              label="Geschäftsführer"
              value={settings['company.managingDirector'] || ''}
              onChange={(v) => set('company.managingDirector', v)}
              placeholder="z.B. Max Mustermann"
            />
          </div>
          <SettingsInput
            label="Steuernummer"
            value={settings['company.taxNumber'] || ''}
            onChange={(v) => set('company.taxNumber', v)}
            placeholder="z.B. 123/456/78900"
          />
          <SettingsInput
            label="USt-IdNr."
            value={settings['company.vatId'] || ''}
            onChange={(v) => set('company.vatId', v)}
            placeholder="z.B. DE123456789"
          />
          <div className="col-span-2">
            <SettingsInput
              label="Bank"
              value={settings['company.bankName'] || ''}
              onChange={(v) => set('company.bankName', v)}
              placeholder="z.B. Volksbank"
            />
          </div>
          <SettingsInput
            label="SWIFT/BIC"
            value={settings['company.bic'] || ''}
            onChange={(v) => set('company.bic', v)}
            placeholder="z.B. GENODEF1BRK"
          />
          <SettingsInput
            label="IBAN"
            value={settings['company.iban'] || ''}
            onChange={(v) => set('company.iban', v)}
            placeholder="z.B. DE00 1234 5678 9012 3456 78"
          />
        </div>
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── Tenant Overview Section ────────────────────────────────────────────────

function TenantOverviewSection({ tenantId }: { tenantId: string }) {
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    getTenant(tenantId)
      .then(({ data }) => setTenant(data))
      .catch(() => setError('Mandant konnte nicht geladen werden.'))
      .finally(() => setLoading(false))
  }, [tenantId])

  const save = async () => {
    if (!tenant) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await updateTenant(tenantId, {
        code: tenant.code,
        name: tenant.name,
        description: tenant.description,
        tanssCompanyId: tenant.tanssCompanyId,
        tanssCompanyName: tenant.tanssCompanyName,
        isPrimary: tenant.isPrimary,
        isActive: tenant.isActive,
      })
      setMessage('Mandant aktualisiert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !tenant) return <LoadingState />

  return (
    <div className="space-y-6">
      <SettingsSection title="Firmendaten" icon={Building2}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingsInput
            label="Code (Kürzel)"
            value={tenant.code}
            onChange={(v) => setTenant({ ...tenant, code: v })}
            placeholder="z.B. IMP"
          />
          <SettingsInput
            label="Name"
            value={tenant.name}
            onChange={(v) => setTenant({ ...tenant, name: v })}
            placeholder="z.B. implec GmbH"
          />
          <div className="sm:col-span-2">
            <SettingsInput
              label="Beschreibung"
              value={tenant.description ?? ''}
              onChange={(v) => setTenant({ ...tenant, description: v })}
            />
          </div>
        </div>
        <div className="flex items-center gap-6 mt-4">
          <Checkbox
            label="Primärer Mandant"
            checked={tenant.isPrimary}
            onChange={(e) => setTenant({ ...tenant, isPrimary: e.target.checked })}
          />
          <Checkbox
            label="Aktiv"
            checked={tenant.isActive}
            onChange={(e) => setTenant({ ...tenant, isActive: e.target.checked })}
          />
        </div>
      </SettingsSection>

      <p className="text-caption text-gray-500 italic">
        TANSS-Verknüpfung und Kundentypen-Zuordnung findest du jetzt im Tab{' '}
        <strong>Adapter</strong> → TANSS.
      </p>

      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── Tenant AI Section ──────────────────────────────────────────────────────

/** Known AI providers with their models */
const AI_PROVIDERS: Record<string, { label: string; apiKeyPlaceholder: string; models: { id: string; name: string }[] }> = {
  anthropic: {
    label: 'Anthropic',
    apiKeyPlaceholder: 'sk-ant-...',
    models: [
      { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5' },
      { id: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5' },
      { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6' },
      { id: 'claude-opus-4-6', name: 'Claude Opus 4.6' },
      { id: 'claude-opus-4-7', name: 'Claude Opus 4.7' },
    ],
  },
  openai: {
    label: 'OpenAI',
    apiKeyPlaceholder: 'sk-...',
    models: [
      { id: 'gpt-4o', name: 'GPT-4o' },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
      { id: 'gpt-4.1', name: 'GPT-4.1' },
      { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini' },
      { id: 'gpt-4.1-nano', name: 'GPT-4.1 Nano' },
      { id: 'o3', name: 'o3' },
      { id: 'o3-mini', name: 'o3 Mini' },
      { id: 'o4-mini', name: 'o4 Mini' },
    ],
  },
  google: {
    label: 'Google',
    apiKeyPlaceholder: 'AIza...',
    models: [
      { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
      { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash' },
    ],
  },
  mistral: {
    label: 'Mistral',
    apiKeyPlaceholder: 'api-key...',
    models: [
      { id: 'mistral-large-latest', name: 'Mistral Large' },
      { id: 'mistral-medium-latest', name: 'Mistral Medium' },
      { id: 'mistral-small-latest', name: 'Mistral Small' },
    ],
  },
  meta: {
    label: 'Meta',
    apiKeyPlaceholder: 'api-key...',
    models: [
      { id: 'llama-4-maverick', name: 'Llama 4 Maverick' },
      { id: 'llama-4-scout', name: 'Llama 4 Scout' },
    ],
  },
}

const AI_MODULES = [
  { key: 'ai.model.tickets', label: 'Ticket-Assistent' },
  { key: 'ai.model.angebote', label: 'Angebote / Angebots-Assistent' },
  { key: 'ai.model.artikel', label: 'Artikel-Assistent' },
  { key: 'ai.model.vertrieb', label: 'Vertrieb / Verkaufstexte' },
  { key: 'ai.model.akquise', label: 'Kundengewinnung / Akquise-Briefe' },
  { key: 'ai.model.rechnungen', label: 'Rechnungserkennung' },
]

/** Maps deprecated model IDs to their current equivalents (frontend display only) */
const MODEL_MIGRATIONS: Record<string, string> = {
  'claude-sonnet-4-20250514': 'claude-sonnet-4-6',
  'claude-sonnet-4-5-20241022': 'claude-sonnet-4-5-20250929',
  'claude-sonnet-4-6-20250620': 'claude-sonnet-4-6',
  'claude-opus-4-20250514': 'claude-opus-4-6',
  'claude-opus-4-6-20250620': 'claude-opus-4-6',
  'claude-haiku-4-5': 'claude-haiku-4-5-20251001',
  'claude-sonnet-4-7': 'claude-sonnet-4-6',
}

// Module-level cache so multiple hook usages don't trigger duplicate fetches
type FlatModel = { id: string; name: string; provider: string }
let _modelCache: FlatModel[] | null = null
let _modelCacheTs = 0
const MODEL_CACHE_TTL = 5 * 60 * 1000

function staticModels(): FlatModel[] {
  return Object.entries(AI_PROVIDERS).flatMap(([, p]) =>
    p.models.map((m) => ({ ...m, provider: p.label }))
  )
}

async function loadAiModels(): Promise<FlatModel[]> {
  if (_modelCache && Date.now() - _modelCacheTs < MODEL_CACHE_TTL) return _modelCache
  const res = await getAiModels()
  const providerLabels = Object.fromEntries(Object.entries(AI_PROVIDERS).map(([k, v]) => [k, v.label]))
  const models = Object.entries(res.data).flatMap(([provider, models]) =>
    models.map((m) => ({ ...m, provider: providerLabels[provider] ?? provider }))
  )
  _modelCache = models
  _modelCacheTs = Date.now()
  return models
}

function useAiModels(): FlatModel[] {
  const [models, setModels] = useState<FlatModel[]>(staticModels)
  useEffect(() => {
    loadAiModels().then(setModels).catch(() => { /* keep static fallback */ })
  }, [])
  return models
}

/** Searchable model select component */
function ModelSearchSelect({ value, onChange, models, emptyLabel = 'Global (Standard)' }: {
  value: string
  onChange: (v: string) => void
  models: { id: string; name: string; provider: string }[]
  emptyLabel?: string
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [openUpward, setOpenUpward] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      // Flip upward if less than 260px below the button
      setOpenUpward(window.innerHeight - rect.bottom < 260)
    }
    setOpen((o) => !o)
  }

  const filtered = search.trim()
    ? models.filter((m) =>
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.id.toLowerCase().includes(search.toLowerCase()) ||
        m.provider.toLowerCase().includes(search.toLowerCase())
      )
    : models

  const migratedId = value ? (MODEL_MIGRATIONS[value] ?? value) : value
  const selectedModel = models.find((m) => m.id === migratedId)
  const isDeprecated = value && migratedId !== value
  const displayLabel = selectedModel
    ? `${selectedModel.name}${isDeprecated ? ' (veraltet)' : ''} (${selectedModel.provider})`
    : value
      ? `${value} (unbekannt)`
      : emptyLabel

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 border rounded-md text-body bg-white hover:border-gray-400 transition-colors text-left',
          isDeprecated ? 'border-amber-300' : 'border-gray-300',
        )}
      >
        <span className={cn(value ? 'text-gray-900' : 'text-gray-500', isDeprecated && 'text-amber-700')}>
          {displayLabel}
        </span>
        <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className={cn(
          'absolute z-popover w-full bg-white border border-gray-200 rounded-lg shadow-popover',
          openUpward ? 'bottom-full mb-1' : 'mt-1',
        )}>
          <div className="p-2 border-b border-gray-100">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Modell suchen..."
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            <button
              onClick={() => { onChange(''); setOpen(false); setSearch('') }}
              className={cn(
                'w-full flex items-center px-3 py-2 text-small text-left hover:bg-gray-50 transition-colors',
                !value && 'bg-brand-50 text-brand-700 font-medium',
              )}
            >
              {emptyLabel}
            </button>
            {filtered.map((m) => (
              <button
                key={m.id}
                onClick={() => { onChange(m.id); setOpen(false); setSearch('') }}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2 text-small text-left hover:bg-gray-50 transition-colors',
                  (value === m.id || migratedId === m.id) && 'bg-brand-50 text-brand-700 font-medium',
                )}
              >
                <span>{m.name}</span>
                <span className="text-caption text-gray-400">{m.provider}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-caption text-gray-400 italic">Kein Modell gefunden.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Global AI Section ────────────────────────────────────────────────────────

function GlobalAISection({ settings, set, save, saving }: {
  settings: Record<string, string>
  set: (key: string, value: string) => void
  save: () => void
  saving: boolean
}) {
  const allAvailableModels = useAiModels()
  const [openaiConfigured, setOpenaiConfigured] = useState<boolean | null>(null)
  const [searchParams] = useSearchParams()

  // Re-check whenever the URL changes (e.g. after saving the OpenAI adapter on the
  // Adapters tab and switching back here) so the status indicator updates immediately.
  useEffect(() => {
    getGlobalAdapters()
      .then((res) => {
        const adapters = res.data as Array<{ adapter: string; isActive: boolean }>
        setOpenaiConfigured(adapters.some((a) => a.adapter === 'openai' && a.isActive !== false))
      })
      .catch(() => setOpenaiConfigured(false))
  }, [searchParams])

  return (
    <div className="space-y-6">
      <SettingsSection title="Modell-Zuordnung" icon={Bot}>
        <p className="text-caption text-gray-500 mb-3">
          Standard-Modell pro Modul. API-Keys werden unter <strong>Adapter</strong> konfiguriert. Firmen können diese Einstellung überschreiben.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-caption font-medium text-gray-600 mb-1">Standard-Modell</label>
            <ModelSearchSelect
              value={settings['ai.model.default'] || ''}
              onChange={(v) => set('ai.model.default', v)}
              models={allAvailableModels}
              emptyLabel="Kein Standard gesetzt"
            />
          </div>
          {AI_MODULES.map(({ key, label }) => (
            <div key={key}>
              <label className="block text-caption font-medium text-gray-600 mb-1">{label}</label>
              <ModelSearchSelect
                value={settings[key] || ''}
                onChange={(v) => set(key, v)}
                models={allAvailableModels}
                emptyLabel="Kein Standard (Standard-Modell)"
              />
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="KI-Wissensspeicher (Embeddings)" icon={Database}>
        <p className="text-caption text-gray-500 mb-3">
          Der Wissensspeicher nutzt Vektorembeddings für semantische Suche. Diese werden
          von OpenAI berechnet und erfordern einen separaten API-Key.
        </p>
        <div className="space-y-2 text-caption">
          <div className="flex items-center gap-2 text-gray-700">
            <span className="font-medium">Modell:</span>
            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">text-embedding-3-small</code>
            <span className="text-gray-400">via OpenAI</span>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <span className="font-medium">API-Key:</span>
            {openaiConfigured === null ? (
              <span className="text-gray-400 text-xs">Prüfe…</span>
            ) : openaiConfigured ? (
              <span className="text-green-600 text-xs flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                Konfiguriert
              </span>
            ) : (
              <span className="text-amber-600 text-xs flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Nicht konfiguriert — Embeddings nicht möglich
              </span>
            )}
          </div>
        </div>
        {openaiConfigured === false && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-caption text-amber-800">
            <strong>OpenAI-API-Key fehlt.</strong> Ohne diesen Key können keine Vektorembeddings
            berechnet werden — der Wissensspeicher ist nicht via KI-Suche nutzbar.
            {' '}
            <RouterLink
              to="/einstellungen?tab=adapters"
              className="underline font-medium hover:text-amber-900"
            >
              Jetzt unter Adapter & Postfächer konfigurieren →
            </RouterLink>
          </div>
        )}
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── Tenant Invoice Section ─────────────────────────────────────────────────

function TenantInvoiceSection({ settings, set, save, saving }: {
  settings: Record<string, string>
  set: (key: string, value: string) => void
  save: () => void
  saving: boolean
}) {
  return (
    <div className="space-y-6">
      <SettingsSection title="Rechnungseingang" icon={Receipt}>
        <p className="text-xs text-gray-500 mb-3">
          Konfiguration des Rechnungseingangs für diese Firma. Jede Firma kann eigene Mail-Ordner und OneDrive-Pfade haben.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingsInput
            label="Postfach-Ordner-ID"
            value={settings['invoice.mailboxFolderId'] || ''}
            onChange={(v) => set('invoice.mailboxFolderId', v)}
            placeholder="M365 Ordner-ID"
          />
          <SettingsInput
            label="Verarbeitet-Ordner-ID"
            value={settings['invoice.processedFolderId'] || ''}
            onChange={(v) => set('invoice.processedFolderId', v)}
            placeholder="Ziel-Ordner-ID"
          />
          <SettingsInput
            label="OneDrive-Pfad"
            value={settings['invoice.onedrivePath'] || ''}
            onChange={(v) => set('invoice.onedrivePath', v)}
            placeholder="z.B. /Rechnungen/2024"
          />
          <SettingsInput
            label="Scan-Intervall (Minuten)"
            value={settings['invoice.scanIntervalMin'] || ''}
            onChange={(v) => set('invoice.scanIntervalMin', v)}
            placeholder="z.B. 15"
          />
        </div>
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── Tenant Adapters Config UI ─────────────────────────────────────────────

const ADAPTER_LABELS: Record<string, string> = {
  // KI-Anbieter
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google (Gemini)',
  mistral: 'Mistral',
  meta: 'Meta (Llama)',
  // Other adapters
  sap: 'SAP Business One',
  also: 'ALSO Marketplace',
  sevenio: 'seven.io SMS',
  dhl: 'DHL Paket',
  epost: 'E-POSTBUSINESS',
  pensaki: 'PENSAKI',
  'eset-ema': 'ESET MSP Administrator 2 (mspapi.eset.com)',
  'eset-protect': 'ESET PROTECT On-Prem (REST, ≥ v13.0)',
  unifi: 'UniFi',
  connectwise: 'ConnectWise Automate',
  mailstore: 'MailStore',
  nospamproxy: 'NoSpamProxy',
  tanss: 'TANSS',
  m365: 'Microsoft 365',
  letterxpress: 'LetterXpress',
  egis: 'EGIS Business Connector',
  osm: 'OpenStreetMap (Overpass)',
  'google-places': 'Google Places API',
  apollo: 'Apollo.io Enrichment',
  kundenschreiber: 'Kundenschreiber',
  gocardless: 'GoCardless Bank Account Data (PSD2)',
  confluence: 'Confluence Cloud (Wiki-Sync)',
  'tanss-teams-bridge': 'TANSS ↔ Microsoft-Teams-Bridge',
  openregister: 'OpenRegister (Handelsregister)',
  airkey: 'EVVA Airkey (Schlüsselverwaltung)',
  whatsapp: 'WhatsApp via Twilio',
  ninjaone: 'NinjaOne RMM',
}

interface AdapterFieldDef {
  key: string
  label: string
  type: 'text' | 'password' | 'number' | 'toggle'
  placeholder?: string
  help?: string
  /** Fest vordefinierter Default-Wert, wird beim Anlegen eines neuen Adapters automatisch uebernommen. */
  default?: string
}

const ADAPTER_FIELDS: Record<string, AdapterFieldDef[]> = {
  anthropic: [
    { key: 'apiKey', label: 'API-Key', type: 'password', placeholder: 'sk-ant-...' },
  ],
  openai: [
    { key: 'apiKey', label: 'API-Key', type: 'password', placeholder: 'sk-...' },
  ],
  google: [
    { key: 'apiKey', label: 'API-Key (Gemini)', type: 'password', placeholder: 'AIza...' },
  ],
  mistral: [
    { key: 'apiKey', label: 'API-Key', type: 'password', placeholder: 'api-key...' },
  ],
  meta: [
    { key: 'apiKey', label: 'API-Key', type: 'password', placeholder: 'api-key...' },
  ],
  tanss: [
    { key: 'baseUrl', label: 'Base-URL', type: 'text', placeholder: 'https://tanss.example.de/backend', help: 'TANSS-Server-URL ohne /api/v1 Suffix' },
    { key: 'erpApiKey', label: 'ERP-API-Key', type: 'password', help: 'Nur für TANSS-ERP-Funktionen (Abrechnung etc.). Normale Zugriffe laufen über User-Credentials in den Benutzereinstellungen.' },
    { key: 'portalUsername', label: 'Kundenportal-Benutzer', type: 'text', placeholder: 'z. B. portal.service', help: 'Zentraler TANSS-Account für alle Aktionen des Kundenportals (Termine buchen, Zeiten abfragen, Tickets lesen). Dieser Account wird nicht mit einem SysMan-User verlinkt — Credentials werden hier direkt gespeichert.' },
    { key: 'portalPassword', label: 'Kundenportal-Passwort', type: 'password', help: 'Passwort des zentralen Kundenportal-Accounts.' },
    { key: 'portal2faSecret', label: 'Kundenportal-2FA-Secret', type: 'password', help: 'Base32-kodiertes TOTP-Secret (optional). Leer lassen, wenn 2FA beim Kundenportal-Account deaktiviert ist.' },
  ],
  sap: [
    { key: 'baseUrl', label: 'Service Layer URL', type: 'text', placeholder: 'https://sap.example.de:50000/b1s/v1' },
    { key: 'companyDb', label: 'Company DB', type: 'text' },
    { key: 'username', label: 'Benutzername', type: 'text' },
    { key: 'password', label: 'Passwort', type: 'password' },
  ],
  osm: [
    { key: 'overpassUrl', label: 'Overpass-API URL', type: 'text', placeholder: 'https://overpass-api.de/api/interpreter', default: 'https://overpass-api.de/api/interpreter' },
    { key: 'maxRequestsPer10s', label: 'Max. Requests / 10s', type: 'number', help: 'Rate-Limit (Standard: 2)', default: '2' },
    { key: 'cacheTtlHours', label: 'Cache-TTL (Stunden)', type: 'number', help: 'Standard: 24', default: '24' },
  ],
  'google-places': [
    { key: 'apiKey', label: 'Google API-Key', type: 'password', help: 'Places API (New) muss aktiviert sein' },
    { key: 'maxResultsPerQuery', label: 'Max. Ergebnisse pro Abfrage', type: 'number', help: 'Standard: 20, max. 20' },
  ],
  apollo: [
    { key: 'apiKey', label: 'Apollo API-Key', type: 'password' },
    { key: 'maxCreditsPerMonth', label: 'Max. Credits / Monat', type: 'number', help: 'Monatliches Limit (Standard: 100)' },
  ],
  also: [
    {
      key: 'baseUrl',
      label: 'Base-URL',
      type: 'text',
      placeholder: 'https://marketplace.also.de/SimpleAPI/SimpleAPIService.svc/rest',
      default: 'https://marketplace.also.de/SimpleAPI/SimpleAPIService.svc/rest',
      help: 'Endpunkt der ALSO Marketplace Simple API (ohne trailing Slash). Regionaler Host: DE = also.de, CH = also.ch, NL = also.nl, AT = also.at.',
    },
    {
      key: 'username',
      label: 'Benutzername',
      type: 'text',
      placeholder: 'api.user@firma.de',
      help: 'ALSO Marketplace API-User. MFA muss am Account deaktiviert sein – die Simple API unterstützt keine 2FA.',
    },
    {
      key: 'password',
      label: 'Passwort',
      type: 'password',
      help: 'Wird verschlüsselt gespeichert (AES-GCM).',
    },
    {
      key: 'resellerAccountId',
      label: 'Reseller-AccountId',
      type: 'text',
      placeholder: '868723',
      default: '868723',
      help: 'Numerische AccountId aus der Marketplace-URL (z.B. /Account/View/868723/Overview).',
    },
  ],
  sevenio: [
    { key: 'apiKey', label: 'API-Key', type: 'password' },
  ],
  dhl: [
    { key: 'apiKey', label: 'API-Key', type: 'password' },
    { key: 'ekp', label: 'EKP-Nummer', type: 'text' },
    { key: 'password', label: 'Passwort', type: 'password' },
  ],
  epost: [
    { key: 'apiKey', label: 'API-Key', type: 'password' },
    { key: 'username', label: 'Benutzername', type: 'text' },
  ],
  pensaki: [
    { key: 'apiUrl', label: 'API-URL', type: 'text', placeholder: 'https://www.pensaki.com/api', default: 'https://www.pensaki.com/api' },
    { key: 'apiKey', label: 'API-Key', type: 'password' },
  ],
  'eset-ema': [
    { key: 'baseUrl',  label: 'Base-URL', type: 'text',
      placeholder: 'https://mspapi.eset.com/api',
      default: 'https://mspapi.eset.com/api',
      help: 'In der Regel nicht ändern' },
    { key: 'username', label: 'MSP-Benutzer', type: 'text',
      help: 'Login wie unter msp.eset.com — der Account muss API-fähig sein' },
    { key: 'password', label: 'MSP-Passwort', type: 'password' },
  ],
  'eset-protect': [
    { key: 'baseUrl',  label: 'Server-URL', type: 'text',
      placeholder: 'https://protect.example.de:9443',
      help: 'Default-Port 9443, REST-API muss in PROTECT aktiviert sein' },
    { key: 'username', label: 'API-Benutzer', type: 'text',
      help: 'NICHT der Administrator — separater User mit Permission-Set' },
    { key: 'password', label: 'Passwort', type: 'password' },
    { key: 'allowSelfSignedCert', label: 'Self-Signed-Zertifikat akzeptieren',
      type: 'toggle', default: 'true' },
  ],
  unifi: [
    { key: 'apiKey', label: 'API-Key', type: 'password', placeholder: 'x_xxxxxxxxxxxxxxxxxx',
      help: 'UniFi Cloud API-Key aus ui.com → API Keys' },
    { key: 'hostId', label: 'Host-ID (optional)', type: 'text',
      help: 'Für Multi-Host-Setups; leer lassen um alle Hosts abzurufen' },
    { key: 'siteId', label: 'Site-ID (optional)', type: 'text',
      help: 'Für Multi-Site-Setups; leer lassen um alle Sites abzurufen' },
  ],
  connectwise: [
    { key: 'serverUrl', label: 'Server-URL', type: 'text' },
    { key: 'username', label: 'Benutzername', type: 'text' },
    { key: 'password', label: 'Passwort', type: 'password' },
  ],
  mailstore: [
    { key: 'serverUrl', label: 'Server-URL', type: 'text' },
    { key: 'username', label: 'Benutzername', type: 'text' },
    { key: 'password', label: 'Passwort', type: 'password' },
  ],
  nospamproxy: [
    { key: 'serverUrl', label: 'Server-URL', type: 'text' },
    { key: 'apiKey', label: 'API-Key', type: 'password' },
  ],
  m365: [
    { key: 'tenantId', label: 'Tenant-ID', type: 'text' },
    { key: 'clientId', label: 'Client-ID', type: 'text' },
    { key: 'clientSecret', label: 'Client-Secret', type: 'password' },
    { key: 'redirectUri', label: 'Redirect-URI', type: 'text' },
  ],
  letterxpress: [
    { key: 'username', label: 'Benutzername', type: 'text' },
    { key: 'apiKey', label: 'API-Key', type: 'password' },
  ],
  egis: [
    { key: 'baseUrl', label: 'Base-URL', type: 'text', placeholder: 'https://www.egis-online.de', default: 'https://www.egis-online.de', help: 'Nur Domain ohne Pfad — z. B. https://www.egis-online.de (NICHT mit /EBC/schema/... Suffix)' },
    { key: 'login', label: 'Login', type: 'text' },
    { key: 'password', label: 'Passwort', type: 'password' },
    { key: 'erpName', label: 'ERP-Name', type: 'text', placeholder: 'SystemhausManager', help: 'Von EGIS vergebener ERP-Bezeichner (bei EGIS erfragen, falls HTTP 500)' },
  ],
  kundenschreiber: [
    { key: 'apiUrl', label: 'API-URL', type: 'text', placeholder: 'https://api.kundenschreiber.de/functions/v1/public-api/v1', default: 'https://api.kundenschreiber.de/functions/v1/public-api/v1' },
    { key: 'apiKey', label: 'API-Key', type: 'password', help: 'ks_live_... oder ks_test_...' },
  ],
  gocardless: [
    { key: 'baseUrl', label: 'Base-URL', type: 'text', placeholder: 'https://bankaccountdata.gocardless.com/api/v2', default: 'https://bankaccountdata.gocardless.com/api/v2' },
    { key: 'secretId', label: 'Secret ID', type: 'text', help: 'Aus dem GoCardless User Portal' },
    { key: 'secretKey', label: 'Secret Key', type: 'password', help: 'Aus dem GoCardless User Portal' },
  ],
  confluence: [
    {
      key: 'baseUrl',
      label: 'Confluence-URL',
      type: 'text',
      placeholder: 'https://company.atlassian.net',
      help: 'Cloud-Instanz-URL ohne abschließenden Slash. Für Confluence Cloud: https://<subdomain>.atlassian.net',
    },
    {
      key: 'email',
      label: 'E-Mail',
      type: 'text',
      placeholder: 'name@firma.de',
      help: 'Atlassian-Account-E-Mail des API-Benutzers.',
    },
    {
      key: 'apiToken',
      label: 'API-Token',
      type: 'password',
      help: 'Atlassian API-Token (nicht das Login-Passwort). Erstellen unter id.atlassian.com → Sicherheit → API-Token.',
    },
  ],
  openregister: [
    { key: 'apiKey', label: 'API-Key', type: 'password', help: 'Bearer-Token von openregister.de (kostenloser Tarif: 50 Credits/Monat)' },
    { key: 'monthlyBudget', label: 'Monatsbudget (Credits)', type: 'number', help: 'Standard: 50. Suche = 1 Credit, vollständige Details = 10 Credits.', default: '50' },
  ],
  'tanss-teams-bridge': [
    {
      key: 'webhookBaseUrl',
      label: 'Webhook-Basis-URL',
      type: 'text',
      placeholder: 'https://sysman.implec.de/api',
      help: 'Externe Basis-URL des Backends — Microsoft Graph schickt Notifications an <baseUrl>/webhooks/graph/teams. Pflicht: ueber HTTPS erreichbar.',
    },
    {
      key: 'clientStateSecret',
      label: 'Webhook-Client-State-Secret',
      type: 'password',
      help: 'HMAC-Schluessel zur Validierung eingehender Graph-Notifications. Pflichtenheft §12.3. Bei Verlust einfach neu generieren — alle Subscriptions werden beim naechsten renew-Lauf automatisch neu angelegt.',
    },
  ],
  airkey: [
    { key: 'apiKey', label: 'API-Key', type: 'password', help: 'Aus dem Airkey Web-Portal unter Integration → API-Schluessel.' },
    { key: 'baseUrl', label: 'Base-URL', type: 'text', placeholder: 'https://integration.api.airkey.evva.com', default: 'https://integration.api.airkey.evva.com', help: 'In der Regel nicht aendern. Nur fuer On-Premise-Instanzen.' },
  ],
  whatsapp: [
    {
      key: 'accountSid',
      label: 'Account SID',
      type: 'text',
      placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      help: 'Twilio Account SID — zu finden unter console.twilio.com → Account Info.',
    },
    {
      key: 'authToken',
      label: 'Auth Token',
      type: 'password',
      help: 'Twilio Auth Token — wird verschlüsselt gespeichert (AES-GCM).',
    },
    {
      key: 'fromNumber',
      label: 'WhatsApp-Absendernummer',
      type: 'text',
      placeholder: 'whatsapp:+4915XXXXXXXX',
      help: 'Twilio WhatsApp-fähige Nummer im Format whatsapp:+<Ländervorwahl>... — aus der Twilio-Konsole unter Messaging → Senders.',
    },
    {
      key: 'webhookBaseUrl',
      label: 'Webhook-Basis-URL',
      type: 'text',
      placeholder: 'https://sysman.implec.de/api',
      help: 'Externe Basis-URL des Backends — Twilio sendet eingehende Nachrichten an <baseUrl>/webhooks/twilio/whatsapp. Muss öffentlich über HTTPS erreichbar sein.',
    },
    {
      key: 'phoneMatchCacheTtlHours',
      label: 'Kontakt-Cache TTL (Stunden)',
      type: 'number',
      help: 'Wie lange die Telefonnummer → TANSS-Kontakt-Zuordnung gecacht wird. Standard: 24.',
      default: '24',
    },
    {
      key: 'salesKeywords',
      label: 'Vertriebsanfragen-Keywords',
      type: 'text',
      placeholder: 'angebot,preis,kaufen,kosten,bestellung',
      help: 'Komma-getrennte Stichwörter. Enthält eine eingehende Nachricht eines davon, wird sie als Vertriebsanfrage eingestuft (eigener TANSS-Ticket-Typ).',
    },
    {
      key: 'defaultCategoryId',
      label: 'TANSS Kategorie-ID (Standard)',
      type: 'number',
      help: 'Optionale TANSS-Ticket-Kategorie für normale WhatsApp-Anfragen. Leer lassen = keine Kategorie.',
    },
    {
      key: 'salesCategoryId',
      label: 'TANSS Kategorie-ID (Vertrieb)',
      type: 'number',
      help: 'Optionale TANSS-Ticket-Kategorie für als Vertriebsanfragen erkannte Nachrichten.',
    },
    {
      key: 'defaultCompanyId',
      label: 'TANSS Fallback-Firmen-ID',
      type: 'number',
      help: 'TANSS-Firmen-ID die genutzt wird wenn keine Telefonnummer zugeordnet werden kann. Ohne Eintrag wird kein Ticket erstellt.',
    },
  ],
  ninjaone: [
    { key: 'clientId', label: 'Client ID', type: 'text', placeholder: 'NinjaOne Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'NinjaOne Client Secret' },
    {
      key: 'webhookSecret',
      label: 'Webhook-Secret (Token)',
      type: 'password',
      placeholder: 'Zufälliges Token für POST /api/webhooks/ninja?token=…',
      help: 'Sicherheits-Token das NinjaOne als Query-Parameter ?token=<secret> mitschickt. In NinjaOne unter Administration → Apps → Benachrichtigungskanäle → Webhook → URL eintragen.',
    },
    {
      key: 'webhookTenantId',
      label: 'Webhook Fallback-Mandant (UUID)',
      type: 'text',
      placeholder: 'Mandanten-UUID aus Einstellungen',
      help: 'Wird verwendet wenn das NinjaOne-Event keine Organisation enthält oder die Org-ID keinem Kunden zugeordnet ist. Muss die UUID eines konfigurierten Mandanten sein.',
    },
    {
      key: 'webhookEmployeeId',
      label: 'System-Mitarbeiter-ID (TANSS)',
      type: 'number',
      placeholder: '0',
      help: 'TANSS-Mitarbeiter-ID für automatische Zeiteinträge bei Webhook-Alerts. 0 = kein Zeiteintrag.',
    },
  ],
}

// Adapters sorted: AI providers first, then discovery adapters, then alphabetically
const ADAPTER_OPTIONS = Object.keys(ADAPTER_LABELS).sort((a, b) =>
  (ADAPTER_LABELS[a] ?? a).localeCompare(ADAPTER_LABELS[b] ?? b, 'de')
)

/**
 * Liefert die Default-Werte aller Felder eines Adapters (fuer die Vorbefuellung
 * beim Anlegen eines neuen Adapters). Felder ohne explizit definiertes
 * `default` werden weggelassen, damit der Placeholder weiter als Hinweis
 * sichtbar bleibt.
 */
function defaultsForAdapter(adapter: string): Record<string, unknown> {
  const fields = ADAPTER_FIELDS[adapter] ?? []
  const defaults: Record<string, unknown> = {}
  for (const field of fields) {
    if (field.default !== undefined) {
      defaults[field.key] = field.type === 'number' ? Number(field.default) : field.default
    }
  }
  return defaults
}

interface AdapterConfig {
  id: string
  adapter: string
  label?: string
  config: Record<string, unknown>
  isActive: boolean
  useGlobal?: boolean
}

// Backend GET masks sensitive fields with `••••••••`. On edit start
// we strip the mask so the field is empty — the user must actively
// type to change the password. Empty-stays-empty is interpreted by the backend
// as "do not change" (see lib/adapterConfigMerge.ts).
const ADAPTER_PASSWORD_MASK = '••••••••'

function stripMaskedConfig(config: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(config)) {
    out[k] = v === ADAPTER_PASSWORD_MASK ? '' : v
  }
  return out
}

function TenantAdaptersOverview({ tenantId }: { tenantId: string }) {
  const [configs, setConfigs] = useState<AdapterConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [pendingDeleteAdapter, setPendingDeleteAdapter] = useState<string | null>(null)

  // Edit state
  const [editingAdapter, setEditingAdapter] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Record<string, unknown>>({})
  const [editLabel, setEditLabel] = useState('')
  const [editActive, setEditActive] = useState(true)
  const [editUseGlobal, setEditUseGlobal] = useState(false)

  // Add new adapter
  const [showAdd, setShowAdd] = useState(false)
  const [newAdapterType, setNewAdapterType] = useState('')
  const [newForm, setNewForm] = useState<Record<string, unknown>>({})
  const [newLabel, setNewLabel] = useState('')
  const [newUseGlobal, setNewUseGlobal] = useState(false)

  const loadConfigs = useCallback(() => {
    setLoading(true)
    getTenantAdapters(tenantId)
      .then(({ data }) => setConfigs(data))
      .catch(() => setError('Adapter konnten nicht geladen werden.'))
      .finally(() => setLoading(false))
  }, [tenantId])

  useEffect(() => { loadConfigs() }, [loadConfigs])

  const configuredAdapters = configs.map((c) => c.adapter)
  const availableAdapters = ADAPTER_OPTIONS.filter((a) => !configuredAdapters.includes(a))

  const startEdit = (cfg: AdapterConfig) => {
    setEditingAdapter(cfg.adapter)
    setEditForm(stripMaskedConfig(cfg.config))
    setEditLabel(cfg.label ?? '')
    setEditActive(cfg.isActive)
    setEditUseGlobal(cfg.useGlobal ?? false)
    setMessage('')
  }

  const cancelEdit = () => {
    setEditingAdapter(null)
    setEditForm({})
  }

  const saveAdapter = async (adapter: string, config: Record<string, unknown>, label: string, isActive: boolean, useGlobal: boolean = false) => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await saveTenantAdapter(tenantId, {
        adapter,
        label: label || null,
        config: useGlobal ? {} : config,
        isActive,
        useGlobal,
      })
      setMessage('Adapter-Konfiguration gespeichert.')
      setEditingAdapter(null)
      setShowAdd(false)
      setNewAdapterType('')
      setNewForm({})
      setNewLabel('')
      loadConfigs()
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const deleteAdapter = async (adapter: string) => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await deleteTenantAdapter(tenantId, adapter)
      setMessage('Adapter entfernt.')
      setEditingAdapter(null)
      setPendingDeleteAdapter(null)
      loadConfigs()
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Löschen.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const startAdd = () => {
    setShowAdd(true)
    const firstAdapter = availableAdapters[0] ?? ''
    setNewAdapterType(firstAdapter)
    setNewForm(defaultsForAdapter(firstAdapter))
    setNewLabel('')
    setMessage('')
    setEditingAdapter(null)
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-6">
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <SettingsSection title="Adapter-Konfigurationen" icon={Settings}>
        <p className="text-caption text-gray-500 mb-3">
          Externe Systeme und Dienste für diese Firma konfigurieren.
          Sensitive Werte (API-Keys, Passwörter) werden verschlüsselt gespeichert.
        </p>

        {configs.length === 0 && !showAdd && (
          <p className="text-small text-gray-400 italic mb-3">Keine Adapter konfiguriert.</p>
        )}

        <div className="space-y-2">
          {configs.map((cfg) => (
            <div key={cfg.id}>
              {editingAdapter === cfg.adapter ? (
                <div className="space-y-4">
                  <AdapterForm
                    adapter={cfg.adapter}
                    fields={ADAPTER_FIELDS[cfg.adapter] ?? []}
                    values={editForm}
                    onChange={setEditForm}
                    label={editLabel}
                    onLabelChange={setEditLabel}
                    isActive={editActive}
                    onActiveChange={setEditActive}
                    useGlobal={editUseGlobal}
                    onUseGlobalChange={setEditUseGlobal}
                    allowUseGlobal={true}
                    onSave={() => saveAdapter(cfg.adapter, editForm, editLabel, editActive, editUseGlobal)}
                    onCancel={cancelEdit}
                    onDelete={() => setPendingDeleteAdapter(cfg.adapter)}
                    saving={saving}
                    isNew={false}
                    tenantId={tenantId}
                  />
                  {cfg.adapter === 'tanss' && (
                    <TanssAdapterExtensions tenantId={tenantId} />
                  )}
                </div>
              ) : (
                <div
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => startEdit(cfg)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-small font-medium">{ADAPTER_LABELS[cfg.adapter] ?? cfg.adapter}</span>
                    {cfg.label && <span className="text-small text-gray-400">({cfg.label})</span>}
                    {cfg.useGlobal && <Badge variant="info" size="sm">global</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={cfg.isActive ? 'success' : 'neutral'} size="sm">
                      {cfg.isActive ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    <Code2 className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add new adapter */}
        {showAdd && (
          <div className="mt-4">
            <div className="mb-3">
              {availableAdapters.length === 0 ? (
                <p className="text-small text-gray-400 italic">Alle Adapter bereits konfiguriert</p>
              ) : (
                <Select
                  label="Adapter-Typ"
                  value={newAdapterType}
                  onValueChange={(v) => { setNewAdapterType(v); setNewForm(defaultsForAdapter(v)) }}
                  options={availableAdapters.map((a) => ({
                    value: a,
                    label: ADAPTER_LABELS[a] ?? a,
                  }))}
                  searchable
                />
              )}
            </div>
            {newAdapterType && (
              <AdapterForm
                adapter={newAdapterType}
                fields={ADAPTER_FIELDS[newAdapterType] ?? []}
                values={newForm}
                onChange={setNewForm}
                label={newLabel}
                onLabelChange={setNewLabel}
                isActive={true}
                onActiveChange={() => {}}
                useGlobal={newUseGlobal}
                onUseGlobalChange={setNewUseGlobal}
                allowUseGlobal={true}
                onSave={() => saveAdapter(newAdapterType, newForm, newLabel, true, newUseGlobal)}
                onCancel={() => { setShowAdd(false); setNewAdapterType(''); setNewForm({}); setNewLabel(''); setNewUseGlobal(false) }}
                saving={saving}
                isNew={true}
                tenantId={tenantId}
              />
            )}
          </div>
        )}

        {!showAdd && availableAdapters.length > 0 && (
          <Button
            className="mt-3"
            variant="secondary"
            leadingIcon={<Plus className="w-4 h-4" />}
            onClick={startAdd}
          >
            Adapter hinzufügen
          </Button>
        )}
      </SettingsSection>

      <ConfirmDialog
        open={pendingDeleteAdapter !== null}
        title="Adapter entfernen"
        message={
          pendingDeleteAdapter
            ? `Adapter "${ADAPTER_LABELS[pendingDeleteAdapter] ?? pendingDeleteAdapter}" wirklich entfernen?`
            : ''
        }
        confirmLabel="Entfernen"
        variant="danger"
        loading={saving}
        onConfirm={() => { if (pendingDeleteAdapter) deleteAdapter(pendingDeleteAdapter) }}
        onCancel={() => setPendingDeleteAdapter(null)}
      />
    </div>
  )
}

// ─── ALSO Connection Test ───────────────────────────────────────────────────

interface AlsoTestResult {
  ok: boolean
  durationMs: number
  source?: string
  baseUrl?: string
  username?: string
  resellerAccountId?: string
  error?: {
    message: string
    code?: string
    httpStatus?: number
    detail?: string
    notConfigured?: boolean
  }
}

function AlsoConnectionTest() {
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AlsoTestResult | null>(null)
  const [showDetails, setShowDetails] = useState(false)

  const run = async () => {
    setRunning(true)
    setResult(null)
    setShowDetails(false)
    try {
      const { data } = await testAlsoConnection()
      setResult(data)
    } catch (err) {
      setResult({
        ok: false,
        durationMs: 0,
        error: { message: extractErrorMessage(err) || 'Anfrage fehlgeschlagen' },
      })
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="border-t border-brand-200 pt-3 mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={run}
          loading={running}
          leadingIcon={<Link className="w-3.5 h-3.5" />}
        >
          Verbindung testen
        </Button>
        <span className="text-caption text-gray-500">
          Login → PingPong → Logout. Speichert Credentials nicht.
        </span>
      </div>

      {result && result.ok && (
        <div className="rounded border border-green-200 bg-green-50 p-3 text-caption text-green-800">
          <div className="font-medium">Verbindung erfolgreich</div>
          <div className="mt-1 text-green-700">
            Quelle: <code>{result.source ?? '?'}</code> · Dauer: {result.durationMs} ms
          </div>
          <div className="text-green-700">
            Reseller-AccountId: <code>{result.resellerAccountId ?? '—'}</code>
            {result.username ? <> · User: <code>{result.username}</code></> : null}
          </div>
        </div>
      )}

      {result && !result.ok && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-caption text-red-800">
          <div className="font-medium flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Verbindung fehlgeschlagen
          </div>
          <div className="mt-1 text-red-700">{result.error?.message ?? 'Unbekannter Fehler'}</div>
          {result.error?.code && (
            <div className="text-red-700">
              Code: <code>{result.error.code}</code>
              {result.error.httpStatus ? <> · HTTP {result.error.httpStatus}</> : null}
            </div>
          )}
          {result.error?.notConfigured && (
            <div className="mt-1 text-red-700">
              Hinweis: Adapter ist noch nicht gespeichert. Erst „Speichern" klicken, danach „Verbindung testen".
            </div>
          )}
          {result.error?.detail && (
            <div className="mt-2">
              <button
                type="button"
                className="text-caption underline text-red-700 hover:text-red-900"
                onClick={() => setShowDetails((v) => !v)}
              >
                {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
              </button>
              {showDetails && (
                <pre className="mt-1 p-2 bg-red-100 border border-red-200 rounded text-caption text-red-900 whitespace-pre-wrap break-words max-h-64 overflow-auto">
                  {result.error.detail}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── TANSS Portal Connection Test ───────────────────────────────────────────

function TanssPortalConnectionTest() {
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<TanssPortalTestResult | null>(null)

  const run = async () => {
    setRunning(true)
    setResult(null)
    try {
      const { data } = await testTanssPortalConnection()
      setResult(data)
    } catch (err) {
      setResult({
        ok: false,
        durationMs: 0,
        error: { message: extractErrorMessage(err) || 'Anfrage fehlgeschlagen' },
      })
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="border-t border-brand-200 pt-3 mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={run}
          loading={running}
          leadingIcon={<Link className="w-3.5 h-3.5" />}
        >
          Portal-Verbindung testen
        </Button>
        <span className="text-caption text-gray-500">
          Testet Login des Portal-Service-Accounts und ruft /employees/technicians ab.
        </span>
      </div>

      {result && result.ok && (
        <div className="rounded border border-green-200 bg-green-50 p-3 text-caption text-green-800">
          <div className="font-medium">Verbindung erfolgreich</div>
          <div className="mt-1 text-green-700">
            {result.employeeCount != null ? (
              <>{result.employeeCount} Techniker geladen · </>
            ) : null}
            Dauer: {result.durationMs} ms
          </div>
        </div>
      )}

      {result && !result.ok && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-caption text-red-800">
          <div className="font-medium flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Verbindung fehlgeschlagen
          </div>
          <div className="mt-1 text-red-700">{result.error?.message ?? 'Unbekannter Fehler'}</div>
          {result.error?.code && (
            <div className="text-red-700">Code: <code>{result.error.code}</code></div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── ESET MSP Connection Test ───────────────────────────────────────────────

interface EsetEmaTestStep {
  name: string
  ok: boolean
  durationMs: number
  info?: string
  error?: { message: string; code?: string; httpStatus?: number; detail?: string }
}

interface EsetEmaTestResult {
  ok: boolean
  durationMs: number
  baseUrl?: string
  username?: string
  mode?: 'live' | 'resolver'
  steps: EsetEmaTestStep[]
  error?: { message: string; code?: string; httpStatus?: number }
  diagnostics?: EsetEmaConfigDiagnostics
  credSources?: {
    baseUrl: 'form' | 'db' | 'default' | 'none'
    username: 'form' | 'db' | 'none'
    password: 'form' | 'db' | 'none'
    passwordLength: number
    dbRowFound: boolean
    dbDecryptOk: boolean
  }
}

interface EsetEmaConfigDiagnostics {
  devMode: boolean
  perUserSettings: { username: boolean; password: boolean }
  user: { found: boolean; defaultTenantId: string | null }
  tenantAdapter:
    | { row: 'missing' }
    | {
        row: 'present'
        isActive: boolean
        useGlobal: boolean
        configKeys: string[]
        hasBaseUrl: boolean
        hasUsername: boolean
        hasPassword: boolean
        decryptOk: boolean
      }
  globalAdapter:
    | { row: 'missing' }
    | {
        row: 'present'
        isActive: boolean
        configKeys: string[]
        hasBaseUrl: boolean
        hasUsername: boolean
        hasPassword: boolean
        decryptOk: boolean
      }
  systemSettings: { hasBaseUrl: boolean; hasUsername: boolean; hasPassword: boolean }
  resolved: { hasBaseUrl: boolean; hasUsername: boolean; hasPassword: boolean }
}

// Backend GET /tenants/:id/adapters masks passwords with `'••••••••'`. In the
// edit form the mask appears in the password field — we do not send it as the
// actual password; instead the backend loads the real config via
// `tenantId` from the DB.
const ESET_PASSWORD_MASK = '••••••••'

function EsetEmaConnectionTest({
  formValues,
  tenantId,
}: {
  formValues?: Record<string, unknown>
  tenantId?: string
}) {
  const [running, setRunning] = useState(false)
  const [orderTest, setOrderTest] = useState(false)
  const [result, setResult] = useState<EsetEmaTestResult | null>(null)
  const [showDetailsForStep, setShowDetailsForStep] = useState<Record<number, boolean>>({})

  // Evaluate form values carefully:
  //   - Mask means "unchanged", not "actual password".
  //   - Username/baseUrl are sent as-is since they are not masked.
  //   - tenantId is always sent so the backend can load the saved config
  //     when the form password is empty or masked.
  const username = typeof formValues?.username === 'string' ? formValues.username : ''
  const passwordRaw = typeof formValues?.password === 'string' ? formValues.password : ''
  const password = passwordRaw === ESET_PASSWORD_MASK ? '' : passwordRaw
  const baseUrl = typeof formValues?.baseUrl === 'string' ? formValues.baseUrl : ''
  const isPasswordMasked = passwordRaw === ESET_PASSWORD_MASK
  const useLiveCreds = username.length > 0 && password.length > 0

  const run = async () => {
    setRunning(true)
    setResult(null)
    setShowDetailsForStep({})
    try {
      const payload: Record<string, unknown> = { orderTest }
      if (tenantId) payload.tenantId = tenantId
      if (username) payload.username = username
      if (password) payload.password = password
      if (baseUrl) payload.baseUrl = baseUrl
      const { data } = await testEsetConnection(payload)
      setResult(data)
    } catch (err) {
      setResult({
        ok: false,
        durationMs: 0,
        steps: [],
        error: { message: extractErrorMessage(err) || 'Anfrage fehlgeschlagen' },
      })
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="border-t border-brand-200 pt-3 mt-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={run}
          loading={running}
          leadingIcon={<Link className="w-3.5 h-3.5" />}
        >
          Verbindung testen
        </Button>
        <label className="flex items-center gap-1.5 text-caption text-gray-600 select-none">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-gray-300"
            checked={orderTest}
            onChange={(e) => setOrderTest(e.target.checked)}
            disabled={running}
          />
          Auch Trial-Lizenz buchen + suspenden
        </label>
        <span className="text-caption text-gray-500">
          Auth → AvailableProducts → Search/Companies{orderTest ? ' → Order(Trial) → Detail → Suspend' : ''}.
        </span>
        {useLiveCreds ? (
          <span className="text-caption text-brand-600 font-medium">
            Test nutzt aktuelle Formular-Werte (noch nicht gespeichert).
          </span>
        ) : (isPasswordMasked || (tenantId && !password)) ? (
          <span className="text-caption text-gray-500">
            Test nutzt das in der DB gespeicherte Passwort dieses Tenants. Zum Test mit
            anderem Passwort: Feld neu ausfüllen.
          </span>
        ) : null}
      </div>

      {result && (
        <div
          className={`rounded border p-3 text-caption ${
            result.ok
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <div className="font-medium">
            {result.ok ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
          </div>
          {(result.baseUrl || result.username) && (
            <div className="mt-1 text-gray-700">
              {result.baseUrl ? <>Base-URL: <code>{result.baseUrl}</code></> : null}
              {result.baseUrl && result.username ? ' · ' : null}
              {result.username ? <>User: <code>{result.username}</code></> : null}
              {' · '}Gesamt: {result.durationMs} ms
            </div>
          )}

          <ul className="mt-2 space-y-1">
            {result.steps.map((step, idx) => (
              <li key={idx} className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className={step.ok ? 'text-green-700' : 'text-red-700'}>
                    {step.ok ? '✓' : '✗'}
                  </span>
                  <span className="font-medium">{step.name}</span>
                  <span className="text-gray-500">— {step.durationMs} ms</span>
                </div>
                {step.info && <div className="ml-5 text-gray-700">{step.info}</div>}
                {step.error && (
                  <div className="ml-5 text-red-700">
                    <div>
                      {step.error.message}
                      {step.error.code ? <> · <code>{step.error.code}</code></> : null}
                      {step.error.httpStatus ? <> · HTTP {step.error.httpStatus}</> : null}
                    </div>
                    {step.error.detail && (
                      <>
                        <button
                          type="button"
                          className="underline text-red-700 hover:text-red-900"
                          onClick={() =>
                            setShowDetailsForStep((s) => ({ ...s, [idx]: !s[idx] }))
                          }
                        >
                          {showDetailsForStep[idx] ? 'Details ausblenden' : 'Details anzeigen'}
                        </button>
                        {showDetailsForStep[idx] && (
                          <pre className="mt-1 p-2 bg-red-100 border border-red-200 rounded text-red-900 whitespace-pre-wrap break-words max-h-48 overflow-auto">
                            {step.error.detail}
                          </pre>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {result.error && (
            <div className="mt-2 text-red-700">
              Unerwartet: {result.error.message}
              {result.error.code ? <> · <code>{result.error.code}</code></> : null}
            </div>
          )}

          {result.credSources && (
            <details className="mt-2 text-caption">
              <summary className="cursor-pointer font-medium">
                Was wurde an ESET geschickt? — Credential-Quellen
              </summary>
              <div className="pt-2 pl-3 space-y-0.5">
                <div>
                  baseUrl: <code>{result.baseUrl ?? '—'}</code> · Quelle:{' '}
                  <span className="font-medium">{credSourceLabel(result.credSources.baseUrl)}</span>
                </div>
                <div>
                  username: <code>{result.username ?? '—'}</code> · Quelle:{' '}
                  <span className="font-medium">{credSourceLabel(result.credSources.username)}</span>
                </div>
                <div>
                  password: {result.credSources.passwordLength > 0 ? `${result.credSources.passwordLength} Zeichen` : '— LEER —'} · Quelle:{' '}
                  <span className="font-medium">{credSourceLabel(result.credSources.password)}</span>
                </div>
                <div>
                  Tenant-DB-Lookup: row {result.credSources.dbRowFound ? '✓ gefunden' : '✗ fehlt'}
                  {result.credSources.dbRowFound
                    ? `, decrypt ${result.credSources.dbDecryptOk ? '✓' : '✗'}`
                    : ''}
                </div>
              </div>
            </details>
          )}

          {result.diagnostics && (
            <EsetEmaDiagnosticsBlock diag={result.diagnostics} />
          )}
        </div>
      )}
    </div>
  )
}

function credSourceLabel(s: 'form' | 'db' | 'default' | 'none'): string {
  switch (s) {
    case 'form': return 'aus Formular'
    case 'db': return 'aus gespeicherter Tenant-Konfig'
    case 'default': return 'Default (mspapi.eset.com/api)'
    default: return 'nicht gesetzt'
  }
}

function EsetEmaDiagnosticsBlock({ diag }: { diag: EsetEmaConfigDiagnostics }) {
  const yes = (b: boolean) => (b ? '✓' : '✗')
  const cls = (b: boolean) => (b ? 'text-green-700' : 'text-red-700')
  return (
    <details className="mt-3 border-t border-red-200 pt-2">
      <summary className="cursor-pointer font-medium text-red-800">
        Konfig-Auflösung — Resolver-Verlauf
      </summary>
      <div className="mt-2 space-y-2 text-gray-800 bg-white border border-gray-200 rounded p-2">
        <div>
          <span className="font-medium">Dev-Mode:</span>{' '}
          <span className={cls(!diag.devMode)}>{diag.devMode ? 'aktiv' : 'aus'}</span>
        </div>

        <div>
          <span className="font-medium">User:</span>{' '}
          {diag.user.found ? (
            <>
              gefunden, <code>defaultTenantId</code>:{' '}
              {diag.user.defaultTenantId ? (
                <code>{diag.user.defaultTenantId}</code>
              ) : (
                <span className="text-red-700">nicht gesetzt</span>
              )}
            </>
          ) : (
            <span className="text-red-700">User-Row nicht gefunden</span>
          )}
        </div>

        <div>
          <span className="font-medium">Per-User Credentials:</span>{' '}
          username <span className={cls(diag.perUserSettings.username)}>
            {yes(diag.perUserSettings.username)}
          </span>{' · '}
          password <span className={cls(diag.perUserSettings.password)}>
            {yes(diag.perUserSettings.password)}
          </span>
        </div>

        <div>
          <span className="font-medium">Tenant-Adapter (eset-ema):</span>{' '}
          {diag.tenantAdapter.row === 'missing' ? (
            <span className="text-red-700">kein DB-Eintrag für deinen Tenant</span>
          ) : (
            <div className="ml-4 space-y-0.5">
              <div>
                isActive <span className={cls(diag.tenantAdapter.isActive)}>
                  {yes(diag.tenantAdapter.isActive)}
                </span>{' · '}
                useGlobal{' '}
                <span className={diag.tenantAdapter.useGlobal ? 'text-amber-700' : 'text-green-700'}>
                  {yes(diag.tenantAdapter.useGlobal)}
                </span>{' · '}
                decryptOk <span className={cls(diag.tenantAdapter.decryptOk)}>
                  {yes(diag.tenantAdapter.decryptOk)}
                </span>
              </div>
              <div>
                Felder:{' '}
                baseUrl <span className={cls(diag.tenantAdapter.hasBaseUrl)}>
                  {yes(diag.tenantAdapter.hasBaseUrl)}
                </span>{' · '}
                username <span className={cls(diag.tenantAdapter.hasUsername)}>
                  {yes(diag.tenantAdapter.hasUsername)}
                </span>{' · '}
                password <span className={cls(diag.tenantAdapter.hasPassword)}>
                  {yes(diag.tenantAdapter.hasPassword)}
                </span>
              </div>
              {diag.tenantAdapter.configKeys.length > 0 && (
                <div className="text-gray-500">
                  Gespeicherte Keys: <code>{diag.tenantAdapter.configKeys.join(', ')}</code>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <span className="font-medium">Global-Adapter (eset-ema):</span>{' '}
          {diag.globalAdapter.row === 'missing' ? (
            <span className="text-gray-500">kein globaler Eintrag</span>
          ) : (
            <div className="ml-4 space-y-0.5">
              <div>
                isActive <span className={cls(diag.globalAdapter.isActive)}>
                  {yes(diag.globalAdapter.isActive)}
                </span>{' · '}
                decryptOk <span className={cls(diag.globalAdapter.decryptOk)}>
                  {yes(diag.globalAdapter.decryptOk)}
                </span>
              </div>
              <div>
                baseUrl <span className={cls(diag.globalAdapter.hasBaseUrl)}>
                  {yes(diag.globalAdapter.hasBaseUrl)}
                </span>{' · '}
                username <span className={cls(diag.globalAdapter.hasUsername)}>
                  {yes(diag.globalAdapter.hasUsername)}
                </span>{' · '}
                password <span className={cls(diag.globalAdapter.hasPassword)}>
                  {yes(diag.globalAdapter.hasPassword)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div>
          <span className="font-medium">Legacy systemSettings-Fallback:</span>{' '}
          baseUrl <span className={cls(diag.systemSettings.hasBaseUrl)}>
            {yes(diag.systemSettings.hasBaseUrl)}
          </span>{' · '}
          username <span className={cls(diag.systemSettings.hasUsername)}>
            {yes(diag.systemSettings.hasUsername)}
          </span>{' · '}
          password <span className={cls(diag.systemSettings.hasPassword)}>
            {yes(diag.systemSettings.hasPassword)}
          </span>
        </div>

        <div className="border-t border-gray-200 pt-2">
          <span className="font-medium">Final aufgelöst:</span>{' '}
          baseUrl <span className={cls(diag.resolved.hasBaseUrl)}>{yes(diag.resolved.hasBaseUrl)}</span>{' · '}
          username <span className={cls(diag.resolved.hasUsername)}>{yes(diag.resolved.hasUsername)}</span>{' · '}
          password <span className={cls(diag.resolved.hasPassword)}>{yes(diag.resolved.hasPassword)}</span>
        </div>
      </div>
    </details>
  )
}

function AdapterForm({
  adapter,
  fields,
  values,
  onChange,
  label,
  onLabelChange,
  isActive,
  onActiveChange,
  useGlobal = false,
  onUseGlobalChange,
  allowUseGlobal = false,
  onSave,
  onCancel,
  onDelete,
  saving,
  isNew,
  tenantId,
}: {
  adapter: string
  fields: AdapterFieldDef[]
  values: Record<string, unknown>
  onChange: (v: Record<string, unknown>) => void
  label: string
  onLabelChange: (v: string) => void
  isActive: boolean
  onActiveChange: (v: boolean) => void
  useGlobal?: boolean
  onUseGlobalChange?: (v: boolean) => void
  allowUseGlobal?: boolean
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
  tenantId?: string
  saving: boolean
  isNew: boolean
}) {
  const setField = (key: string, value: unknown) => onChange({ ...values, [key]: value })
  const configDisabled = useGlobal

  return (
    <div className="border border-brand-200 rounded-lg p-4 bg-brand-50/30 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-small font-semibold text-gray-900">
          {ADAPTER_LABELS[adapter] ?? adapter}
          {isNew && <span className="ml-2 text-caption font-normal text-brand-600">(Neu)</span>}
        </h3>
        {!isNew && (
          <Switch
            label="Aktiv"
            checked={isActive}
            onChange={(e) => onActiveChange(e.target.checked)}
          />
        )}
      </div>

      {allowUseGlobal && onUseGlobalChange && (
        <label className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded cursor-pointer">
          <Checkbox
            checked={useGlobal}
            onChange={(e) => onUseGlobalChange(e.target.checked)}
          />
          <div className="flex-1">
            <div className="text-caption font-medium text-blue-900">Globale Konfiguration verwenden</div>
            <div className="text-caption text-blue-700">
              {useGlobal
                ? 'Diese Firma nutzt die globale Konfiguration aus Einstellungen → Global → Adapter'
                : 'Aktivieren, um statt eigener Werte die globale Konfiguration dieses Adapters zu verwenden.'}
            </div>
          </div>
        </label>
      )}

      <Input
        label="Bezeichnung (optional)"
        type="text"
        value={label}
        onChange={(e) => onLabelChange(e.target.value)}
        placeholder="z.B. Hauptinstanz, Test-System..."
      />

      {fields.length > 0 ? (
        <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-3', configDisabled && 'opacity-40 pointer-events-none')}>
          {fields.map((field) => (
            <div key={field.key}>
              {field.type === 'toggle' ? (
                <>
                  <label className="block text-caption font-medium text-gray-600 mb-1">{field.label}</label>
                  <Switch
                    checked={Boolean(values[field.key])}
                    onChange={() => setField(field.key, !values[field.key])}
                  />
                  {field.help && <p className="text-caption text-gray-400 mt-0.5">{field.help}</p>}
                </>
              ) : (
                <Input
                  label={field.label}
                  type={field.type === 'number' ? 'number' : field.type}
                  value={String(values[field.key] ?? '')}
                  onChange={(e) => setField(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
                  placeholder={
                    field.type === 'password' && !isNew && !values[field.key]
                      ? '•••••••• (gespeichert — leer lassen, um nicht zu ändern)'
                      : field.placeholder
                  }
                  disabled={configDisabled}
                  hint={field.help}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-caption text-gray-400 italic">
          Für diesen Adapter sind keine spezifischen Felder definiert.
          Konfiguration kann über die JSON-API gesetzt werden.
        </p>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button
          onClick={onSave}
          loading={saving}
          leadingIcon={<Save className="w-4 h-4" />}
        >
          Speichern
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
        {onDelete && !isNew && (
          <Button
            className="ml-auto"
            variant="danger"
            size="sm"
            leadingIcon={<Trash2 className="w-3.5 h-3.5" />}
            disabled={saving}
            onClick={onDelete}
          >
            Entfernen
          </Button>
        )}
      </div>

      {adapter === 'also' && !isNew && !useGlobal && <AlsoConnectionTest />}
      {adapter === 'eset-ema' && !useGlobal && (
        <EsetEmaConnectionTest formValues={values} tenantId={tenantId} />
      )}
      {adapter === 'egis' && !isNew && !useGlobal && <EgisConnectionTest />}
      {adapter === 'tanss' && !isNew && <TanssPortalConnectionTest />}
    </div>
  )
}

// ─── System Sub-Tab Components ──────────────────────────────────────────────

function SaveButton({ save, saving }: { save: () => void; saving: boolean }) {
  return (
    <div className="pt-2">
      <Button
        onClick={save}
        loading={saving}
        leadingIcon={<Save className="w-4 h-4" />}
        size="lg"
      >
        Speichern
      </Button>
    </div>
  )
}

interface SystemSectionProps {
  settings: SystemSettings
  set: (key: string, value: string) => void
  save: () => void
  saving: boolean
}


function SystemGeneralSection({ settings, set, save, saving }: SystemSectionProps) {
  return (
    <div className="space-y-6">
      <SettingsSection title="Allgemein" icon={Building2}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingsInput
            label="Firmenname"
            value={settings.companyName || ''}
            onChange={(v) => set('companyName', v)}
            placeholder="z.B. implec GmbH"
          />
          <Select
            label="Standard-Rolle für neue Benutzer"
            value={settings.defaultUserRole || 'technician'}
            onValueChange={(v) => set('defaultUserRole', v)}
            options={ROLES.map((role) => ({
              value: role,
              label: ROLE_LABELS[role] ?? role,
            }))}
          />
        </div>
      </SettingsSection>

      <div className="text-caption text-gray-500 italic bg-gray-50 border border-gray-200 rounded p-3">
        Der Dev-Mode wird seit dem letzten Update <strong>pro Modul</strong> konfiguriert
        (Einstellungen → Module → jeweiliges Modul → Dev-Mode-Toggle). Der alte globale
        Dev-Mode-Schalter wurde entfernt.
      </div>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}


// ─── Customer Portal – Shared Config ─────────────────────────────────────────

interface KatTagGroup {
  id: number
  name: string
  label: string
  children: { id: number; name: string; label: string }[]
}

const PORTAL_FEATURES: { key: string; label: string; description: string }[] = [
  { key: 'customerPortal.features.requestCallback', label: 'Rückruf anfordern', description: 'Kunde kann an ein Ticket einen Rückruf-Wunsch anhängen.' },
  { key: 'customerPortal.features.requestAppointment', label: 'Termin vereinbaren', description: 'Kunde kann an ein Ticket einen Terminwunsch anhängen.' },
  { key: 'customerPortal.features.invoices', label: 'Dokumente (Angebote, Rechnungen, …)', description: 'Sichtbarkeit von Angeboten, Aufträgen, Rechnungen, OPOS.' },
  { key: 'customerPortal.features.devices', label: 'Geräteübersicht', description: 'Assets/Geräte des Kunden anzeigen.' },
  { key: 'customerPortal.features.contracts', label: 'Verträge & Services', description: 'Vertragsübersicht sichtbar.' },
  { key: 'customerPortal.features.employees', label: 'Mitarbeiterverwaltung', description: 'Eigene Mitarbeiterliste der Kundenfirma.' },
  { key: 'customerPortal.features.accessMatrix', label: 'Zugriffsmatrix', description: 'Berechtigungen auf Freigaben & Gruppen einsehen.' },
  { key: 'customerPortal.features.securityStatus', label: 'Sicherheitsstatus', description: 'Übersicht IT-Sicherheit.' },
]

const SERVICE_WINDOW_OPTIONS = [
  { value: 'standard', label: 'Standard – Mo–Fr 08:00–17:00' },
  { value: 'extended1h', label: 'Erweitert +1h – Mo–Fr 07:00–18:00 (+10 %)' },
  { value: 'extended2h', label: 'Erweitert +2h – Mo–Fr 06:00–19:00 (+20 %)' },
]

const TRANSPARENCY_OPTIONS = [
  { value: 'deadline', label: 'Nur Reaktionsfrist zeigen (empfohlen)' },
  { value: 'none', label: 'Nichts zeigen – nur „Ticket eingegangen"' },
  { value: 'full', label: 'Prio + Reaktionsfrist zeigen' },
]

function parseKatDisabled(raw: string | undefined): Set<number> {
  if (!raw) return new Set()
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return new Set()
    return new Set(arr.filter((v): v is number => typeof v === 'number'))
  } catch {
    return new Set()
  }
}

function useKatTags(): { groups: KatTagGroup[]; loading: boolean; error: string } {
  const [groups, setGroups] = useState<KatTagGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  useEffect(() => {
    getKatTagGroups()
      .then(({ data }) => setGroups(data.groups ?? []))
      .catch((err) => setError(extractErrorMessage(err) || 'Kat-Tags konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [])
  return { groups, loading, error }
}

// ─── System Customer Portal Section (Global Defaults) ───────────────────────

function SystemCustomerPortalSection({ settings, set, save, saving }: SystemSectionProps) {
  const { groups, loading: katLoading, error: katError } = useKatTags()
  const disabledIds = parseKatDisabled(settings['customerPortal.kat.disabled'])

  const toggleKat = (id: number) => {
    const next = new Set(disabledIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    set('customerPortal.kat.disabled', JSON.stringify([...next]))
  }

  const featureValue = (key: string) => (settings[key] === 'off' ? 'off' : 'on')

  return (
    <div className="space-y-6">
      <p className="text-small text-gray-500">
        Diese Einstellungen gelten als Default für <strong>alle</strong> Firmen. Einzelne Firmen können
        die Werte im Reiter „Firma → Kundenportal" überschreiben.
      </p>

      <SettingsSection title="Portal-Funktionen" icon={Boxes}>
        <div className="space-y-2">
          {PORTAL_FEATURES.map((f) => (
            <div key={f.key} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
              <div className="flex-1">
                <div className="text-small font-medium">{f.label}</div>
                <div className="text-caption text-gray-500">{f.description}</div>
              </div>
              <Switch
                checked={featureValue(f.key) === 'on'}
                onChange={(e) => set(f.key, e.target.checked ? 'on' : 'off')}
              />
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="SLA & Servicezeit" icon={Clock}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-caption text-gray-700 mb-1">Servicezeit (Default)</label>
            <Select
              value={settings['customerPortal.serviceWindow'] || 'standard'}
              onValueChange={(v) => set('customerPortal.serviceWindow', String(v))}
              options={SERVICE_WINDOW_OPTIONS}
            />
          </div>
          <div>
            <label className="block text-caption text-gray-700 mb-1">Transparenz für Kunden</label>
            <Select
              value={settings['customerPortal.transparency'] || 'deadline'}
              onValueChange={(v) => set('customerPortal.transparency', String(v))}
              options={TRANSPARENCY_OPTIONS}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title="Kategorie-Tags (Kat)" icon={Plus}>
        <p className="text-caption text-gray-500 mb-3">
          Wählt aus, welche Kat-Kategorien im Kundenportal angeboten werden. Deaktivierte Kategorien erscheinen
          nicht bei der Ticketanlage. Pro Firma überschreibbar.
        </p>
        {katLoading && <div className="text-small text-gray-500">Lade Kat-Tags …</div>}
        {katError && <div className="text-small text-red-700">{katError}</div>}
        {!katLoading && !katError && groups.length === 0 && (
          <div className="text-small text-gray-500">
            Noch keine Kat-Tags in TANSS vorhanden. Bitte in TANSS anlegen.
          </div>
        )}
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="border rounded p-3">
              <div className="font-medium text-small mb-2 flex items-center justify-between">
                <span>{g.label}</span>
                <Checkbox
                  checked={!disabledIds.has(g.id)}
                  onChange={() => toggleKat(g.id)}
                  label="Gruppe aktiv"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-caption">
                {g.children.map((c) => (
                  <Checkbox
                    key={c.id}
                    checked={!disabledIds.has(c.id)}
                    onChange={() => toggleKat(c.id)}
                    label={c.label}
                    disabled={disabledIds.has(g.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Ticket-Vorlagen (Standard-Tickets)" icon={ClipboardList}>
        <p className="text-caption text-gray-500 mb-4">
          Diese Vorlagen bestimmen Betreff, Priorität und Beschreibung der Tickets, die automatisch beim
          Anlegen / Löschen von Mitarbeitern und beim Speichern der Zugriffsmatrix erstellt werden.
          Platzhalter in der Form <code className="bg-gray-100 px-1 rounded">{'{{variable}}'}</code> werden
          beim Erstellen des Tickets durch echte Werte ersetzt. Leer lassen = Systemstandard.
        </p>

        <Tabs defaultValue="new_user">
          <TabsList>
            <TabsTrigger value="new_user">Neuer Mitarbeiter</TabsTrigger>
            <TabsTrigger value="delete_user">Mitarbeiter löschen</TabsTrigger>
            <TabsTrigger value="access_matrix">Zugriffsmatrix</TabsTrigger>
          </TabsList>

          <TabsContent value="new_user" className="space-y-3 pt-4">
            <p className="text-caption text-gray-400">
              Variablen:{' '}
              {['{{fullName}}', '{{role}}', '{{email}}', '{{phone}}'].map((v) => (
                <code key={v} className="bg-gray-100 px-1 rounded text-xs mr-1">{v}</code>
              ))}
            </p>
            <Input
              label="Betreff"
              placeholder="Neuer Mitarbeiter: {{fullName}}"
              value={settings['portal.ticket.new_user.subject'] ?? ''}
              onChange={(e) => set('portal.ticket.new_user.subject', e.target.value)}
              hint="Leer = Systemstandard. Ticket wird nur erstellt wenn Betreff gesetzt ist."
            />
            <Select
              label="Priorität"
              value={settings['portal.ticket.new_user.priority'] ?? ''}
              onValueChange={(v) => set('portal.ticket.new_user.priority', v)}
              options={[
                { value: '', label: 'Standard (Mittel)' },
                { value: 'low', label: 'Niedrig' },
                { value: 'medium', label: 'Mittel' },
                { value: 'high', label: 'Hoch' },
              ]}
            />
            <Textarea
              label="Beschreibung"
              rows={5}
              placeholder="Ein neuer Mitarbeiter wurde über das Kundenportal angelegt."
              value={settings['portal.ticket.new_user.description'] ?? ''}
              onChange={(e) => set('portal.ticket.new_user.description', e.target.value)}
              hint="Leer = Systemstandard. Mehrzeilig möglich."
            />
          </TabsContent>

          <TabsContent value="delete_user" className="space-y-3 pt-4">
            <p className="text-caption text-gray-400">
              Variablen:{' '}
              {['{{fullName}}', '{{tanssId}}', '{{role}}', '{{email}}', '{{phone}}', '{{mobile}}', '{{reason}}'].map((v) => (
                <code key={v} className="bg-gray-100 px-1 rounded text-xs mr-1">{v}</code>
              ))}
            </p>
            <Input
              label="Betreff"
              placeholder="Löschantrag Mitarbeiter: {{fullName}}"
              value={settings['portal.ticket.delete_user.subject'] ?? ''}
              onChange={(e) => set('portal.ticket.delete_user.subject', e.target.value)}
              hint="Leer = Systemstandard wird verwendet."
            />
            <Select
              label="Priorität"
              value={settings['portal.ticket.delete_user.priority'] ?? ''}
              onValueChange={(v) => set('portal.ticket.delete_user.priority', v)}
              options={[
                { value: '', label: 'Standard (Mittel)' },
                { value: 'low', label: 'Niedrig' },
                { value: 'medium', label: 'Mittel' },
                { value: 'high', label: 'Hoch' },
              ]}
            />
            <Textarea
              label="Beschreibung"
              rows={8}
              placeholder="Der Kunde bittet um Löschung des folgenden Mitarbeiters: ..."
              value={settings['portal.ticket.delete_user.description'] ?? ''}
              onChange={(e) => set('portal.ticket.delete_user.description', e.target.value)}
              hint="Leer = Systemstandard. Mehrzeilig möglich."
            />
          </TabsContent>

          <TabsContent value="access_matrix" className="space-y-3 pt-4">
            <p className="text-caption text-gray-400">
              Variablen:{' '}
              {['{{companyName}}', '{{version}}'].map((v) => (
                <code key={v} className="bg-gray-100 px-1 rounded text-xs mr-1">{v}</code>
              ))}
            </p>
            <Input
              label="Betreff"
              placeholder="Compliance-Änderung [{{companyName}}] v{{version}}"
              value={settings['portal.ticket.access_matrix.subject'] ?? ''}
              onChange={(e) => set('portal.ticket.access_matrix.subject', e.target.value)}
              hint="Leer = Systemstandard. Beschreibung enthält immer die Änderungsliste (Diff)."
            />
            <Select
              label="Priorität"
              value={settings['portal.ticket.access_matrix.priority'] ?? ''}
              onValueChange={(v) => set('portal.ticket.access_matrix.priority', v)}
              options={[
                { value: '', label: 'Standard (Mittel)' },
                { value: 'low', label: 'Niedrig' },
                { value: 'medium', label: 'Mittel' },
                { value: 'high', label: 'Hoch' },
              ]}
            />
          </TabsContent>
        </Tabs>
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── System Calendar Title Templates Section ────────────────────────────────

const DEFAULT_OUTLOOK_TITLE_TEMPLATE =
  '{ort} / {ansprechpartner}, {kunde} / {anmerkung} {ticketMarker}'
const DEFAULT_TANSS_TITLE_TEMPLATE = '{outlookTitle}'
const DEFAULT_OUTLOOK_BODY_TEMPLATE = `TANSS-Support #{supportId}
{ticketLink}
{tanssTicketUrl}

{anmerkung}`

const OUTLOOK_TITLE_VARIABLES: { name: string; description: string }[] = [
  { name: 'ort', description: 'Ort (Buero / Kunde / Remote oder Outlook-Location)' },
  { name: 'ansprechpartner', description: 'Ansprechpartner aus dem TANSS-Ticket' },
  { name: 'kunde', description: 'Kundenname aus dem TANSS-Ticket' },
  { name: 'anmerkung', description: 'Notiz / Beschreibung (Support-Text bzw. Ticket-Subject)' },
  { name: 'ticketId', description: 'TANSS-Ticket-ID (z.B. 1234)' },
  { name: 'ticketSubject', description: 'Subject des verknüpften TANSS-Tickets' },
  { name: 'ticketMarker', description: 'Marker [T#1234] / [TANSS] – wird vom Sync zurückgelesen' },
  { name: 'planningType', description: 'Planungs-Typ-Label (Termin, Privattermin, …)' },
]

const TANSS_TITLE_VARIABLES: { name: string; description: string }[] = [
  { name: 'outlookTitle', description: 'Original Outlook-Subject' },
  { name: 'outlookBody', description: 'Outlook-Bemerkung (Vorschau, ~255 Zeichen)' },
  { name: 'ticketId', description: 'TANSS-Ticket-ID falls verknüpft' },
  { name: 'ticketSubject', description: 'Subject des verknüpften TANSS-Tickets' },
  { name: 'kunde', description: 'Kundenname aus dem TANSS-Ticket' },
  { name: 'ansprechpartner', description: 'Ansprechpartner aus dem TANSS-Ticket' },
]

const OUTLOOK_BODY_VARIABLES: { name: string; description: string }[] = [
  { name: 'supportId', description: 'TANSS-Support-ID' },
  { name: 'ticketLink', description: 'Vorgefertigte Ticket-Link-Zeile (Sysman) oder leer' },
  { name: 'tanssTicketUrl', description: 'Vorgefertigte TANSS-Web-URL-Zeile oder leer' },
  { name: 'anmerkung', description: 'Original-Text aus TANSS (Support-Text)' },
  { name: 'ticketId', description: 'TANSS-Ticket-ID falls verknüpft' },
  { name: 'kunde', description: 'Kundenname aus dem TANSS-Ticket' },
  { name: 'ansprechpartner', description: 'Ansprechpartner aus dem TANSS-Ticket' },
  { name: 'ort', description: 'Ort (Buero / Kunde / Remote oder Outlook-Location)' },
  { name: 'planningType', description: 'Planungs-Typ-Label' },
]

const OUTLOOK_PREVIEW_VARS: Record<string, string> = {
  ort: 'Buero',
  ansprechpartner: 'Max Mustermann',
  kunde: 'Mustermann GmbH',
  anmerkung: 'Server-Migration',
  ticketId: '1234',
  ticketSubject: 'Server-Migration',
  ticketMarker: '[T#1234]',
  planningType: 'Termin',
}

const TANSS_PREVIEW_VARS: Record<string, string> = {
  outlookTitle: 'Beratungstermin Server-Migration',
  outlookBody: 'Agenda: RAID-Status prüfen, Backup einspielen.',
  ticketId: '1234',
  ticketSubject: 'Server-Migration',
  kunde: 'Mustermann GmbH',
  ansprechpartner: 'Max Mustermann',
}

const OUTLOOK_BODY_PREVIEW_VARS: Record<string, string> = {
  supportId: '5678',
  ticketLink: 'Ticket: #1234 — https://sysman.implec.de/tickets/1234',
  tanssTicketUrl: 'TANSS: https://tanss.implec.de/index.php?section=bug&sub=view&bugID=1234',
  anmerkung: 'RAID-Status prüfen, Backup einspielen.',
  ticketId: '1234',
  kunde: 'Mustermann GmbH',
  ansprechpartner: 'Max Mustermann',
  ort: 'Buero',
  planningType: 'Termin',
}

function renderTitlePreview(template: string, vars: Record<string, string>): string {
  let out = template
  for (const [name, value] of Object.entries(vars)) {
    out = out.replace(new RegExp(`\\{${name}\\}`, 'g'), value ?? '')
  }
  out = out.replace(/\s+/g, ' ')
  for (let i = 0; i < 3; i++) {
    out = out.replace(/(^|\/)\s*,\s*/g, '$1')
    out = out.replace(/,\s*(\/|$)/g, '$1')
    out = out.replace(/,\s*,/g, ',')
  }
  for (let i = 0; i < 3; i++) {
    out = out.replace(/\/\s*\//g, '/')
    out = out.replace(/^\s*\/\s*/, '')
    out = out.replace(/\s*\/\s*$/, '')
  }
  out = out.replace(/\s*\/\s*/g, ' / ')
  out = out.replace(/\s*,\s*/g, ', ')
  return out.trim()
}

function renderBodyPreview(template: string, vars: Record<string, string>): string {
  let out = template
  for (const [name, value] of Object.entries(vars)) {
    out = out.replace(new RegExp(`\\{${name}\\}`, 'g'), value ?? '')
  }
  const lines = out.split(/\r?\n/).map((l) => l.trimEnd())
  const trimmed: string[] = []
  for (const line of lines) {
    const isEmpty = line.trim() === ''
    if (isEmpty && (trimmed.length === 0 || trimmed[trimmed.length - 1] === '')) continue
    trimmed.push(isEmpty ? '' : line)
  }
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') trimmed.pop()
  return trimmed.join('\n')
}

function CalendarBodyTemplateField({
  label,
  description,
  value,
  onChange,
  defaultValue,
  variables,
  previewVars,
}: {
  label: string
  description: string
  value: string
  onChange: (v: string) => void
  defaultValue: string
  variables: { name: string; description: string }[]
  previewVars: Record<string, string>
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const effective = (value ?? '').trim() ? value : defaultValue
  const preview = renderBodyPreview(effective, previewVars)

  const insertVariable = (name: string) => {
    const el = textareaRef.current
    const insertText = `{${name}}`
    const current = value ?? ''
    if (!el) {
      onChange(current + insertText)
      return
    }
    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? current.length
    const next = current.slice(0, start) + insertText + current.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      el.focus()
      const cursor = start + insertText.length
      el.setSelectionRange(cursor, cursor)
    })
  }

  return (
    <div className="space-y-3">
      <Textarea
        ref={textareaRef}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={defaultValue}
        hint={description}
        rows={6}
      />

      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="text-caption text-gray-500 mb-1">Vorschau</div>
        <div className="text-small font-medium text-gray-900 whitespace-pre-wrap break-words">
          {preview || <span className="italic text-gray-400">(leer)</span>}
        </div>
      </div>

      <div>
        <div className="text-caption text-gray-500 mb-2">
          Variablen einfügen (Klick fügt am Cursor ein):
        </div>
        <div className="flex flex-wrap gap-1.5">
          {variables.map((v) => (
            <button
              key={v.name}
              type="button"
              onClick={() => insertVariable(v.name)}
              title={v.description}
              className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-0.5 text-caption text-gray-700 hover:bg-brand-50 hover:border-brand-300"
            >
              <code>{`{${v.name}}`}</code>
            </button>
          ))}
        </div>
        <div className="mt-2 space-y-0.5 text-caption text-gray-500">
          {variables.map((v) => (
            <div key={v.name}>
              <code className="text-gray-700">{`{${v.name}}`}</code> – {v.description}
            </div>
          ))}
        </div>
      </div>

      {value && value !== defaultValue && (
        <Button variant="ghost" size="sm" onClick={() => onChange(defaultValue)}>
          Auf Standard zurücksetzen
        </Button>
      )}
    </div>
  )
}

function CalendarTemplateField({
  label,
  description,
  value,
  onChange,
  defaultValue,
  variables,
  previewVars,
  placeholder,
}: {
  label: string
  description: string
  value: string
  onChange: (v: string) => void
  defaultValue: string
  variables: { name: string; description: string }[]
  previewVars: Record<string, string>
  placeholder: string
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const effective = (value ?? '').trim() || defaultValue
  const preview = renderTitlePreview(effective, previewVars)

  const insertVariable = (name: string) => {
    const el = inputRef.current
    const insertText = `{${name}}`
    const current = value ?? ''
    if (!el) {
      onChange(current + insertText)
      return
    }
    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? current.length
    const next = current.slice(0, start) + insertText + current.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      el.focus()
      const cursor = start + insertText.length
      el.setSelectionRange(cursor, cursor)
    })
  }

  return (
    <div className="space-y-3">
      <Input
        ref={inputRef}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        hint={description}
      />

      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="text-caption text-gray-500 mb-1">Vorschau</div>
        <div className="text-small font-medium text-gray-900 break-words">
          {preview || <span className="italic text-gray-400">(leer)</span>}
        </div>
      </div>

      <div>
        <div className="text-caption text-gray-500 mb-2">
          Variablen einfügen (Klick fügt am Cursor ein):
        </div>
        <div className="flex flex-wrap gap-1.5">
          {variables.map((v) => (
            <button
              key={v.name}
              type="button"
              onClick={() => insertVariable(v.name)}
              title={v.description}
              className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-0.5 text-caption text-gray-700 hover:bg-brand-50 hover:border-brand-300"
            >
              <code>{`{${v.name}}`}</code>
            </button>
          ))}
        </div>
        <div className="mt-2 space-y-0.5 text-caption text-gray-500">
          {variables.map((v) => (
            <div key={v.name}>
              <code className="text-gray-700">{`{${v.name}}`}</code> – {v.description}
            </div>
          ))}
        </div>
      </div>

      {value && value.trim() !== defaultValue && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(defaultValue)}
        >
          Auf Standard zurücksetzen
        </Button>
      )}
    </div>
  )
}

const DEFAULT_SYNC_WINDOW_PAST_DAYS = 7
const DEFAULT_SYNC_WINDOW_FUTURE_DAYS = 60
const MAX_SYNC_WINDOW_PAST_DAYS = 365
const MAX_SYNC_WINDOW_FUTURE_DAYS = 730

function SystemCalendarTitlesSection({ settings, set, save, saving }: SystemSectionProps) {
  const pastDaysRaw = settings['calendar.syncWindowPastDays'] ?? ''
  const futureDaysRaw = settings['calendar.syncWindowFutureDays'] ?? ''
  const pastDaysEffective =
    Number.parseInt(pastDaysRaw, 10) >= 0
      ? Number.parseInt(pastDaysRaw, 10)
      : DEFAULT_SYNC_WINDOW_PAST_DAYS
  const futureDaysEffective =
    Number.parseInt(futureDaysRaw, 10) >= 0
      ? Number.parseInt(futureDaysRaw, 10)
      : DEFAULT_SYNC_WINDOW_FUTURE_DAYS

  return (
    <div className="space-y-6">
      <p className="text-small text-gray-500">
        Diese Vorlagen bestimmen, wie Termin-Titel und -Bemerkung beim Sync
        zwischen Outlook und TANSS aufgebaut werden. Variablen in geschweiften
        Klammern werden zur Laufzeit ersetzt; leere Variablen ziehen umgebende
        Trenner (<code>/</code>, <code>,</code>) bzw. Leerzeilen automatisch
        zusammen.
      </p>

      <SettingsSection title="Sync-Zeitfenster" icon={Clock}>
        <p className="text-small text-gray-500 mb-3">
          Legt fest, wie weit in die Vergangenheit bzw. Zukunft Termine
          synchronisiert werden. Aktuelles Fenster:{' '}
          <strong>
            {pastDaysEffective} Tage zurück bis {futureDaysEffective} Tage voraus
          </strong>{' '}
          (= {pastDaysEffective + futureDaysEffective} Tage gesamt).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            type="number"
            min={0}
            max={MAX_SYNC_WINDOW_PAST_DAYS}
            label="Tage rückwärts"
            value={pastDaysRaw}
            onChange={(e) => set('calendar.syncWindowPastDays', e.target.value)}
            placeholder={String(DEFAULT_SYNC_WINDOW_PAST_DAYS)}
            hint={`Standard: ${DEFAULT_SYNC_WINDOW_PAST_DAYS}, Maximum: ${MAX_SYNC_WINDOW_PAST_DAYS}.`}
          />
          <Input
            type="number"
            min={0}
            max={MAX_SYNC_WINDOW_FUTURE_DAYS}
            label="Tage voraus"
            value={futureDaysRaw}
            onChange={(e) => set('calendar.syncWindowFutureDays', e.target.value)}
            placeholder={String(DEFAULT_SYNC_WINDOW_FUTURE_DAYS)}
            hint={`Standard: ${DEFAULT_SYNC_WINDOW_FUTURE_DAYS}, Maximum: ${MAX_SYNC_WINDOW_FUTURE_DAYS}. Größere Fenster verlängern die Sync-Laufzeit.`}
          />
        </div>
      </SettingsSection>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SettingsSection title="Outlook-Kalendereintrag (TANSS → Outlook)" icon={Calendar}>
          <CalendarTemplateField
            label="Titel-Vorlage für Outlook-Termine"
            description="Wird verwendet, wenn ein TANSS-Support nach Outlook gespiegelt wird."
            value={settings['calendar.outlookTitleTemplate'] ?? ''}
            onChange={(v) => set('calendar.outlookTitleTemplate', v)}
            defaultValue={DEFAULT_OUTLOOK_TITLE_TEMPLATE}
            variables={OUTLOOK_TITLE_VARIABLES}
            previewVars={OUTLOOK_PREVIEW_VARS}
            placeholder={DEFAULT_OUTLOOK_TITLE_TEMPLATE}
          />
        </SettingsSection>

        <SettingsSection title="Outlook-Bemerkung (TANSS → Outlook)" icon={Calendar}>
          <CalendarBodyTemplateField
            label="Bemerkungs-Vorlage für Outlook-Termine"
            description="Mehrzeilig — Zeilenumbrüche werden zu HTML-Linebreaks. Leere Variablen kollabieren benachbarte Leerzeilen."
            value={settings['calendar.outlookBodyTemplate'] ?? ''}
            onChange={(v) => set('calendar.outlookBodyTemplate', v)}
            defaultValue={DEFAULT_OUTLOOK_BODY_TEMPLATE}
            variables={OUTLOOK_BODY_VARIABLES}
            previewVars={OUTLOOK_BODY_PREVIEW_VARS}
          />
        </SettingsSection>
      </div>

      <SettingsSection title="TANSS-Termineintrag (Outlook → TANSS)" icon={Clock}>
        <CalendarBodyTemplateField
          label="Text-Vorlage für TANSS-Supports"
          description={'Wird verwendet, wenn ein Outlook-Termin nach TANSS gespiegelt wird (Feld „Text" am Support). Mehrzeilig — Zeilenumbrüche werden in TANSS als Plaintext-Newlines übernommen. Per {outlookBody} kann auch die Outlook-Bemerkung eingebunden werden.'}
          value={settings['calendar.tanssTitleTemplate'] ?? ''}
          onChange={(v) => set('calendar.tanssTitleTemplate', v)}
          defaultValue={DEFAULT_TANSS_TITLE_TEMPLATE}
          variables={TANSS_TITLE_VARIABLES}
          previewVars={TANSS_PREVIEW_VARS}
        />
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── Tenant Customer Portal Section (Overrides) ─────────────────────────────

function TenantCustomerPortalSection({
  tenantId: _tenantId,
  settings,
  set,
  save,
  saving,
}: {
  tenantId: string
  settings: Record<string, string>
  set: (key: string, value: string) => void
  save: () => Promise<void>
  saving: boolean
}) {
  const [globalSettings, setGlobalSettings] = useState<Record<string, string>>({})
  const [globalLoading, setGlobalLoading] = useState(true)
  const { groups, loading: katLoading, error: katError } = useKatTags()

  useEffect(() => {
    getSystemSettings()
      .then(({ data }) => setGlobalSettings(data))
      .catch(() => {})
      .finally(() => setGlobalLoading(false))
  }, [])

  // Tri-state helper: 'inherit' | 'on' | 'off' based on whether tenant has an explicit value
  const featureState = (key: string): 'inherit' | 'on' | 'off' => {
    const v = settings[key]
    if (v === 'on') return 'on'
    if (v === 'off') return 'off'
    return 'inherit'
  }
  const globalFeatureLabel = (key: string) => (globalSettings[key] === 'off' ? 'aus' : 'an')

  const setTriState = (key: string, state: 'inherit' | 'on' | 'off') => {
    set(key, state === 'inherit' ? '' : state)
  }

  // Select-override helper (serviceWindow, transparency)
  const selectState = (key: string): string => settings[key] ?? ''
  const globalSelectLabel = (key: string, opts: { value: string; label: string }[]) => {
    const v = globalSettings[key] ?? opts[0]!.value
    return opts.find((o) => o.value === v)?.label ?? v
  }

  // Kat override: if tenant has explicit disabled list, use that; otherwise inherit global
  const tenantKatRaw = settings['customerPortal.kat.disabled']
  const hasKatOverride = tenantKatRaw !== undefined && tenantKatRaw !== ''
  const effectiveDisabled = hasKatOverride
    ? parseKatDisabled(tenantKatRaw)
    : parseKatDisabled(globalSettings['customerPortal.kat.disabled'])

  const toggleKat = (id: number) => {
    const next = new Set(effectiveDisabled)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    set('customerPortal.kat.disabled', JSON.stringify([...next]))
  }
  const resetKatToGlobal = () => set('customerPortal.kat.disabled', '')

  if (globalLoading) return <LoadingState />

  return (
    <div className="space-y-6">
      <p className="text-small text-gray-500">
        Hier werden nur Abweichungen vom Global-Default gesetzt. Felder mit „Global übernehmen" erben
        den globalen Wert. Änderungen am Global-Default wirken sich automatisch auch hier aus, solange
        kein Override gesetzt ist.
      </p>

      <SettingsSection title="Portal-Funktionen (Override)" icon={Boxes}>
        <div className="space-y-2">
          {PORTAL_FEATURES.map((f) => {
            const state = featureState(f.key)
            return (
              <div key={f.key} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
                <div className="flex-1">
                  <div className="text-small font-medium">{f.label}</div>
                  <div className="text-caption text-gray-500">
                    Global: <strong>{globalFeatureLabel(f.key)}</strong>
                  </div>
                </div>
                <Select
                  value={state}
                  onValueChange={(v) => setTriState(f.key, v as 'inherit' | 'on' | 'off')}
                  options={[
                    { value: 'inherit', label: 'Global übernehmen' },
                    { value: 'on', label: 'Aktiv' },
                    { value: 'off', label: 'Deaktiviert' },
                  ]}
                />
              </div>
            )
          })}
        </div>
      </SettingsSection>

      <SettingsSection title="SLA & Servicezeit (Override)" icon={Clock}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-caption text-gray-700 mb-1">Servicezeit</label>
            <Select
              value={selectState('customerPortal.serviceWindow')}
              onValueChange={(v) => set('customerPortal.serviceWindow', String(v))}
              options={[
                { value: '', label: `Global übernehmen (${globalSelectLabel('customerPortal.serviceWindow', SERVICE_WINDOW_OPTIONS)})` },
                ...SERVICE_WINDOW_OPTIONS,
              ]}
            />
          </div>
          <div>
            <label className="block text-caption text-gray-700 mb-1">Transparenz für Kunden</label>
            <Select
              value={selectState('customerPortal.transparency')}
              onValueChange={(v) => set('customerPortal.transparency', String(v))}
              options={[
                { value: '', label: `Global übernehmen (${globalSelectLabel('customerPortal.transparency', TRANSPARENCY_OPTIONS)})` },
                ...TRANSPARENCY_OPTIONS,
              ]}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title="Kategorien (Override)" icon={Plus}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-caption text-gray-500">
            {hasKatOverride
              ? 'Diese Firma verwendet eine eigene Kategorienauswahl.'
              : 'Diese Firma übernimmt die globale Kategorienauswahl.'}
          </p>
          {hasKatOverride && (
            <Button variant="ghost" size="sm" onClick={resetKatToGlobal}>
              Auf Global zurücksetzen
            </Button>
          )}
        </div>
        {katLoading && <div className="text-small text-gray-500">Lade Kat-Tags …</div>}
        {katError && <div className="text-small text-red-700">{katError}</div>}
        {!katLoading && !katError && groups.length === 0 && (
          <div className="text-small text-gray-500">Keine Kat-Tags vorhanden.</div>
        )}
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="border rounded p-3">
              <div className="font-medium text-small mb-2 flex items-center justify-between">
                <span>{g.label}</span>
                <Checkbox
                  checked={!effectiveDisabled.has(g.id)}
                  onChange={() => toggleKat(g.id)}
                  label="Gruppe aktiv"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-caption">
                {g.children.map((c) => (
                  <Checkbox
                    key={c.id}
                    checked={!effectiveDisabled.has(c.id)}
                    onChange={() => toggleKat(c.id)}
                    label={c.label}
                    disabled={effectiveDisabled.has(g.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </SettingsSection>

      <SaveButton save={save} saving={saving} />
    </div>
  )
}

// ─── User Management Section ─────────────────────────────────────────────────

function UserManagementSection({ currentUserId }: { currentUserId: string }) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ManagedUser | null>(null)

  const fetchUsers = useCallback(async () => {
    try {
      // Internal users only — customer portal users are managed in the customer portal admin.
      const { data } = await getInternalUsers()
      setUsers(data)
      setError('')
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        setError('Keine Berechtigung für Benutzerverwaltung.')
      } else {
        const msg = extractErrorMessage(err)
        setError(`Benutzer konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const updateUser = async (
    id: string,
    updates: {
      isActive?: boolean
      role?: string
      password?: string
    },
  ) => {
    setActionLoading(id)
    try {
      const { data } = await updateUserApi(id, updates)
      setUsers((prev) => prev.map((u) => (u.id === id ? data : u)))
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data
      const message = data?.error || data?.message || 'Fehler beim Aktualisieren'
      alert(message)
    } finally {
      setActionLoading(null)
    }
  }

  const deleteUser = async (id: string) => {
    setActionLoading(id)
    try {
      await deleteUserApi(id)
      setUsers((prev) => prev.filter((u) => u.id !== id))
      setPendingDelete(null)
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data
      const message = data?.error || data?.message || 'Fehler beim Löschen'
      alert(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleUserCreated = (newUser: ManagedUser) => {
    setUsers((prev) => [...prev, newUser].sort((a, b) => a.name.localeCompare(b.name)))
    setShowCreateForm(false)
  }

  const pendingUsers = users.filter((u) => !u.isActive)
  const activeUsers = users.filter((u) => u.isActive)

  if (loading) return <LoadingState />

  return (
    <div>
      {error && <StatusBanner type="error" text={error} />}

      {/* Create new user */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-small font-semibold text-gray-900">Benutzer anlegen</h2>
          {!showCreateForm && (
            <Button
              size="sm"
              leadingIcon={<Plus className="w-3.5 h-3.5" />}
              onClick={() => setShowCreateForm(true)}
            >
              Neuer Benutzer
            </Button>
          )}
        </div>
        {showCreateForm && (
          <CreateUserForm
            onCancel={() => setShowCreateForm(false)}
            onCreated={handleUserCreated}
          />
        )}
      </section>

      {/* Wartende Freigaben */}
      {pendingUsers.length > 0 && (
        <section className="mb-8">
          <h2 className="text-small font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-500" />
            Warten auf Freigabe ({pendingUsers.length})
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg divide-y divide-amber-200">
            {pendingUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                currentUserId={currentUserId}
                isLoading={actionLoading === user.id}
                onUpdate={updateUser}
                onDelete={(_id, _name) => setPendingDelete(user)}
                isPending
              />
            ))}
          </div>
        </section>
      )}

      {/* Active users */}
      <section>
        <h2 className="text-small font-semibold text-gray-900 mb-3">
          Aktive Benutzer ({activeUsers.length})
        </h2>
        <Card>
          <div className="divide-y divide-gray-200">
            {activeUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                currentUserId={currentUserId}
                isLoading={actionLoading === user.id}
                onUpdate={updateUser}
                onDelete={(_id, _name) => setPendingDelete(user)}
              />
            ))}
            {activeUsers.length === 0 && (
              <p className="px-4 py-3 text-small text-gray-500">Keine aktiven Benutzer.</p>
            )}
          </div>
        </Card>
      </section>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Benutzer löschen"
        message={
          pendingDelete
            ? `Möchtest du "${pendingDelete.name}" wirklich endgültig löschen?`
            : ''
        }
        confirmLabel="Löschen"
        variant="danger"
        loading={actionLoading === pendingDelete?.id}
        onConfirm={() => { if (pendingDelete) deleteUser(pendingDelete.id) }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

// ─── Create User Form ────────────────────────────────────────────────────────

function CreateUserForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (user: ManagedUser) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('technician')
  const [password, setPassword] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setError('')
    if (!name.trim() || !email.trim()) {
      setError('Name und E-Mail sind erforderlich.')
      return
    }
    if (password && password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.')
      return
    }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim(),
        role,
        isActive,
      }
      if (password) payload.password = password
      const { data } = await createUser(payload)
      onCreated(data)
    } catch (err: unknown) {
      const msg = extractErrorMessage(err)
      setError(msg || 'Benutzer konnte nicht angelegt werden.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingsInput label="Name" value={name} onChange={setName} placeholder="Max Mustermann" />
          <SettingsInput
            label="E-Mail"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="max@firma.de"
          />
          <Select
            label="Rolle"
            value={role}
            onValueChange={setRole}
            options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }))}
          />
          <SettingsInput
            label="Passwort (optional)"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="Mind. 8 Zeichen – leer lassen für reinen M365-Login"
          />
        </div>

        <Checkbox
          label="Sofort aktivieren (keine separate Freigabe nötig)"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />

        <p className="text-caption text-gray-500">
          Ohne Passwort kann sich der Benutzer ausschließlich per Microsoft 365 anmelden. Mit
          Passwort funktioniert zusätzlich der klassische Login über E-Mail + Passwort.
        </p>

        {error && <StatusBanner type="error" text={error} />}

        <div className="flex items-center gap-2">
          <Button
            onClick={submit}
            loading={saving}
            leadingIcon={<Save className="w-4 h-4" />}
          >
            Benutzer anlegen
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Abbrechen
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Modules Settings Tab ────────────────────────────────────────────────────

function ModulesSettingsTab() {
  const { statuses, setModuleStatus, fetchStatuses, loaded } = useModulesStore()
  const currentUser = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [, seg1] = useSettingsSegments()
  const [saving, setSaving] = useState<string | null>(null)
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  // Pro-Modul Dev-Mode Flags
  const [devModeModules, setDevModeModules] = useState<Set<string>>(new Set())
  const [savingDev, setSavingDev] = useState<string | null>(null)
  const { settings: sysSettings, set: setSysSetting, save: saveSysSettings, saving: savingSys } = useSystemSettings()

  useEffect(() => {
    if (!loaded) fetchStatuses()
  }, [loaded, fetchStatuses])

  useEffect(() => {
    getDevModeModules()
      .then(({ data }) => setDevModeModules(new Set<string>(data.modules ?? [])))
      .catch(() => { /* ignore */ })
  }, [])

  const handleDevModeToggle = async (e: React.MouseEvent, moduleId: string) => {
    e.stopPropagation()
    setSavingDev(moduleId)
    const nextEnabled = !devModeModules.has(moduleId)
    try {
      await setDevModeModule(moduleId, nextEnabled)
      setDevModeModules((prev) => {
        const next = new Set(prev)
        if (nextEnabled) next.add(moduleId); else next.delete(moduleId)
        return next
      })
    } catch {
      // ignore
    } finally {
      setSavingDev(null)
    }
  }

  const grouped: Record<string, { id: string; name: string; description: string; defaultStatus: ModuleStatus }[]> = getModulesByCategory()
  const categories = Object.keys(grouped)

  // Derive active category from URL (seg1 = slug, fallback to first category)
  const activeCategory: string = (seg1 ? SLUG_TO_MODULE_CATEGORY[seg1] : undefined) ?? categories[0] ?? ''

  const [error, setError] = useState('')

  const handleToggle = async (e: React.MouseEvent, moduleId: string, currentStatus: ModuleStatus) => {
    e.stopPropagation()
    setSaving(moduleId)
    setError('')
    const newStatus: ModuleStatus = currentStatus === 'beta' ? 'stable' : 'beta'
    try {
      await setModuleStatus(moduleId, newStatus)
    } catch (err: unknown) {
      const axErr = err as { response?: { status?: number } }
      if (axErr?.response?.status === 403) {
        setError('Keine Berechtigung.')
      } else {
        setError('Modulstatus konnte nicht geaendert werden.')
      }
    } finally {
      setSaving(null)
    }
  }

  const currentModules = activeCategory ? (grouped[activeCategory] ?? []) : []
  const activeModule = currentModules.find((m) => m.id === activeModuleId)

  return (
    <div className="space-y-4">
      {/* Row 1: Category Tabs */}
      <Tabs
        value={activeCategory}
        onValueChange={(v) => { navigate('/einstellungen/module/' + (MODULE_CATEGORY_SLUGS[v as ModuleCategory] ?? v.toLowerCase())); setActiveModuleId(null) }}
      >
        <TabsList>
          {categories.map((category) => (
            <TabsTrigger key={category} value={category}>
              {category}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Row 2: Module Tabs within category */}
      <div className="flex gap-1 flex-wrap">
        {currentModules.map((mod) => {
          const status = statuses[mod.id] || mod.defaultStatus
          const isActiveMod = activeModuleId === mod.id
          return (
            <Button
              key={mod.id}
              variant={isActiveMod ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveModuleId(isActiveMod ? null : mod.id)}
            >
              {mod.name}
              {status === 'beta' && <BetaBadge />}
            </Button>
          )
        })}
      </div>

      {error && <StatusBanner type="error" text={error} />}

      {/* Module Detail: Beta-Toggle + Description + Settings */}
      {activeModule && (
        <div className="space-y-4">
          {/* Module info + toggles */}
          <Card>
            <Card.Body className="space-y-3">
              <div>
                <p className="text-small font-medium text-gray-900">{activeModule.name}</p>
                <p className="text-caption text-gray-500">{activeModule.description}</p>
              </div>
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                {/* Beta-Modus */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-caption font-medium text-gray-700">Beta-Modus</div>
                    <div className="text-caption text-gray-500">
                      {(statuses[activeModule.id] || activeModule.defaultStatus) === 'beta'
                        ? 'Aktiv – Modul wird als Beta markiert (Badge im Menü).'
                        : 'Aus – Modul ist stabil.'}
                    </div>
                  </div>
                  <Switch
                    checked={(statuses[activeModule.id] || activeModule.defaultStatus) === 'beta'}
                    disabled={saving === activeModule.id}
                    onChange={(e) =>
                      handleToggle(
                        e as unknown as React.MouseEvent,
                        activeModule.id,
                        statuses[activeModule.id] || activeModule.defaultStatus,
                      )
                    }
                  />
                </div>
                {/* Dev-Mode */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-caption font-medium text-gray-700">Dev-Mode</div>
                    <div className="text-caption text-gray-500">
                      {devModeModules.has(activeModule.id)
                        ? 'Aktiv – Adapter-Aufrufe dieses Moduls gehen auf Dev-Instanzen.'
                        : 'Aus – Produktiv-Credentials.'}
                    </div>
                  </div>
                  <Switch
                    checked={devModeModules.has(activeModule.id)}
                    disabled={savingDev === activeModule.id}
                    onChange={(e) =>
                      handleDevModeToggle(e as unknown as React.MouseEvent, activeModule.id)
                    }
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Module-specific settings */}
          {activeModuleId === 'finanzen-automatic-support-workflow' ? (
            <AswTenantSettingsSection />
          ) : activeModuleId === 'vertriebsboard' ? (
            <VertriebsboardPhaseMappingSection isAdmin={currentUser?.role === 'admin'} />
          ) : activeModuleId === 'sap-auftraege' ? (
            <SapAuftraegeSettingsSection />
          ) : activeModuleId === 'neukundengewinnung' ? (
            <EmptyState
              title="Diese Einstellungen finden Sie im Firma-Tab"
              description="ICP-Scoring, Geo-Zonen und Pain Points werden pro Firma konfiguriert. Siehe: Firma > Vertrieb."
              size="sm"
            />
          ) : activeModuleId === 'tanss-teams-bridge' ? (
            <TanssTeamsBridgeSettingsSection />
          ) : activeModuleId === 'tanss-outlook-sync' ? (
            <SystemCalendarTitlesSection settings={sysSettings} set={setSysSetting} save={saveSysSettings} saving={savingSys} />
          ) : activeModuleId === 'kundenportal-admin' ? (
            <SystemCustomerPortalSection settings={sysSettings} set={setSysSetting} save={saveSysSettings} saving={savingSys} />
          ) : activeModuleId === 'statistiken' ? (
            <StatistikenSettingsSection />
          ) : activeModuleId === 'artikel' ? (
            <Card>
              <Card.Body className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-small font-medium text-gray-900">Warengruppen, Preisgruppen &amp; Defaults</p>
                  <p className="text-caption text-gray-500">SAP-Warengruppen, Preisgruppen und Standard-Werte für neue Artikel konfigurieren.</p>
                </div>
                <RouterLink
                  to="/einstellungen/artikel"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
                >
                  Zu den Einstellungen →
                </RouterLink>
              </Card.Body>
            </Card>
          ) : (
            <EmptyState
              title="Keine zusätzlichen Einstellungen"
              description="Für dieses Modul gibt es keine zusätzlichen Einstellungen."
              size="sm"
            />
          )}
        </div>
      )}

      {/* No module selected hint */}
      {!activeModule && currentModules.length > 0 && (
        <EmptyState
          title="Kein Modul ausgewählt"
          description="Wähle ein Modul oben, um Einstellungen zu konfigurieren."
          size="sm"
        />
      )}

    </div>
  )
}

// ─── Statistics & Reports: Module Settings ───────────────────────────────────
//
// Threshold (days since last invoice) that determines whether a customer is
// considered active vs. inactive in the "Reports → Customers" view.

function StatistikenSettingsSection() {
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const [threshold, setThreshold] = useState<string>('90')
  const [saved, setSavedValue] = useState<string>('90')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedFlash, setSavedFlash] = useState(false)

  useEffect(() => {
    if (!activeTenantId) return
    let cancelled = false
    setLoading(true)
    getTenantSettings(activeTenantId)
      .then((r) => {
        if (cancelled) return
        const v = String(r.data?.['customer_active_threshold_days'] ?? '90')
        setThreshold(v)
        setSavedValue(v)
      })
      .catch(() => {/* setting evtl. noch nicht angelegt */})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [activeTenantId])

  async function save() {
    if (!activeTenantId) return
    const n = Number(threshold)
    if (!Number.isFinite(n) || n <= 0) {
      setSaveError('Schwelle muss eine positive Zahl in Tagen sein')
      return
    }
    setSaving(true)
    setSaveError(null)
    setSavedFlash(false)
    try {
      await updateTenantSettings(activeTenantId, {
        'customer_active_threshold_days': String(Math.round(n)),
      })
      setSavedValue(String(Math.round(n)))
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2500)
    } catch (err) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setSaveError(axErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Speichern fehlgeschlagen'))
    } finally {
      setSaving(false)
    }
  }

  const dirty = threshold !== saved

  return (
    <Card>
      <Card.Header>
        <Card.Title>Aktiv-/Inaktiv-Schwelle</Card.Title>
        <p className="text-small text-gray-500">
          Ein Kunde gilt unter „Auswertungen → Kunden" als aktiv, wenn die
          letzte Rechnung nicht älter ist als die hier eingestellte Anzahl Tage.
          Datenquelle: SAP-Dokumenten-Cache (Belege vom Typ „Rechnung"). Wenn
          der Cache noch leer ist, fällt die Auswertung auf das SAP-Flag
          `is_active` zurück.
        </p>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <LoadingState label="Lade Einstellung…" />
        ) : (
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-48">
              <Input
                type="number"
                min="1"
                step="1"
                label="Schwelle (Tage)"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </Button>
            {savedFlash ? <span className="text-small text-green-600">Gespeichert</span> : null}
            {saveError ? <span className="text-small text-red-600">{saveError}</span> : null}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── TANSS ↔ Teams Bridge: Module Settings ───────────────────────────────────
//
// All bridge settings live here. Status/monitoring (ticket syncs,
// messages, user mapping, subscriptions, debug diagnostics) is found under
// Admin → TANSS↔Teams Bridge.

function TanssTeamsBridgeSettingsSection() {
  return (
    <div className="space-y-4">
      <TanssTeamsBridgeDatePrefixCard />
      <TanssTeamsBridgeStartMessageCard />
    </div>
  )
}

const PREFIX_TEMPLATE_DEFAULT = '[{quelle} {uhrzeit}]'

const PREFIX_VARIABLES: { key: string; label: string; example: string }[] = [
  { key: '{quelle}',    label: 'Quelle',      example: 'TANSS / Teams' },
  { key: '{uhrzeit}',   label: 'Uhrzeit',     example: '18:25' },
  { key: '{datum}',     label: 'Datum',        example: '11.05.' },
  { key: '{datumJahr}', label: 'Datum + Jahr', example: '11.05.2026' },
  { key: '{wochentag}', label: 'Wochentag',    example: 'Mo.' },
]

function TanssTeamsBridgeDatePrefixCard() {
  const [template, setTemplate] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getSystemSettings()
      .then(({ data }) => setTemplate(data['tanss_teams_bridge.prefixTemplate'] ?? PREFIX_TEMPLATE_DEFAULT))
      .catch(() => setTemplate(PREFIX_TEMPLATE_DEFAULT))
  }, [])

  const save = async (val: string) => {
    setSaving(true)
    setSaved(false)
    setError('')
    try {
      await updateSystemSettings({ 'tanss_teams_bridge.prefixTemplate': val })
      setSaved(true)
    } catch (err) {
      setError(extractErrorMessage(err) || 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const insertVariable = (key: string) => {
    const el = inputRef.current
    if (!el || template === null) return
    const start = el.selectionStart ?? template.length
    const end = el.selectionEnd ?? template.length
    const next = template.slice(0, start) + key + template.slice(end)
    setTemplate(next)
    setSaved(false)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + key.length, start + key.length)
    })
  }

  if (template === null) return <LoadingState />

  return (
    <Card>
      <Card.Header>
        <h2 className="text-h3">Nachrichten-Prefix</h2>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {error && <StatusBanner type="error" text={error} />}
          <p className="text-caption text-gray-500">
            Vorlage für die Info-Zeile, die vor jeder gespiegelten Nachricht steht.
            Leer lassen = kein Prefix. Standard: <code>{PREFIX_TEMPLATE_DEFAULT}</code>
          </p>
          <div className="space-y-2">
            <div className="text-caption font-medium text-gray-600">Variablen – Klick zum Einfügen</div>
            <div className="flex flex-wrap gap-1.5">
              {PREFIX_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVariable(v.key)}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-caption font-mono hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  title={v.example}
                >
                  {v.key}
                  <span className="text-gray-400 font-sans">→ {v.example}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Input
              ref={inputRef}
              value={template}
              onChange={(e) => { setTemplate((e as unknown as React.ChangeEvent<HTMLInputElement>).target.value); setSaved(false) }}
              placeholder={PREFIX_TEMPLATE_DEFAULT}
              className="font-mono"
            />
            <Button variant="primary" size="sm" onClick={() => save(template)} loading={saving}>
              Speichern
            </Button>
            {saved && <span className="text-caption text-green-600">Gespeichert</span>}
          </div>
          <div className="text-caption text-gray-400">
            {(() => {
              const t = String(template ?? '')
              const preview = (quelle: string) => t
                .replace(/\{quelle\}/g, quelle)
                .replace(/\{uhrzeit\}/g, '18:25')
                .replace(/\{datum\}/g, '11.05.')
                .replace(/\{datumJahr\}/g, '11.05.2026')
                .replace(/\{wochentag\}/g, 'Mo.')
              return <>Vorschau TANSS→Teams: <code>{preview('TANSS')}</code>&ensp;·&ensp;Teams→TANSS: <code>{preview('Teams')}</code></>
            })()}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// Initial message template for new Teams group chats.
// Persistence + variable definitions: services/tanssTeamsBridge.ts.

interface StartMessageVariable {
  key: string
  label: string
  description: string
}

interface StartMessageConfig {
  template: string
  default: string
  variables: StartMessageVariable[]
}

function TanssTeamsBridgeStartMessageCard() {
  const [config, setConfig] = useState<StartMessageConfig | null>(null)
  const [draft, setDraft] = useState<string>('')
  const [previewHtml, setPreviewHtml] = useState<string>('')
  const [previewErr, setPreviewErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    let cancelled = false
    getTeamsBridgeStartMessage()
      .then(({ data }) => {
        if (cancelled) return
        setConfig(data)
        setDraft(data.template)
      })
      .catch((e: unknown) => !cancelled && setError(extractErrorMessage(e) || 'Konnte nicht geladen werden.'))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (config == null) return
    const handle = window.setTimeout(() => {
      previewTeamsBridgeStartMessage(draft)
        .then(({ data }) => {
          setPreviewHtml(data.html)
          setPreviewErr(null)
        })
        .catch((e: unknown) => setPreviewErr(extractErrorMessage(e) || 'Vorschau-Fehler'))
    }, 250)
    return () => window.clearTimeout(handle)
  }, [draft, config])

  function insertVariable(label: string) {
    const ta = textareaRef.current
    if (!ta) {
      setDraft((d) => d + label)
      return
    }
    const start = ta.selectionStart ?? draft.length
    const end = ta.selectionEnd ?? draft.length
    const next = draft.slice(0, start) + label + draft.slice(end)
    setDraft(next)
    window.requestAnimationFrame(() => {
      ta.focus()
      const pos = start + label.length
      ta.setSelectionRange(pos, pos)
    })
  }

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage('')
    try {
      const { data } = await saveTeamsBridgeStartMessage(draft)
      setDraft(data.template)
      if (config) setConfig({ ...config, template: data.template })
      setMessage('Startnachricht gespeichert.')
    } catch (e) {
      setError(extractErrorMessage(e) || 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  if (error && config == null) return <StatusBanner type="error" text={error} />
  if (config == null) return <LoadingState />

  const isDirty = draft !== config.template
  const isDefault = draft.trim() === config.default.trim()

  return (
    <Card>
      <Card.Header>
        <h2 className="text-h3">Startnachricht-Vorlage</h2>
        <p className="text-caption text-gray-500">
          Wird beim Anlegen eines neuen Teams-Gruppenchats als erste Nachricht gepostet und an die TANSS-Ticketreferenz gepinnt. Markdown-light: <code className="font-mono text-xs">**fett**</code> und <code className="font-mono text-xs">_kursiv_</code>. Zeilen mit ausschließlich leeren Variablen werden automatisch ausgelassen.
        </p>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {error && <StatusBanner type="error" text={error} />}
          {message && <StatusBanner type="success" text={message} />}
          <div>
            <div className="text-small font-medium mb-2">Verfügbare Variablen — Klick zum Einfügen</div>
            <div className="flex flex-wrap gap-2">
              {config.variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVariable(v.label)}
                  className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-xs font-mono text-gray-800 hover:bg-gray-100"
                  title={v.description}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            className="w-full rounded-md border border-gray-300 bg-white p-3 font-mono text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            spellCheck={false}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={() => void onSave()} disabled={!isDirty || saving}>
              {saving ? 'Speichert …' : 'Speichern'}
            </Button>
            <Button variant="secondary" onClick={() => setDraft(config.template)} disabled={!isDirty}>
              Verwerfen
            </Button>
            <Button variant="ghost" onClick={() => setDraft(config.default)} disabled={isDefault}>
              Standard wiederherstellen
            </Button>
          </div>
          <div>
            <div className="text-small font-medium mb-2">
              Vorschau · Beispiel-Ticket #273293 „M. Peters: Performance-Problem neues Notebook" · Kunde „Peters GmbH" · Ansprechpartner „Markus Peters"
            </div>
            {previewErr ? (
              <StatusBanner type="error" text={`Vorschau-Fehler: ${previewErr}`} />
            ) : (
              <div
                className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-900"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── SAP Orders & Quotations: Module Settings ─────────────────────────────
// Manages the SAP price list number that newly created/pushed items write
// their sales price to. SAP B1 rejects direct `SalesPrice` assignments;
// the correct list can be set explicitly per tenant here
// (instead of being guessed automatically).
interface SapPriceListOption { PriceListNo: number; PriceListName?: string; Active?: 'tYES' | 'tNO' }
interface SapManufacturerOption { Code: number; ManufacturerName?: string; Status?: 'msActive' | 'msInactive' }

function SapAuftraegeSettingsSection() {
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const [priceLists, setPriceLists] = useState<SapPriceListOption[]>([])
  const [manufacturers, setManufacturers] = useState<SapManufacturerOption[]>([])
  const [loadingLists, setLoadingLists] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const [loadingMfgs, setLoadingMfgs] = useState(true)
  const [mfgError, setMfgError] = useState<string | null>(null)

  const [defaultPriceListNo, setDefaultPriceListNo] = useState<string>('')
  const [savedPriceListNo, setSavedPriceListNo] = useState<string>('')
  const [defaultManufacturerCode, setDefaultManufacturerCode] = useState<string>('')
  const [savedManufacturerCode, setSavedManufacturerCode] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!activeTenantId) return
    let cancelled = false
    setLoadingLists(true)
    setListError(null)
    getSapPriceLists(activeTenantId)
      .then((r) => {
        if (cancelled) return
        setPriceLists(r.data?.data ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        const msg = err?.response?.data?.error ?? (err instanceof Error ? err.message : String(err))
        setListError(msg)
      })
      .finally(() => { if (!cancelled) setLoadingLists(false) })

    setLoadingMfgs(true)
    setMfgError(null)
    getSapManufacturers(activeTenantId)
      .then((r) => {
        if (cancelled) return
        setManufacturers(r.data?.data ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        const msg = err?.response?.data?.error ?? (err instanceof Error ? err.message : String(err))
        setMfgError(msg)
      })
      .finally(() => { if (!cancelled) setLoadingMfgs(false) })
    return () => { cancelled = true }
  }, [activeTenantId])

  useEffect(() => {
    if (!activeTenantId) return
    let cancelled = false
    getTenantSettings(activeTenantId)
      .then((r) => {
        if (cancelled) return
        const v = String(r.data?.['sap.defaultPriceListNo'] ?? '')
        setDefaultPriceListNo(v)
        setSavedPriceListNo(v)
        const m = String(r.data?.['sap.defaultManufacturerCode'] ?? '')
        setDefaultManufacturerCode(m)
        setSavedManufacturerCode(m)
      })
      .catch(() => { /* setting may not exist yet */ })
    return () => { cancelled = true }
  }, [activeTenantId])

  async function save() {
    if (!activeTenantId) return
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      await updateTenantSettings(activeTenantId, {
        'sap.defaultPriceListNo': defaultPriceListNo,
        'sap.defaultManufacturerCode': defaultManufacturerCode,
      })
      setSavedPriceListNo(defaultPriceListNo)
      setSavedManufacturerCode(defaultManufacturerCode)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setSaveError(axErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Speichern fehlgeschlagen'))
    } finally {
      setSaving(false)
    }
  }

  const dirty =
    defaultPriceListNo !== savedPriceListNo ||
    defaultManufacturerCode !== savedManufacturerCode

  const options = priceLists
    .filter((p) => p.Active !== 'tNO')
    .sort((a, b) => a.PriceListNo - b.PriceListNo)
    .map((p) => ({
      value: String(p.PriceListNo),
      label: `${p.PriceListNo} — ${p.PriceListName ?? '(ohne Name)'}`,
    }))

  const mfgOptions = manufacturers
    .filter((m) => m.Status !== 'msInactive')
    .sort((a, b) => a.Code - b.Code)
    .map((m) => ({
      value: String(m.Code),
      label: `${m.Code} — ${m.ManufacturerName ?? '(ohne Name)'}`,
    }))

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div>
          <p className="text-small font-medium text-gray-900">Standard-Verkaufspreisliste (SAP)</p>
          <p className="text-caption text-gray-500">
            Beim Anlegen neuer Artikel in SAP (über Resolve-Modal oder Artikelverwaltung) schreibt
            SysMan den Verkaufspreis in diese Preisliste. Wenn nichts gesetzt ist, wird der Artikel
            <strong> ohne Preis</strong> in SAP angelegt — er ist gültig, der Preis muss dann in
            SAP B1 manuell ergänzt werden. Automatisch wird keine Liste mehr geraten, weil
            SAP die Auswahl sonst regelmäßig mit „Base price list not found" ablehnt.
          </p>
        </div>

        {loadingLists ? (
          <LoadingState label="PriceLists werden aus SAP geladen…" />
        ) : listError ? (
          <ErrorState title="PriceLists konnten nicht geladen werden" description={listError} />
        ) : options.length === 0 ? (
          <EmptyState
            title="Keine aktiven SAP-PriceLists gefunden"
            description="In SAP B1 sind keine aktiven Preislisten konfiguriert. Bitte erst in SAP eine Liste anlegen."
            size="sm"
          />
        ) : (
          <Select
            label="Default-Preisliste"
            value={defaultPriceListNo}
            onValueChange={(v) => setDefaultPriceListNo(v)}
            options={[{ value: '', label: '(nicht setzen — Artikel ohne Preis anlegen)' }, ...options]}
            placeholder="Bitte wählen…"
          />
        )}

        <div className="border-t border-gray-100 pt-4">
          <p className="text-small font-medium text-gray-900">Default-Hersteller (SAP)</p>
          <p className="text-caption text-gray-500">
            Manche SAP-B1-Mandanten haben den „Generic Manufacturer"-Sentinel (Code -1)
            deaktiviert oder gelöscht und lehnen das Anlegen mit „(1302) Bitte wählen Sie
            einen gültigen Hersteller" ab. Hier kannst du explizit einen real existierenden
            Hersteller wählen, der bei neu angelegten Artikeln gesetzt wird, falls keiner
            am Artikel hinterlegt ist.
          </p>
        </div>

        {loadingMfgs ? (
          <LoadingState label="Hersteller werden aus SAP geladen…" />
        ) : mfgError ? (
          <ErrorState title="Hersteller konnten nicht geladen werden" description={mfgError} />
        ) : mfgOptions.length === 0 ? (
          <EmptyState
            title="Keine aktiven Hersteller in SAP"
            description="In SAP B1 sind keine aktiven Hersteller hinterlegt. Bitte erst in SAP einen Hersteller anlegen."
            size="sm"
          />
        ) : (
          <Select
            label="Default-Hersteller"
            value={defaultManufacturerCode}
            onValueChange={(v) => setDefaultManufacturerCode(v)}
            options={[
              { value: '', label: '(automatisch — erster aktiver Hersteller)' },
              { value: '-1', label: '-1 — kein Hersteller (SAP-Standard, falls erlaubt)' },
              ...mfgOptions,
            ]}
            placeholder="Bitte wählen…"
          />
        )}

        {saveError && <p className="text-small text-red-700">{saveError}</p>}
        {saved && <p className="text-small text-emerald-700">Gespeichert.</p>}

        <div className="flex justify-end gap-2">
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Save className="w-4 h-4" />}
            onClick={save}
            disabled={!dirty || saving}
            loading={saving}
          >
            Speichern
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Vertrieb Settings Section (Tenant-Level) ─────────────────────────────

function VertriebSettingsSection({ tenantId }: { tenantId: string }) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">
        Vertriebseinstellungen für diese Firma. ICP-Scoring, Geo-Zonen und Pain Points
        steuern die automatische Neukundengewinnung.
      </p>
      <NeukundengewinnungModuleSettings tenantId={tenantId} />
    </div>
  )
}

// ─── Sales Board Phase Mapping (Module Settings) ──────────────────────────

interface TanssTag {
  id: number
  name: string
  isGroupParent: boolean
  groupTagId: number
  parentName: string | null
}

interface PhaseMapping {
  phase: string
  name: string
  probability: number
  tagIds: number[]
  tagNames: string[]
}

function VertriebsboardPhaseMappingSection({ isAdmin }: { isAdmin: boolean }) {
  const [phases, setPhases] = useState<PhaseMapping[]>([])
  const [tags, setTags] = useState<TanssTag[]>([])
  const [source, setSource] = useState<'configured' | 'auto' | 'empty'>('empty')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    setError('')
    Promise.all([
      getDealsPhaseMapping(),
      getDealsTagsConfig(),
    ])
      .then(([mappingRes, tagsRes]) => {
        setPhases(mappingRes.data.phases ?? [])
        setSource(mappingRes.data.source ?? 'empty')
        setTags(tagsRes.data.tags ?? [])
      })
      .catch((err) => {
        const msg = extractErrorMessage(err)
        if ((err as { response?: { status?: number } })?.response?.status === 502) {
          setError('TANSS nicht erreichbar. Bitte Verbindung prüfen und Seite neu laden.')
        } else {
          setError(`Einstellungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
        }
      })
      .finally(() => setLoading(false))
  }, [reloadKey])

  const toggleTag = (phase: string, tagId: number) => {
    setPhases((prev) =>
      prev.map((p) => {
        if (p.phase !== phase) return p
        const has = p.tagIds.includes(tagId)
        return {
          ...p,
          tagIds: has ? p.tagIds.filter((id) => id !== tagId) : [...p.tagIds, tagId],
        }
      }),
    )
    setMessage('')
  }

  const save = async () => {
    if (!isAdmin) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const mapping: Record<string, number[]> = {}
      for (const p of phases) mapping[p.phase] = p.tagIds
      await saveDealsPhaseMapping(mapping)
      setMessage('Phasen-Zuordnung gespeichert.')
      setSource('configured')
      setReloadKey((k) => k + 1)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const resetPhase = (phase: string) => {
    setPhases((prev) => prev.map((p) => (p.phase === phase ? { ...p, tagIds: [] } : p)))
    setMessage('')
  }

  // Group tags by parent name for dropdown display
  const tagsByGroup = (() => {
    const groups: Record<string, TanssTag[]> = {}
    for (const t of tags) {
      if (t.isGroupParent) continue
      const key = t.parentName ?? 'Ohne Gruppe'
      if (!groups[key]) groups[key] = []
      groups[key]!.push(t)
    }
    return groups
  })()

  const tagById = new Map(tags.map((t) => [t.id, t]))

  if (loading) {
    return <LoadingState label="TANSS-Tags werden geladen…" />
  }

  if (error) {
    return <StatusBanner type="error" text={error} />
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-500" />
            Phasen → TANSS-Tags
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Jeder Phase des Vertriebsboards werden ein oder mehrere TANSS-Tags zugeordnet.
            Tickets, die einen dieser Tags tragen, erscheinen in der jeweiligen Spalte.
          </p>
        </div>

        {source === 'auto' && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            Keine manuelle Zuordnung gespeichert. Die Zuordnung wurde automatisch aus der
            TANSS-Tag-Gruppe „Vertrieb" abgeleitet. Bitte bestätigen und speichern.
          </div>
        )}
        {source === 'empty' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700">
            Es ist weder eine Zuordnung gespeichert noch eine TANSS-Tag-Gruppe „Vertrieb"
            vorhanden. Das Vertriebsboard bleibt leer, bis mindestens einer Phase ein Tag
            zugeordnet ist.
          </div>
        )}

        {tags.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700">
            Keine TANSS-Tags gefunden. Bitte TANSS-Zugang in den Benutzereinstellungen
            prüfen oder Dev-Mode deaktivieren.
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {phases.map((phase) => (
          <div key={phase.phase} className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-baseline gap-3 flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-900">{phase.name}</span>
                <span className="text-[11px] text-gray-400">
                  Wahrscheinlichkeit {phase.probability}%
                </span>
              </div>
              {isAdmin && phase.tagIds.length > 0 && (
                <button
                  onClick={() => resetPhase(phase.phase)}
                  className="text-[11px] text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  zurücksetzen
                </button>
              )}
            </div>

            {/* Selected tags */}
            <div className="flex flex-wrap gap-1.5">
              {phase.tagIds.length === 0 && (
                <span className="text-[11px] text-gray-400 italic">
                  Keine Tags zugeordnet – Phase ist im Board leer.
                </span>
              )}
              {phase.tagIds.map((id) => {
                const tag = tagById.get(id)
                const label = tag
                  ? tag.parentName
                    ? `${tag.parentName}: ${tag.name}`
                    : tag.name
                  : `#${id}`
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 text-[11px] bg-brand-50 text-brand-700 border border-brand-100 rounded px-2 py-0.5"
                  >
                    {label}
                    {isAdmin && (
                      <button
                        onClick={() => toggleTag(phase.phase, id)}
                        className="hover:text-brand-900"
                        type="button"
                        aria-label="Tag entfernen"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                )
              })}
            </div>

            {/* Tag picker */}
            {isAdmin && (
              <details className="group">
                <summary className="cursor-pointer text-[11px] text-brand-600 hover:text-brand-800 select-none list-none inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" />
                  Tag hinzufügen
                </summary>
                <div className="mt-2 border border-gray-200 rounded-md p-2 max-h-60 overflow-y-auto space-y-3">
                  {Object.entries(tagsByGroup).sort(([a], [b]) => a.localeCompare(b)).map(([group, groupTags]) => (
                    <div key={group}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                        {group}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {groupTags.map((t) => {
                          const checked = phase.tagIds.includes(t.id)
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => toggleTag(phase.phase, t.id)}
                              className={cn(
                                'text-[11px] px-2 py-0.5 rounded border transition-colors',
                                checked
                                  ? 'bg-brand-500 text-white border-brand-500'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-brand-300 hover:text-brand-700',
                              )}
                            >
                              {t.name}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {Object.keys(tagsByGroup).length === 0 && (
                    <p className="text-[11px] text-gray-400 italic">Keine Tags verfügbar.</p>
                  )}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>

      {message && <StatusBanner type="success" text={message} />}

      {isAdmin ? (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            loading={saving}
            leadingIcon={<Save className="w-3 h-3" />}
            onClick={save}
          >
            Zuordnung speichern
          </Button>
          <span className="text-caption text-gray-400">
            Wirkt sofort – keine Neuanmeldung nötig.
          </span>
        </div>
      ) : (
        <p className="text-caption text-gray-400 italic">
          Nur Administratoren können diese Zuordnung ändern.
        </p>
      )}
    </div>
  )
}

// ─── Neukundengewinnung Module Settings ────────────────────────────────────

function NeukundengewinnungModuleSettings({ tenantId }: { tenantId?: string } = {}) {
  const navigate = useNavigate()
  const [, , seg2] = useSettingsSegments()
  const activeSection: VertriebSection = (seg2 ? SLUG_TO_VERTRIEB_SECTION[seg2] : undefined) ?? 'aufgaben'
  const setActiveSection = (v: VertriebSection) => navigate('/einstellungen/firma/vertrieb/' + VERTRIEB_SECTION_SLUGS[v])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [recalculating, setRecalculating] = useState(false)

  // Task assignment: list of employees who handle acquisition tasks.
  // Stored in tenant_settings under `acquisition.task.assignedUserIds` as a JSON
  // array of user IDs. Round-robin distribution is handled in the backend.
  const [assigneeUsers, setAssigneeUsers] = useState<ManagedUser[]>([])
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [assigneesSaving, setAssigneesSaving] = useState(false)
  const [assigneesMessage, setAssigneesMessage] = useState('')

  // Scoring config
  const [weights, setWeights] = useState({
    weightIndustryFit: 25, weightCompanySize: 15, weightRegionMatch: 15,
    weightDecisionMaker: 15, weightTriggerEvents: 20, weightContactHistory: 10,
  })
  const [excludedIndustries, setExcludedIndustries] = useState<string[]>([])
  const [newExclusion, setNewExclusion] = useState('')
  const [triggerScores, setTriggerScores] = useState<Record<string, number>>({})
  const [newTrigger, setNewTrigger] = useState({ key: '', score: 20 })

  // Geo zones
  const [zones, setZones] = useState<Array<{ id: string; name: string; type: string; plzPrefixes: string[] | null; centerPlz: string | null; radiusKm: number | null }>>([])
  const [showAddZone, setShowAddZone] = useState(false)
  const [newZone, setNewZone] = useState<{ name: string; type: 'plz_prefix' | 'radius'; plzPrefixes: string; centerPlz: string; radiusKm: number }>({ name: '', type: 'plz_prefix', plzPrefixes: '', centerPlz: '', radiusKm: 50 })
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null)
  const [editZone, setEditZone] = useState({ name: '', plzPrefixes: '', centerPlz: '', radiusKm: 50 })

  // Pain points
  const [painPoints, setPainPoints] = useState<Array<{ id: string; industry: string; title: string; description: string; isActive: boolean }>>([])
  const [generating, setGenerating] = useState(false)

  // Calendar sync (per-day schedule)
  const CAL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
  const CAL_DAY_LABELS: Record<string, string> = { Mon: 'Mo', Tue: 'Di', Wed: 'Mi', Thu: 'Do', Fri: 'Fr', Sat: 'Sa', Sun: 'So' }
  // Generate all 30-min slots from 08:00 to 18:30
  const CAL_ALL_SLOTS = Array.from({ length: 22 }, (_, i) => {
    const totalMin = 8 * 60 + i * 30
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  })
  const buildDefaultCalSchedule = () => {
    const defaultSlots = ['09:00','09:30','10:00','10:30','11:00','11:30','14:00','14:30','15:00','15:30','16:00','16:30']
    return Object.fromEntries(CAL_DAYS.map((d) => [d, ['Sat','Sun'].includes(d) ? [] : [...defaultSlots]]))
  }
  const [calSchedule, setCalSchedule] = useState<Record<string, string[]>>(buildDefaultCalSchedule())
  const [calSubjectFormat, setCalSubjectFormat] = useState('{title} – {name}, {company}')
  const [calSaving, setCalSaving] = useState(false)
  const [calMessage, setCalMessage] = useState('')

  // Brief prompts (per campaign type)
  const [promptCampaignType, setPromptCampaignType] = useState<'' | 'standard' | 'referenz' | 'stellenanzeige'>('')
  const [promptSystem, setPromptSystem] = useState('')
  const [promptUser, setPromptUser] = useState('')
  const [promptSaving, setPromptSaving] = useState(false)
  const [promptMessage, setPromptMessage] = useState('')
  const [allPrompts, setAllPrompts] = useState<Record<string, string>>({})

  // Load data
  useEffect(() => {
    getScoringConfig().then(({ data }) => {
      if (data) {
        setWeights({
          weightIndustryFit: data.weightIndustryFit ?? 25,
          weightCompanySize: data.weightCompanySize ?? 15,
          weightRegionMatch: data.weightRegionMatch ?? 15,
          weightDecisionMaker: data.weightDecisionMaker ?? 15,
          weightTriggerEvents: data.weightTriggerEvents ?? 20,
          weightContactHistory: data.weightContactHistory ?? 10,
        })
        setTriggerScores(data.triggerEventScores ?? {})
        const excluded = (data.targetIndustries ?? []).filter((t: { score: number }) => t.score === 0).map((t: { industry: string }) => t.industry)
        setExcludedIndustries(excluded)
      }
    }).catch(() => {})
    getGeoZones().then(({ data }) => setZones(data ?? [])).catch(() => {})
    getPainPoints().then(({ data }) => setPainPoints(data ?? [])).catch(() => {})
    if (tenantId) {
      getTenantSettings(tenantId).then(({ data }) => {
        const prompts: Record<string, string> = {}
        for (const key of Object.keys(data)) {
          if (key.startsWith('ai.prompt.letter.')) prompts[key] = data[key]
        }
        setAllPrompts(prompts)
        if (prompts['ai.prompt.letter.system']) setPromptSystem(prompts['ai.prompt.letter.system'])
        if (prompts['ai.prompt.letter.user']) setPromptUser(prompts['ai.prompt.letter.user'])
        // Calendar settings
        if (data['acquisition.calendar.schedule']) {
          try { setCalSchedule(JSON.parse(data['acquisition.calendar.schedule'])) } catch { /* keep default */ }
        }
        if (data['acquisition.calendar.subjectFormat']) setCalSubjectFormat(data['acquisition.calendar.subjectFormat'])
        // Acquisition task pool
        if (data['acquisition.task.assignedUserIds']) {
          try {
            const parsed = JSON.parse(data['acquisition.task.assignedUserIds'])
            if (Array.isArray(parsed)) setSelectedAssignees(parsed.filter((x: unknown): x is string => typeof x === 'string'))
          } catch { /* keep default */ }
        }
      }).catch(() => {})
    }
    // Load list of employees (internal users) among whom acquisition tasks
    // can be distributed. Endpoint is admin-only — if permission is missing
    // the list stays empty and the tab section shows a hint.
    getInternalUsers()
      .then(({ data }) => setAssigneeUsers(Array.isArray(data) ? data.filter((u) => u.isActive) : []))
      .catch(() => setAssigneeUsers([]))
  }, [])

  const totalWeight = Object.values(weights).reduce((s, v) => s + v, 0)

  const saveConfig = async () => {
    setSaving(true); setMessage('')
    try {
      await saveScoringConfig({
        ...weights,
        targetIndustries: excludedIndustries.map((ind) => ({ industry: ind, score: 0 })),
        triggerEventScores: triggerScores,
        targetRoles: [],
      })
      setMessage('Konfiguration gespeichert.')
    } catch { setMessage('Fehler beim Speichern.') }
    finally { setSaving(false) }
  }

  const recalculate = async () => {
    setRecalculating(true); setMessage('')
    try {
      const { data } = await recalculateScoring()
      setMessage(`${data.updated} Zielkunden neu bewertet.`)
    } catch { setMessage('Fehler bei Neuberechnung.') }
    finally { setRecalculating(false) }
  }

  const addGeoZone = async () => {
    try {
      const body = newZone.type === 'plz_prefix'
        ? { name: newZone.name, type: 'plz_prefix', plzPrefixes: newZone.plzPrefixes.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean) }
        : { name: newZone.name, type: 'radius', centerPlz: newZone.centerPlz, radiusKm: newZone.radiusKm }
      const { data } = await createGeoZone(body)
      setZones((prev) => [data, ...prev])
      setShowAddZone(false)
      setNewZone({ name: '', type: 'plz_prefix', plzPrefixes: '', centerPlz: '', radiusKm: 50 })
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string }; status?: number } }
      const detail = axErr?.response?.data?.error || (axErr?.response?.status === 500 ? 'Tabelle existiert noch nicht. Bitte Deploy/Migration ausfuehren.' : '')
      setMessage(`Fehler beim Erstellen.${detail ? ' ' + detail : ''}`)
    }
  }

  const deleteGeoZone = async (id: string) => {
    await deleteGeoZoneApi(id)
    setZones((prev) => prev.filter((z) => z.id !== id))
  }

  // Parse plzPrefixes robustly (may be string, JSON string, or array)
  function parsePlzPrefixes(val: unknown): string[] {
    if (Array.isArray(val)) return val.filter(Boolean)
    if (typeof val === 'string') {
      try { const parsed = JSON.parse(val); if (Array.isArray(parsed)) return parsed.filter(Boolean) } catch { /* not JSON */ }
      return val.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
    }
    return []
  }

  const startEditZone = (zone: typeof zones[0]) => {
    setEditingZoneId(zone.id)
    setEditZone({
      name: zone.name,
      plzPrefixes: parsePlzPrefixes(zone.plzPrefixes).join(', '),
      centerPlz: zone.centerPlz || '',
      radiusKm: zone.radiusKm || 50,
    })
  }

  const saveEditZone = async (id: string, type: string) => {
    try {
      const body: Record<string, unknown> = { name: editZone.name }
      if (type === 'plz_prefix') {
        body.plzPrefixes = editZone.plzPrefixes.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
      } else {
        body.centerPlz = editZone.centerPlz
        body.radiusKm = editZone.radiusKm
      }
      const { data } = await updateGeoZoneApi(id, body)
      setZones((prev) => prev.map((z) => z.id === id ? { ...z, ...data } : z))
      setEditingZoneId(null)
    } catch { setMessage('Fehler beim Speichern.') }
  }

  const generatePainPoints = async () => {
    setGenerating(true); setMessage('')
    try {
      const { data } = await generatePainPointsApi()
      setMessage(`${data.generated} Pain Points generiert.`)
      const pp = await getPainPoints()
      setPainPoints(pp.data ?? [])
    } catch { setMessage('Fehler bei der Generierung.') }
    finally { setGenerating(false) }
  }

  const togglePainPoint = async (id: string, isActive: boolean) => {
    await togglePainPointApi(id, isActive)
    setPainPoints((prev) => prev.map((p) => p.id === id ? { ...p, isActive: !isActive } : p))
  }

  const sectionTabs = [
    { key: 'aufgaben' as const, label: 'Aufgaben', icon: ClipboardList },
    { key: 'scoring' as const, label: 'ICP-Scoring', icon: Target },
    { key: 'geo' as const, label: 'Geo-Zonen', icon: MapPin },
    { key: 'painpoints' as const, label: 'Pain Points', icon: AlertTriangle },
    { key: 'prompts' as const, label: 'Brief-Prompts', icon: Code2 },
    { key: 'kalender' as const, label: 'Kalender', icon: Clock },
  ]

  return (
    <Tabs
      value={activeSection}
      onValueChange={(v) => { setActiveSection(v as typeof activeSection); setMessage('') }}
    >
      <TabsList>
        {sectionTabs.map(({ key, label, icon: Icon }) => (
          <TabsTrigger key={key} value={key}>
            <Icon className="w-3.5 h-3.5" />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>

      {message && <div className="text-small text-brand-700 bg-brand-50 border border-brand-200 rounded-lg p-3 mt-4">{message}</div>}

      {/* AUFGABEN-ZUWEISUNG */}
      <TabsContent value="aufgaben" className="space-y-4">
        <SettingsSection title="Akquise-Mitarbeiter" icon={ClipboardList}>
          <p className="text-caption text-gray-500 mb-3">
            Mitarbeiter, die Akquise-Aufgaben (Briefe, Anrufe, Termine) bearbeiten.
            Neue Aufgaben werden im Round-Robin-Verfahren auf die ausgewaehlten
            Personen verteilt — jede Aufgabe ist immer genau einer Person zugewiesen.
            Mitarbeiter, die hier nicht ausgewaehlt sind, sehen keine
            Akquise-Aufgaben in ihrem Daily-Workload.
          </p>
          {assigneeUsers.length === 0 ? (
            <EmptyState
              size="sm"
              title="Keine Mitarbeiter verfuegbar"
              description="Es wurden keine internen Benutzer gefunden. Lege zuerst Mitarbeiter unter 'Benutzer' an."
            />
          ) : (
            <div className="space-y-1.5">
              {assigneeUsers.map((u) => {
                const checked = selectedAssignees.includes(u.id)
                return (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => {
                        setSelectedAssignees((prev) =>
                          checked ? prev.filter((id) => id !== u.id) : [...prev, u.id],
                        )
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-small font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-caption text-gray-500 truncate">{u.email}</p>
                    </div>
                    <Badge variant="neutral" size="sm">{ROLE_LABELS[u.role] ?? u.role}</Badge>
                  </label>
                )
              })}
            </div>
          )}
          {assigneesMessage && (
            <div className="text-small text-brand-700 bg-brand-50 border border-brand-200 rounded-lg p-3 mt-3">
              {assigneesMessage}
            </div>
          )}
          <div className="pt-3 flex items-center gap-3 flex-wrap">
            <Button
              size="sm"
              loading={assigneesSaving}
              disabled={!tenantId}
              leadingIcon={<Save className="w-3.5 h-3.5" />}
              onClick={async () => {
                if (!tenantId) return
                setAssigneesSaving(true)
                setAssigneesMessage('')
                try {
                  await updateTenantSettings(tenantId, {
                    'acquisition.task.assignedUserIds': JSON.stringify(selectedAssignees),
                  })
                  setAssigneesMessage(
                    selectedAssignees.length === 0
                      ? 'Gespeichert. Hinweis: Ohne ausgewaehlte Mitarbeiter werden Aufgaben dem erstellenden User zugewiesen.'
                      : `Gespeichert. ${selectedAssignees.length} Mitarbeiter erhalten Akquise-Aufgaben.`,
                  )
                } catch {
                  setAssigneesMessage('Fehler beim Speichern.')
                } finally {
                  setAssigneesSaving(false)
                }
              }}
            >
              Speichern
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!tenantId || selectedAssignees.length === 0}
              leadingIcon={<RefreshCw className="w-3.5 h-3.5" />}
              onClick={async () => {
                if (!tenantId) return
                setAssigneesMessage('')
                try {
                  const { data } = await redistributeTasks()
                  setAssigneesMessage(
                    `Bestand neu verteilt: ${data.prospectsUpdated} Zielkunden, ${data.tasksUpdated} Aufgaben aktualisiert.`,
                  )
                } catch (err) {
                  setAssigneesMessage(extractErrorMessage(err) || 'Fehler beim Verteilen.')
                }
              }}
            >
              Bestehende verteilen
            </Button>
            <span className="text-caption text-gray-400">
              {selectedAssignees.length} von {assigneeUsers.length} ausgewaehlt
            </span>
          </div>
          <p className="text-caption text-gray-400 mt-3">
            Hinweis: &quot;Bestehende verteilen&quot; weist Aufgaben und Zielkunden ohne Zuweisung
            den Pool-Mitgliedern im Round-Robin zu. Bereits zugewiesene Aufgaben
            bleiben unveraendert.
          </p>
        </SettingsSection>
      </TabsContent>

      {/* ICP SCORING */}
      <TabsContent value="scoring" className="space-y-4">
          <SettingsSection title="Scoring-Gewichte" icon={Target}>
            <p className="text-caption text-gray-500 mb-3">Summe muss 100 ergeben. Aktuell: <span className={totalWeight === 100 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{totalWeight}/100</span></p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {([['weightIndustryFit', 'Branche'], ['weightCompanySize', 'Groesse'], ['weightRegionMatch', 'Region'], ['weightDecisionMaker', 'Entscheider'], ['weightTriggerEvents', 'Trigger-Events'], ['weightContactHistory', 'Kontakthistorie']] as const).map(([key, label]) => (
                <div key={key}>
                  <label className="text-caption font-medium text-gray-600">{label}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="range" min={0} max={50} value={weights[key]} onChange={(e) => setWeights((w) => ({ ...w, [key]: Number(e.target.value) }))} className="flex-1" />
                    <span className="text-small font-mono w-8 text-right">{weights[key]}%</span>
                  </div>
                </div>
              ))}
            </div>
          </SettingsSection>

          <SettingsSection title="Ausgeschlossene Branchen" icon={X}>
            <div className="flex flex-wrap gap-2 mb-3">
              {excludedIndustries.map((ind, i) => (
                <Badge key={i} variant="danger" size="md" className="gap-1">
                  {ind}
                  <button onClick={() => setExcludedIndustries((e) => e.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                </Badge>
              ))}
              {excludedIndustries.length === 0 && <span className="text-caption text-gray-400">Keine Branchen ausgeschlossen</span>}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input placeholder="Branche ausschliessen..." value={newExclusion} onChange={(e) => setNewExclusion(e.target.value)} />
              </div>
              <IconButton
                icon={<Plus />}
                aria-label="Hinzufügen"
                variant="primary"
                className="bg-red-500 hover:bg-red-600"
                onClick={() => { if (newExclusion) { setExcludedIndustries((e) => [...e, newExclusion]); setNewExclusion('') } }}
              />
            </div>
          </SettingsSection>

          <SettingsSection title="Trigger-Event-Keywords" icon={AlertTriangle}>
            <div className="space-y-1 mb-3">
              {Object.entries(triggerScores).map(([key, score]) => (
                <div key={key} className="flex items-center gap-2 text-small">
                  <code className="flex-1 bg-gray-50 px-2 py-0.5 rounded text-caption">{key}</code>
                  <span className="text-gray-500">+{score}</span>
                  <IconButton
                    icon={<X />}
                    aria-label="Entfernen"
                    variant="danger"
                    size="xs"
                    onClick={() => setTriggerScores((s) => { const n = { ...s }; delete n[key]; return n })}
                  />
                </div>
              ))}
              {Object.keys(triggerScores).length === 0 && <span className="text-caption text-gray-400">Keine Trigger-Events konfiguriert</span>}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input placeholder="z.B. nis2" value={newTrigger.key} onChange={(e) => setNewTrigger((n) => ({ ...n, key: e.target.value }))} />
              </div>
              <div className="w-20">
                <Input type="number" min={0} max={100} value={newTrigger.score} onChange={(e) => setNewTrigger((n) => ({ ...n, score: Number(e.target.value) }))} />
              </div>
              <IconButton
                icon={<Plus />}
                aria-label="Hinzufügen"
                variant="primary"
                onClick={() => { if (newTrigger.key) { setTriggerScores((s) => ({ ...s, [newTrigger.key]: newTrigger.score })); setNewTrigger({ key: '', score: 20 }) } }}
              />
            </div>
          </SettingsSection>

          <div className="flex gap-3">
            <SaveButton save={saveConfig} saving={saving} />
            <Button
              variant="secondary"
              size="lg"
              loading={recalculating}
              leadingIcon={<RefreshCw className="w-4 h-4" />}
              onClick={recalculate}
            >
              Scores neu berechnen
            </Button>
          </div>
      </TabsContent>

      {/* GEO ZONES */}
      <TabsContent value="geo">
        <SettingsSection title="Geo-Zonen (Zielgebiete fuer automatische Firmensuche)" icon={MapPin}>
          <p className="text-caption text-gray-500 mb-3">Ohne Geo-Zonen findet keine automatische Firmensuche statt.</p>
          <div className="space-y-2 mb-4">
            {zones.map((zone) => {
              const prefixes = parsePlzPrefixes(zone.plzPrefixes)
              const isEditing = editingZoneId === zone.id
              return (
                <div key={zone.id} className="bg-gray-50 rounded-lg p-3">
                  {isEditing ? (
                    <div className="space-y-3">
                      <Input value={editZone.name} onChange={(e) => setEditZone((z) => ({ ...z, name: e.target.value }))} />
                      {zone.type === 'plz_prefix' ? (
                        <Input
                          value={editZone.plzPrefixes}
                          onChange={(e) => setEditZone((z) => ({ ...z, plzPrefixes: e.target.value }))}
                          placeholder="PLZ-Bereiche (z.B. 40, 41, 42; 47)"
                        />
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={editZone.centerPlz}
                            onChange={(e) => setEditZone((z) => ({ ...z, centerPlz: e.target.value }))}
                          />
                          <div className="flex items-center gap-2">
                            <input type="range" min={10} max={200} value={editZone.radiusKm} onChange={(e) => setEditZone((z) => ({ ...z, radiusKm: Number(e.target.value) }))} className="flex-1" />
                            <span className="text-small font-mono w-14">{editZone.radiusKm}km</span>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEditZone(zone.id, zone.type)}>
                          Speichern
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingZoneId(null)}>
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="cursor-pointer flex-1" onClick={() => startEditZone(zone)}>
                        <p className="text-small font-medium text-gray-900">{zone.name}</p>
                        <p className="text-caption text-gray-500">
                          {zone.type === 'plz_prefix'
                            ? `PLZ: ${prefixes.length > 0 ? prefixes.join(', ') : '(keine)'}`
                            : `${zone.radiusKm}km um PLZ ${zone.centerPlz}`}
                        </p>
                      </div>
                      <IconButton
                        icon={<Trash2 />}
                        aria-label="Entfernen"
                        variant="danger"
                        size="sm"
                        onClick={() => deleteGeoZone(zone.id)}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {showAddZone ? (
            <div className="border border-brand-200 bg-brand-50 rounded-lg p-4 space-y-3">
              <Input
                placeholder="Name (z.B. Einzugsgebiet NRW)"
                value={newZone.name}
                onChange={(e) => setNewZone((z) => ({ ...z, name: e.target.value }))}
              />
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-small cursor-pointer">
                  <input type="radio" checked={newZone.type === 'plz_prefix'} onChange={() => setNewZone((z) => ({ ...z, type: 'plz_prefix' }))} /> PLZ-Bereich
                </label>
                <label className="flex items-center gap-2 text-small cursor-pointer">
                  <input type="radio" checked={newZone.type === 'radius'} onChange={() => setNewZone((z) => ({ ...z, type: 'radius' }))} /> Umkreis
                </label>
              </div>
              {newZone.type === 'plz_prefix' ? (
                <Input
                  placeholder="PLZ-Bereiche (z.B. 40, 41, 42; 47; 50; 52)"
                  value={newZone.plzPrefixes}
                  onChange={(e) => setNewZone((z) => ({ ...z, plzPrefixes: e.target.value }))}
                />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Center-PLZ (z.B. 41236)"
                    value={newZone.centerPlz}
                    onChange={(e) => setNewZone((z) => ({ ...z, centerPlz: e.target.value }))}
                  />
                  <div className="flex items-center gap-2">
                    <input type="range" min={10} max={200} value={newZone.radiusKm} onChange={(e) => setNewZone((z) => ({ ...z, radiusKm: Number(e.target.value) }))} className="flex-1" />
                    <span className="text-small font-mono w-14">{newZone.radiusKm}km</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={addGeoZone}>Erstellen</Button>
                <Button variant="secondary" onClick={() => setShowAddZone(false)}>
                  Abbrechen
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              fullWidth
              className="border border-dashed border-gray-300 text-gray-600"
              leadingIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddZone(true)}
            >
              Neue Geo-Zone
            </Button>
          )}
        </SettingsSection>
      </TabsContent>

      {/* PAIN POINTS */}
      <TabsContent value="painpoints">
        <SettingsSection title="Branchenspezifische Pain Points (KI-generiert)" icon={AlertTriangle}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-caption text-gray-500">Werden in Briefen als Schmerzpunkte eingesetzt. KI generiert automatisch.</p>
            <Button
              size="sm"
              loading={generating}
              leadingIcon={<RefreshCw className="w-3.5 h-3.5" />}
              onClick={generatePainPoints}
            >
              KI-generieren
            </Button>
          </div>
          {painPoints.length === 0 && (
            <EmptyState title="Keine Pain Points" description='Klicke "KI-generieren".' size="sm" />
          )}
          <div className="space-y-2">
            {painPoints.map((pp) => (
              <div key={pp.id} className={cn('flex items-start gap-3 rounded-lg p-3', pp.isActive ? 'bg-gray-50' : 'bg-gray-50/50 opacity-50')}>
                <Checkbox
                  checked={pp.isActive}
                  onChange={() => togglePainPoint(pp.id, pp.isActive)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="info" size="sm">
                      {pp.industry === '*' ? 'Alle' : pp.industry}
                    </Badge>
                    <p className="text-small font-medium text-gray-900">{pp.title}</p>
                  </div>
                  <p className="text-caption text-gray-500 mt-1 line-clamp-2">{pp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      </TabsContent>

      <TabsContent value="prompts">
        <SettingsSection title="KI-Prompts für Brieferstellung" icon={Code2}>
          <p className="text-caption text-gray-500 mb-3">
            Prompts steuern, wie die KI Akquise-Briefe formuliert. Wenn leer, wird der Standard-Prompt verwendet.
            Pro Kampagnen-Typ kann ein eigener Prompt hinterlegt werden (Fallback: Global).
          </p>
          <div className="flex gap-1 mb-3">
            {([
              { value: '' as const, label: 'Global (Standard)' },
              { value: 'referenz' as const, label: 'Referenz' },
              { value: 'stellenanzeige' as const, label: 'Stellenanzeige' },
            ]).map((tab) => (
              <Button
                key={tab.value}
                variant={promptCampaignType === tab.value ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => {
                  // Save current values before switching
                  const sysKey = promptCampaignType ? `ai.prompt.letter.${promptCampaignType}.system` : 'ai.prompt.letter.system'
                  const usrKey = promptCampaignType ? `ai.prompt.letter.${promptCampaignType}.user` : 'ai.prompt.letter.user'
                  setAllPrompts((prev) => ({ ...prev, [sysKey]: promptSystem, [usrKey]: promptUser }))
                  // Load new tab values
                  const newSysKey = tab.value ? `ai.prompt.letter.${tab.value}.system` : 'ai.prompt.letter.system'
                  const newUsrKey = tab.value ? `ai.prompt.letter.${tab.value}.user` : 'ai.prompt.letter.user'
                  setPromptSystem(allPrompts[newSysKey] || '')
                  setPromptUser(allPrompts[newUsrKey] || '')
                  setPromptCampaignType(tab.value)
                }}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <p className="text-caption text-gray-400 mb-3">
            Platzhalter: <code className="bg-gray-100 px-1 rounded">{'{{DATUM}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{EMPFAENGER}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{ABSENDER}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{THEMA}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">{'{{REFERENZEN}}'}</code>
          </p>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-caption font-medium text-gray-600">System-Prompt</label>
                {promptSystem && (
                  <Button
                    variant="link"
                    size="xs"
                    leadingIcon={<RefreshCw className="w-3 h-3" />}
                    onClick={() => setPromptSystem('')}
                  >
                    Auf Standard zurücksetzen
                  </Button>
                )}
              </div>
              <Textarea
                value={promptSystem}
                onChange={(e) => setPromptSystem(e.target.value)}
                placeholder="Leer = Standard-Prompt wird verwendet"
                rows={12}
                className="text-caption font-mono leading-relaxed"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-caption font-medium text-gray-600">User-Prompt (Template)</label>
                {promptUser && (
                  <Button
                    variant="link"
                    size="xs"
                    leadingIcon={<RefreshCw className="w-3 h-3" />}
                    onClick={() => setPromptUser('')}
                  >
                    Auf Standard zurücksetzen
                  </Button>
                )}
              </div>
              <Textarea
                value={promptUser}
                onChange={(e) => setPromptUser(e.target.value)}
                placeholder="Leer = Standard-Prompt wird verwendet"
                rows={8}
                className="text-caption font-mono leading-relaxed"
              />
            </div>
          </div>
          {promptMessage && <div className="text-small text-brand-700 bg-brand-50 border border-brand-200 rounded-lg p-3 mt-3">{promptMessage}</div>}
          <div className="pt-3">
            <Button
              size="sm"
              loading={promptSaving}
              disabled={!tenantId}
              leadingIcon={<Save className="w-3.5 h-3.5" />}
              onClick={async () => {
                if (!tenantId) return
                setPromptSaving(true); setPromptMessage('')
                try {
                  const sysKey = promptCampaignType ? `ai.prompt.letter.${promptCampaignType}.system` : 'ai.prompt.letter.system'
                  const usrKey = promptCampaignType ? `ai.prompt.letter.${promptCampaignType}.user` : 'ai.prompt.letter.user'
                  await updateTenantSettings(tenantId, {
                    [sysKey]: promptSystem,
                    [usrKey]: promptUser,
                  })
                  setPromptMessage('Prompts gespeichert.')
                } catch { setPromptMessage('Fehler beim Speichern.') }
                finally { setPromptSaving(false) }
              }}
            >
              Prompts speichern
            </Button>
          </div>
        </SettingsSection>
      </TabsContent>

      {/* KALENDER */}
      <TabsContent value="kalender">
        <SettingsSection title="Kalender-Sync (O365)" icon={Clock}>
          <p className="text-xs text-gray-500 mb-4">
            Aktiviere die Slots, in denen Akquise-Anrufe eingetragen werden dürfen. Alle Zeiten sind Berlin-Zeit.
          </p>

          {/* Tag × Slot-Grid */}
          <div className="mb-6 overflow-x-auto">
            <table className="text-[11px] border-collapse">
              <thead>
                <tr>
                  <th className="w-14 text-left text-gray-500 font-normal pb-1">Uhrzeit</th>
                  {CAL_DAYS.map((d) => (
                    <th key={d} className="w-10 text-center text-gray-600 font-medium pb-1">{CAL_DAY_LABELS[d]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CAL_ALL_SLOTS.map((slot) => (
                  <tr key={slot} className="hover:bg-gray-50">
                    <td className="text-gray-400 pr-2 py-0.5">{slot}</td>
                    {CAL_DAYS.map((day) => {
                      const enabled = (calSchedule[day] ?? []).includes(slot)
                      return (
                        <td key={day} className="text-center py-0.5">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setCalSchedule((s) => ({
                              ...s,
                              [day]: e.target.checked
                                ? [...(s[day] ?? []), slot].sort()
                                : (s[day] ?? []).filter((t) => t !== slot),
                            }))}
                            className="accent-brand-500 w-3.5 h-3.5"
                          />
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Betreff-Format */}
          <div className="mb-5">
            <Input
              label="Termin-Betreff"
              type="text"
              value={calSubjectFormat}
              onChange={(e) => setCalSubjectFormat(e.target.value)}
              placeholder="{title} – {name}, {company}"
              hint={
                <>
                  Platzhalter: <code className="bg-gray-100 px-1 rounded">{'{title}'}</code> Aufgabe,{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> Kontakt,{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{company}'}</code> Firma
                </>
              }
            />
          </div>

          {calMessage && <div className="text-small text-brand-700 bg-brand-50 border border-brand-200 rounded-lg p-3 mb-3">{calMessage}</div>}
          <Button
            size="sm"
            loading={calSaving}
            disabled={!tenantId}
            leadingIcon={<Save className="w-3.5 h-3.5" />}
            onClick={async () => {
              if (!tenantId) return
              setCalSaving(true); setCalMessage('')
              try {
                await updateTenantSettings(tenantId, {
                  'acquisition.calendar.schedule': JSON.stringify(calSchedule),
                  'acquisition.calendar.subjectFormat': calSubjectFormat,
                })
                setCalMessage('Kalender-Einstellungen gespeichert.')
              } catch { setCalMessage('Fehler beim Speichern.') }
              finally { setCalSaving(false) }
            }}
          >
            Speichern
          </Button>
        </SettingsSection>
      </TabsContent>
    </Tabs>
  )
}

// ─── TANSS Company Search ────────────────────────────────────────────────────

function TanssCompanySearch({
  selectedId,
  selectedName,
  onSelect,
}: {
  selectedId: string
  selectedName: string
  onSelect: (id: string, name: string) => void
}) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [searchError, setSearchError] = useState('')
  const searchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (search.length < 2) { setResults([]); setSearchError(''); return }
    setLoading(true)
    const timer = setTimeout(() => {
      searchTanssCompanies(search)
        .then(({ data }) => {
          const list = Array.isArray(data?.data) ? data.data : []
          setResults(list.map((c) => ({
            id: c.id as number,
            name: String(c.name ?? c.companyName ?? `ID ${c.id}`),
          })))
          setShowResults(list.length > 0)
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  return (
    <div>
      {selectedId && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <div className="flex-1">
            <p className="text-small font-medium text-brand-700">{selectedName || `ID: ${selectedId}`}</p>
            <p className="text-caption text-brand-500">TANSS Firma-ID: {selectedId}</p>
          </div>
          <IconButton
            icon={<X />}
            aria-label="Auswahl entfernen"
            size="sm"
            variant="danger"
            onClick={() => onSelect('', '')}
          />
        </div>
      )}
      <div className="relative" ref={searchRef}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder="TANSS-Firma suchen (min. 2 Zeichen)..."
          trailingIcon={loading ? <Loader2 className="animate-spin" /> : undefined}
        />
        {showResults && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-popover max-h-48 overflow-y-auto">
            {results.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  onSelect(String(c.id), c.name)
                  setSearch('')
                  setShowResults(false)
                }}
                className="w-full text-left px-4 py-2.5 text-small hover:bg-gray-50 flex items-center justify-between"
              >
                <span>{c.name}</span>
                <span className="text-caption text-gray-400">ID: {c.id}</span>
              </button>
            ))}
          </div>
        )}
        {searchError && (
          <p className="text-caption text-red-500 mt-1">{searchError}</p>
        )}
        {!loading && search.length >= 2 && results.length === 0 && !searchError && showResults && (
          <p className="text-caption text-gray-400 mt-1">Keine Ergebnisse für „{search}"</p>
        )}
      </div>
    </div>
  )
}

// ─── Error Boundary ─────────────────────────────────────────────────────────

class SettingsErrorBoundary extends Component<
  { children: ReactNode; fallbackMessage?: string },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          title={this.props.fallbackMessage ?? 'Fehler beim Laden dieser Sektion.'}
          details={this.state.error?.message ?? null}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      )
    }
    return this.props.children
  }
}

// ─── Outlook ↔ TANSS Calendar Sync Panel ────────────────────────────────────

interface CalendarSyncStatus {
  mappedCount: number
  lastSyncedAt: string | null
  originCounts: { outlook: number; tanss: number }
  job: {
    enabled: boolean
    intervalMinutes: number
    lastRunAt: string | null
    lastRunStatus: string | null
    lastRunMessage: string | null
    lastRunError: string | null
    nextDueAt: string | null
    overdue: boolean
  } | null
}

interface CalendarSyncRunResult {
  ok: boolean
  created: number
  updated: number
  deleted: number
  errors: number
  skipped: number
  errorMessages: string[]
  diagnostics?: {
    m365EventCount: number
    tanssSupportCount: number
    tanssSupportTypesSummary: Record<string, number>
    mapRowCount: number
    tanssEmployeeId: number | null
    window: { from: string; to: string }
    notes: string[]
  }
}

function CalendarSyncPanel() {
  const [status, setStatus] = useState<CalendarSyncStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<CalendarSyncRunResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)
  const [showErrorDetails, setShowErrorDetails] = useState(false)

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true)
    setError(null)
    try {
      const { data } = await getCalendarSyncStatus()
      setStatus(data)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Status konnte nicht geladen werden.${msg ? ' ' + msg : ''}`)
      setErrorDetails(msg || null)
    } finally {
      setLoadingStatus(false)
    }
  }, [])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const runSync = async () => {
    setRunning(true)
    setError(null)
    setErrorDetails(null)
    setResult(null)
    try {
      const { data } = await runCalendarSyncNow()
      setResult(data)
      if (data.errorMessages?.length) {
        setErrorDetails(data.errorMessages.join('\n'))
      }
      await loadStatus()
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Sync fehlgeschlagen.${msg ? ' ' + msg : ''}`)
      setErrorDetails(msg || null)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-small text-gray-600">
          Ubertragen werden Termine Mo–Fr 08–17 Uhr zwischen Outlook und TANSS,
          sowie alle TANSS-Termine und Abwesenheiten der naechsten 60 Tage.
          Getaetigte Leistungen (planningType = SUPPORT) werden NICHT nach Outlook
          gespiegelt.
        </p>
        <RouterLink
          to="/einstellungen/kalender-diagnose"
          className="shrink-0 text-small text-brand-700 hover:underline whitespace-nowrap"
        >
          Diagnose oeffnen →
        </RouterLink>
      </div>

      {loadingStatus ? (
        <LoadingState />
      ) : status ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-small">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-caption text-gray-500">Gemappte Eintraege</div>
              <div className="text-h3 font-semibold text-gray-900">{status.mappedCount}</div>
              <div className="text-caption text-gray-500 mt-1">
                {status.originCounts.outlook} Outlook, {status.originCounts.tanss} TANSS
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-caption text-gray-500">Letzter Sync (Mapping)</div>
              <div className="text-body font-medium text-gray-900">
                {status.lastSyncedAt
                  ? new Date(status.lastSyncedAt).toLocaleString('de-DE')
                  : 'Noch nicht durchgefuehrt'}
              </div>
              <div className="text-caption text-gray-500 mt-1">
                letzte Aenderung an einem Termin
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-caption text-gray-500">Letzter Cron-Tick</div>
              <div className="text-body font-medium text-gray-900">
                {status.job?.lastRunAt
                  ? new Date(status.job.lastRunAt).toLocaleString('de-DE')
                  : 'Noch nie gelaufen'}
              </div>
              <div className="text-caption text-gray-500 mt-1">
                {status.job?.lastRunStatus === 'error'
                  ? 'mit Fehler'
                  : status.job?.lastRunStatus === 'success'
                    ? 'erfolgreich'
                    : 'Status unbekannt'}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-caption text-gray-500">Intervall</div>
              <div className="text-body font-medium text-gray-900">
                {status.job
                  ? status.job.enabled
                    ? `alle ${status.job.intervalMinutes} Minuten`
                    : 'deaktiviert'
                  : 'unbekannt'}
              </div>
              <div className="text-caption text-gray-500 mt-1">plus manueller Trigger</div>
            </div>
          </div>

          {status.job?.overdue && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-small text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">Cron laeuft offenbar nicht.</div>
                <div className="mt-1">
                  Der letzte Cron-Tick liegt mehr als das Doppelte des Intervalls
                  ({status.job.intervalMinutes} Min) zurueck. Pruefe in Plesk unter
                  „Scheduled Tasks", ob der Eintrag fuer{' '}
                  <code className="text-caption">dist/scripts/cron-tick.js</code>{' '}
                  aktiv ist und ohne Fehler laeuft. Manueller Sync via „Jetzt syncen"
                  funktioniert unabhaengig davon.
                </div>
              </div>
            </div>
          )}

          {status.job?.lastRunError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-800">
              <div className="font-semibold">Letzter Cron-Tick mit Fehler:</div>
              <div className="mt-1 break-words">{status.job.lastRunError}</div>
            </div>
          )}
        </>
      ) : null}

      {result && (
        <div
          className={cn(
            'rounded-md border px-3 py-2 text-small',
            !result.ok
              ? 'border-red-200 bg-red-50 text-red-800'
              : result.skipped > 0 || (result.errorMessages?.length ?? 0) > 0
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800',
          )}
        >
          <div className="font-semibold">
            {!result.ok
              ? 'Sync mit Fehlern abgeschlossen.'
              : result.skipped > 0 || (result.errorMessages?.length ?? 0) > 0
                ? 'Sync abgeschlossen – bitte Hinweise pruefen.'
                : 'Sync erfolgreich.'}
          </div>
          <div className="mt-1">
            {result.created} angelegt, {result.updated} aktualisiert, {result.deleted} geloescht
            {result.errors > 0 ? `, ${result.errors} Fehler` : ''}
            {result.skipped > 0 ? `, ${result.skipped} uebersprungen` : ''}
          </div>
          {result.errorMessages && result.errorMessages.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-caption">
              {result.errorMessages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
          {result.diagnostics && (
            <div className="mt-3 text-caption">
              <button
                type="button"
                className="underline"
                onClick={() => setShowErrorDetails((v) => !v)}
              >
                {showErrorDetails ? 'Diagnose ausblenden' : 'Diagnose anzeigen'}
              </button>
              {showErrorDetails && (
                <div className="mt-2 space-y-1 rounded border border-gray-200 bg-white px-3 py-2 text-gray-700">
                  <div>TANSS-Employee-ID: {result.diagnostics.tanssEmployeeId ?? '(unbekannt)'}</div>
                  <div>Outlook-Events im Fenster: {result.diagnostics.m365EventCount}</div>
                  <div>TANSS-Supports im Fenster: {result.diagnostics.tanssSupportCount}</div>
                  {Object.keys(result.diagnostics.tanssSupportTypesSummary).length > 0 && (
                    <div>
                      Planning-Types:{' '}
                      {Object.entries(result.diagnostics.tanssSupportTypesSummary)
                        .map(([t, n]) => `${t}=${n}`)
                        .join(', ')}
                    </div>
                  )}
                  <div>Map-Eintraege: {result.diagnostics.mapRowCount}</div>
                  <div>
                    Fenster: {new Date(result.diagnostics.window.from).toLocaleString('de-DE')} –{' '}
                    {new Date(result.diagnostics.window.to).toLocaleString('de-DE')}
                  </div>
                  {result.diagnostics.notes.length > 0 && (
                    <div className="pt-1">
                      <div className="font-semibold">Hinweise:</div>
                      <ul className="list-disc pl-5">
                        {result.diagnostics.notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <ErrorState
          title={error}
          details={errorDetails}
          onRetry={loadStatus}
        />
      )}

      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={runSync}
          loading={running}
          leadingIcon={<RefreshCw className="w-4 h-4" />}
        >
          Jetzt syncen
        </Button>
        <Button variant="ghost" onClick={loadStatus} disabled={running || loadingStatus}>
          Status aktualisieren
        </Button>
      </div>

      <div className="pt-4 mt-2 border-t border-gray-200">
        <RestoreDeletedAppointmentsSection />
      </div>

      <div className="pt-4 mt-2 border-t border-gray-200">
        <RestoreOutlookTitlesSection />
      </div>
    </div>
  )
}

// ─── Restore Deleted Appointments ───────────────────────────────────────────

interface RestoreDeletedItemDto {
  tanssSupportId: string
  planningType: string
  start: string
  end: string
  durationMin: number
  title: string
  ticketId: number | null
  action: 'will-restore' | 'restored' | 'error'
  newM365EventId?: string
  errorMessage?: string
}

interface RestoreDeletedResultDto {
  dryRun: boolean
  processed: number
  restored: number
  errors: number
  alreadyMapped: number
  range: { from: string; to: string }
  notes: string[]
  items: RestoreDeletedItemDto[]
}

function toLocalDateInput(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function RestoreDeletedAppointmentsSection() {
  // Default: letzte 3 Tage (deckt typischen "gestern + Montag"-Fall ab).
  const today = new Date()
  const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60_000)

  const [fromDate, setFromDate] = useState(toLocalDateInput(threeDaysAgo))
  const [toDate, setToDate] = useState(toLocalDateInput(today))
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<RestoreDeletedResultDto | null>(null)
  const [applied, setApplied] = useState<RestoreDeletedResultDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const reset = () => {
    setPreview(null)
    setApplied(null)
    setError(null)
    setSelected(new Set())
  }

  const buildRange = () => {
    // From: Tagesbeginn local, To: Tagesende local
    const from = new Date(`${fromDate}T00:00:00`)
    const to = new Date(`${toDate}T23:59:59`)
    return { from: from.toISOString(), to: to.toISOString() }
  }

  const runPreview = async () => {
    setLoading(true)
    setError(null)
    setApplied(null)
    try {
      const { data } = await restoreDeletedAppointments({ ...buildRange(), dryRun: true })
      setPreview(data)
      // Default: alle wiederherstellbaren Eintraege ausgewaehlt.
      const initial = new Set<string>(
        (data.items as Array<{ action: string; tanssSupportId: string }>).filter((i) => i.action === 'will-restore').map((i) => i.tanssSupportId),
      )
      setSelected(initial)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Vorschau fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setLoading(false)
    }
  }

  const runApply = async () => {
    setConfirmOpen(false)
    setLoading(true)
    setError(null)
    try {
      const { data } = await restoreDeletedAppointments({
        ...buildRange(),
        dryRun: false,
        tanssSupportIds: [...selected],
      })
      setApplied(data)
      setPreview(null)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Wiederherstellung fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setLoading(false)
    }
  }

  const willRestoreItems = preview?.items.filter((i) => i.action === 'will-restore') ?? []
  const result = applied ?? preview
  const isPreview = !!preview && !applied
  const selectableIds = willRestoreItems.map((i) => i.tanssSupportId)
  const allSelected = isPreview && selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))
  const noneSelected = isPreview && selectableIds.every((id) => !selected.has(id))
  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(selectableIds))
  }
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const formatRange = (it: RestoreDeletedItemDto) => {
    const s = new Date(it.start)
    const e = new Date(it.end)
    const date = s.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
    const fmtTime = (d: Date) =>
      d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    return `${date} ${fmtTime(s)}–${fmtTime(e)}`
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-small font-semibold text-gray-900">
          Geloeschte Outlook-Termine wiederherstellen
        </h3>
        <p className="text-caption text-gray-600 mt-1">
          Recovery fuer den Fall, dass Outlook-Termine bei der Umwandlung
          Termin → Leistung versehentlich geloescht wurden. Legt fuer jeden
          TANSS-Eintrag im gewaehlten Zeitraum, der KEIN aktives Outlook-
          Mapping mehr hat, einen neuen Outlook-Termin an. Originaler
          Beschreibungstext und Anhaenge sind nicht in unserer DB —
          rekonstruiert wird ueber die Outlook-Title-/Body-Templates aus
          den TANSS-Daten.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-caption text-gray-700">
          <span>Von</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-small"
          />
        </label>
        <label className="flex flex-col text-caption text-gray-700">
          <span>Bis</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-small"
          />
        </label>
        <Button variant="secondary" onClick={runPreview} loading={loading} disabled={loading}>
          Vorschau anzeigen
        </Button>
        {willRestoreItems.length > 0 && !applied && (
          <Button
            variant="danger"
            onClick={() => setConfirmOpen(true)}
            disabled={loading || selected.size === 0}
          >
            {selected.size} Termin(e) wiederherstellen
          </Button>
        )}
        {(preview || applied) && (
          <Button variant="ghost" onClick={reset} disabled={loading}>
            Zuruecksetzen
          </Button>
        )}
      </div>

      {error && <ErrorState title={error} />}

      {result && (
        <div
          className={cn(
            'rounded-md border px-3 py-2 text-small',
            applied
              ? result.errors > 0
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-blue-200 bg-blue-50 text-blue-800',
          )}
        >
          <div className="font-semibold">
            {applied
              ? result.errors > 0
                ? `${result.restored} wiederhergestellt, ${result.errors} Fehler`
                : `${result.restored} Outlook-Termin(e) wiederhergestellt`
              : `Vorschau: ${willRestoreItems.length} Eintrag/Eintraege koennen wiederhergestellt werden`}
          </div>
          <div className="mt-1 text-caption">
            {result.processed} TANSS-Eintraege ohne Outlook-Mapping geprueft, {result.alreadyMapped}{' '}
            bereits in Outlook (uebersprungen)
            {result.errors > 0 ? `, ${result.errors} Fehler` : ''}
          </div>
          {result.notes.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-caption">
              {result.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && result.items.length > 0 && (
        <div className="max-h-96 overflow-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-caption">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1 font-semibold w-8">
                  {isPreview && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && !noneSelected
                      }}
                      onChange={toggleAll}
                      aria-label="Alle auswaehlen"
                    />
                  )}
                </th>
                <th className="text-left px-2 py-1 font-semibold">Aktion</th>
                <th className="text-left px-2 py-1 font-semibold">Zeit</th>
                <th className="text-left px-2 py-1 font-semibold">Typ</th>
                <th className="text-left px-2 py-1 font-semibold">Titel</th>
                <th className="text-left px-2 py-1 font-semibold">Ticket</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((it, i) => {
                const selectable = isPreview && it.action === 'will-restore'
                return (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1 whitespace-nowrap">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={selected.has(it.tanssSupportId)}
                          onChange={() => toggleOne(it.tanssSupportId)}
                          aria-label="Auswaehlen"
                        />
                      )}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {it.action === 'will-restore' && (
                        <span className="text-blue-700">Vorschau</span>
                      )}
                      {it.action === 'restored' && (
                        <span className="text-emerald-700">Wiederhergestellt</span>
                      )}
                      {it.action === 'error' && (
                        <span className="text-red-700" title={it.errorMessage}>
                          Fehler
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{formatRange(it)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{it.planningType}</td>
                    <td className="px-2 py-1 break-words">{it.title || '—'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {it.ticketId ? `#${it.ticketId}` : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={runApply}
        title="Termine wirklich wiederherstellen?"
        message={`Es werden ${selected.size} Outlook-Termin(e) im Zeitraum ${fromDate} bis ${toDate} aus den aktuellen TANSS-Daten neu angelegt. Hat der TANSS-Eintrag inzwischen eine andere Beschreibung oder Zeit, wird der wiederhergestellte Outlook-Termin diese uebernehmen.`}
        confirmLabel="Wiederherstellen"
        variant="danger"
      />
    </div>
  )
}

// ─── Restore Outlook Titles ─────────────────────────────────────────────────

interface RestoreItem {
  m365EventId: string
  tanssSupportId: string
  currentSubject: string
  proposedSubject: string
  start?: string
  end?: string
  planningType?: string
  ticketId?: number | null
  action:
    | 'will-restore'
    | 'restored'
    | 'skip-no-text'
    | 'skip-same'
    | 'skip-event-missing'
    | 'error'
  errorMessage?: string
}

interface RestoreResult {
  dryRun: boolean
  processed: number
  restored: number
  skipped: number
  errors: number
  items: RestoreItem[]
}

function formatItemRange(start?: string, end?: string): string {
  if (!start || !end) return '—'
  const s = new Date(start)
  const e = new Date(end)
  const date = s.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  return `${date} ${fmtTime(s)}–${fmtTime(e)}`
}

function RestoreOutlookTitlesSection() {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<RestoreResult | null>(null)
  const [applied, setApplied] = useState<RestoreResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const reset = () => {
    setPreview(null)
    setApplied(null)
    setError(null)
    setSelected(new Set())
  }

  const runPreview = async () => {
    setLoading(true)
    setError(null)
    setApplied(null)
    try {
      const { data } = await restoreOutlookTitles({ dryRun: true })
      setPreview(data)
      const initial = new Set<string>(
        (data.items as Array<{ action: string; m365EventId: string }>).filter((i) => i.action === 'will-restore').map((i) => i.m365EventId),
      )
      setSelected(initial)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Vorschau fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setLoading(false)
    }
  }

  const runApply = async () => {
    setConfirmOpen(false)
    setLoading(true)
    setError(null)
    try {
      const { data } = await restoreOutlookTitles({ dryRun: false, m365EventIds: [...selected] })
      setApplied(data)
      setPreview(null)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Wiederherstellung fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setLoading(false)
    }
  }

  const willRestoreItems = preview?.items.filter((i) => i.action === 'will-restore') ?? []
  const result = applied ?? preview
  const isPreview = !!preview && !applied
  const selectableIds = willRestoreItems.map((i) => i.m365EventId)
  const allSelected = isPreview && selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))
  const noneSelected = isPreview && selectableIds.every((id) => !selected.has(id))
  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(selectableIds))
  }
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-small font-semibold text-gray-900">
          Outlook-Titel aus TANSS wiederherstellen
        </h3>
        <p className="text-caption text-gray-600 mt-1">
          Notfall-Werkzeug: stellt urspruengliche Outlook-Termin-Betreffe aus den
          TANSS-Support-Texten wieder her — z.B. wenn ein Template-Wechsel die Titel
          versehentlich ueberschrieben hat. Funktioniert nur fuer Outlook-stammige
          Eintraege im Sync-Fenster (-7/+60 Tage).
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={runPreview} loading={loading} disabled={loading}>
          Vorschau anzeigen
        </Button>
        {willRestoreItems.length > 0 && !applied && (
          <Button
            variant="danger"
            onClick={() => setConfirmOpen(true)}
            disabled={loading || selected.size === 0}
          >
            {selected.size} Titel wiederherstellen
          </Button>
        )}
        {(preview || applied) && (
          <Button variant="ghost" onClick={reset} disabled={loading}>
            Zuruecksetzen
          </Button>
        )}
      </div>

      {error && <ErrorState title={error} />}

      {result && (
        <div
          className={cn(
            'rounded-md border px-3 py-2 text-small',
            applied
              ? result.errors > 0
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-blue-200 bg-blue-50 text-blue-800',
          )}
        >
          <div className="font-semibold">
            {applied
              ? result.errors > 0
                ? `${result.restored} wiederhergestellt, ${result.errors} Fehler`
                : `${result.restored} Outlook-Titel wiederhergestellt`
              : `Vorschau: ${willRestoreItems.length} von ${result.processed} Eintraegen koennen wiederhergestellt werden`}
          </div>
          <div className="mt-1 text-caption">
            {result.processed} geprueft, {result.skipped} uebersprungen
            {result.errors > 0 ? `, ${result.errors} Fehler` : ''}
          </div>
        </div>
      )}

      {result && result.items.length > 0 && (
        <div className="max-h-96 overflow-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-caption">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1 font-semibold w-8">
                  {isPreview && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && !noneSelected
                      }}
                      onChange={toggleAll}
                      aria-label="Alle auswaehlen"
                    />
                  )}
                </th>
                <th className="text-left px-2 py-1 font-semibold">Aktion</th>
                <th className="text-left px-2 py-1 font-semibold">Zeit</th>
                <th className="text-left px-2 py-1 font-semibold">Typ</th>
                <th className="text-left px-2 py-1 font-semibold">Aktueller Titel</th>
                <th className="text-left px-2 py-1 font-semibold">Wiederhergestellt aus TANSS</th>
                <th className="text-left px-2 py-1 font-semibold">Ticket</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((it, i) => {
                const selectable = isPreview && it.action === 'will-restore'
                return (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1 whitespace-nowrap">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={selected.has(it.m365EventId)}
                          onChange={() => toggleOne(it.m365EventId)}
                          aria-label="Auswaehlen"
                        />
                      )}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {it.action === 'will-restore' && (
                        <span className="text-blue-700">Vorschau</span>
                      )}
                      {it.action === 'restored' && (
                        <span className="text-emerald-700">Wiederhergestellt</span>
                      )}
                      {it.action === 'skip-no-text' && (
                        <span className="text-gray-500">Kein TANSS-Text</span>
                      )}
                      {it.action === 'skip-same' && (
                        <span className="text-gray-500">Identisch</span>
                      )}
                      {it.action === 'skip-event-missing' && (
                        <span className="text-gray-500">Outlook-Event fehlt</span>
                      )}
                      {it.action === 'error' && (
                        <span className="text-red-700" title={it.errorMessage}>
                          Fehler
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {formatItemRange(it.start, it.end)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{it.planningType ?? '—'}</td>
                    <td className="px-2 py-1 break-words">{it.currentSubject || '—'}</td>
                    <td className="px-2 py-1 break-words">{it.proposedSubject || '—'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {it.ticketId ? `#${it.ticketId}` : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={runApply}
        title="Outlook-Titel wirklich wiederherstellen?"
        message={`${selected.size} Outlook-Termin(e) erhalten den ehemaligen Betreff aus TANSS. Diese Aktion kann nicht rueckgaengig gemacht werden — pruefe vorher die Vorschau.`}
        confirmLabel="Wiederherstellen"
        variant="danger"
      />
    </div>
  )
}

// ─── Tenant Finanzen Section ─────────────────────────────────────────────────

const TANSS_SAP_AI_MODE_OPTIONS: { value: TanssSapAiMode; label: string }[] = [
  { value: 'always_ai',            label: 'Immer KI-Texte verwenden' },
  { value: 'never_ai',             label: 'Nie KI verwenden (Rohtext)' },
  { value: 'customer_default_no',  label: 'Standard Nein — Kunde kann KI aktivieren' },
  { value: 'customer_default_yes', label: 'Standard Ja — Kunde kann KI deaktivieren' },
]

function TenantFinanzenSection() {
  const { data, isLoading, error } = useTanssSapSettings()
  const update = useUpdateTanssSapSettings()
  const { data: allTemplates = [] } = useInvoiceTemplates()
  const [form, setForm] = useState<Partial<TanssSapSettings>>({})
  const [message, setMessage] = useState('')
  const [saveError, setSaveError] = useState('')

  const settings = data?.data

  if (isLoading) return <LoadingState />
  if (error || !settings) return <ErrorState title="Einstellungen konnten nicht geladen werden" />

  const merged: TanssSapSettings = { ...settings, ...form }
  const hasChanges = Object.keys(form).length > 0

  const handleSave = async () => {
    setSaveError('')
    setMessage('')
    try {
      await update.mutateAsync(form)
      setForm({})
      setMessage('Einstellungen gespeichert.')
    } catch (err) {
      setSaveError(`Fehler beim Speichern. ${extractErrorMessage(err)}`)
    }
  }

  return (
    <div className="space-y-8">
      {saveError && <StatusBanner type="error" text={saveError} />}
      {message && <StatusBanner type="success" text={message} />}

      <SettingsSection title="TANSS → SAP Rechnungssync" icon={Receipt}>
        <div className="space-y-6">
          <div className="space-y-2">
            <Select
              label="Globaler KI-Modus"
              value={merged.aiMode}
              onValueChange={(v) => setForm((f) => ({ ...f, aiMode: v as TanssSapAiMode }))}
              options={TANSS_SAP_AI_MODE_OPTIONS}
            />
            <p className="text-xs text-muted-foreground">
              Per-Kunde Override: <code>business_partners.companyProfile.tanssSapAi</code> (true/false)
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SettingsInput
              label="Service-Artikel (Standard)"
              value={merged.serviceItemCode}
              onChange={(v) => setForm((f) => ({ ...f, serviceItemCode: v }))}
              placeholder="tanss"
            />
            <SettingsInput
              label="Fahrtkosten-Artikel"
              value={merged.fahrtkostenItemCode}
              onChange={(v) => setForm((f) => ({ ...f, fahrtkostenItemCode: v }))}
              placeholder="9010"
            />
          </div>

          <div className="flex flex-col gap-3">
            <Switch
              label="TANSS-Artikelnummer als SAP-ItemCode verwenden"
              checked={merged.useTanssArticleNumber}
              onChange={(e) => setForm((f) => ({ ...f, useTanssArticleNumber: e.target.checked }))}
            />
            <Switch
              label="Artikelfehler ignorieren (Rechnung trotzdem erstellen)"
              checked={merged.disableItemErrors}
              onChange={(e) => setForm((f) => ({ ...f, disableItemErrors: e.target.checked }))}
            />
          </div>

          <Input
            label="Tage zurück (Leistungen abrufen)"
            type="number"
            value={String(merged.daysBack ?? 14)}
            onChange={(e) => setForm((f) => ({ ...f, daysBack: Math.max(1, Math.min(365, Number(e.target.value) || 14)) }))}
            hint="Wie weit zurück sollen nicht abgerechnete Leistungen aus geschlossenen Tickets abgefragt werden."
          />

          <div className="space-y-3">
            <Input
              label="Cron-Muster (5-stellig)"
              value={merged.cronPattern}
              onChange={(e) => setForm((f) => ({ ...f, cronPattern: e.target.value }))}
              placeholder="30 2 * * *"
              hint="Standard: 30 2 * * * = täglich 02:30 Uhr"
            />
            <Switch
              label="Debug-Modus — kein SAP-Call, Payload in Queue speichern"
              checked={merged.debugMode}
              onChange={(e) => setForm((f) => ({ ...f, debugMode: e.target.checked }))}
            />
          </div>

          <div className="space-y-2">
            <Select
              label="ItemDetails-Vorlage (Positionsbeschreibung)"
              value={merged.serviceTemplateId ?? '__none__'}
              onValueChange={(v) => setForm((f) => ({ ...f, serviceTemplateId: v === '__none__' ? null : v }))}
              options={[
                { value: '__none__', label: 'Standard (hardcodiertes Format)' },
                ...allTemplates
                  .filter((t) => t.type === 'service')
                  .map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
            <p className="text-xs text-muted-foreground">
              Wähle eine Servicetextzeilen-Vorlage aus Abrechnung → Rechnungstemplates (Typ „service"). Variablen: %%client%%, %%dateString%%, %%timestring%%, %%distanceDriven%%, %%techniciansKeys%%
            </p>
          </div>
        </div>
      </SettingsSection>

      <div className="pt-2">
        <Button
          onClick={() => void handleSave()}
          disabled={update.isPending || !hasChanges}
          loading={update.isPending}
          leadingIcon={<Save className="w-4 h-4" />}
          size="lg"
        >
          Speichern
        </Button>
      </div>
    </div>
  )
}

// ─── Shared Components ───────────────────────────────────────────────────────

function SettingsSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: typeof Sun
  children: React.ReactNode
}) {
  return (
    <section>
      <h2 className="text-small font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        {title}
      </h2>
      <Card>
        <Card.Body>{children}</Card.Body>
      </Card>
    </section>
  )
}

function SettingsInput({
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  type?: 'text' | 'password' | 'email'
  placeholder?: string
}) {
  return (
    <Input
      type={type}
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
    />
  )
}

function StatusBanner({ type, text }: { type: 'error' | 'success'; text: string }) {
  return (
    <div
      className={cn(
        'text-small p-3 rounded-md border mb-4',
        type === 'error'
          ? 'bg-red-50 text-red-600 border-red-100'
          : 'bg-green-50 text-green-600 border-green-100',
      )}
    >
      {text}
    </div>
  )
}

function UserRow({
  user,
  currentUserId,
  isLoading,
  onUpdate,
  onDelete,
  isPending,
}: {
  user: ManagedUser
  currentUserId: string
  isLoading: boolean
  onUpdate: (
    id: string,
    updates: {
      isActive?: boolean
      role?: string
    },
  ) => void
  onDelete: (id: string, name: string) => void
  isPending?: boolean
}) {
  const isSelf = user.id === currentUserId
  const [showModules, setShowModules] = useState(false)
  const [showMemberships, setShowMemberships] = useState(false)

  return (
    <div className={cn(isLoading && 'opacity-50 pointer-events-none')}>
      <div className="flex items-center justify-between px-4 py-3 gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-small font-medium text-gray-900 truncate">{user.name}</p>
            {isSelf && <Badge variant="brand" size="sm">Du</Badge>}
            {user.role === 'admin' && <Badge variant="warning" size="sm">Administrator</Badge>}
          </div>
          <p className="text-caption text-gray-500 truncate">{user.email}</p>
          {user.createdAt && (
            <p className="text-caption text-gray-400 mt-0.5">
              Registriert: {new Date(user.createdAt).toLocaleDateString('de-DE')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Switch
            label="Admin"
            checked={user.role === 'admin'}
            onChange={(e) => onUpdate(user.id, { role: e.target.checked ? 'admin' : 'technician' })}
            disabled={isSelf}
          />

          <Button
            variant={showMemberships ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Building className="w-3.5 h-3.5" />}
            trailingIcon={
              <ChevronDown
                className={cn('w-3 h-3 transition-transform', showMemberships && 'rotate-180')}
              />
            }
            onClick={() => setShowMemberships((v) => !v)}
            title="Firmen-Zuordnungen und TANSS-Mapping verwalten"
          >
            Firmen
          </Button>

          <Button
            variant={showModules ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Boxes className="w-3.5 h-3.5" />}
            trailingIcon={
              <ChevronDown
                className={cn('w-3 h-3 transition-transform', showModules && 'rotate-180')}
              />
            }
            onClick={() => setShowModules((v) => !v)}
            title="Modulberechtigungen verwalten"
          >
            Module
          </Button>

          {isPending ? (
            <Button
              size="sm"
              variant="primary"
              leadingIcon={<UserCheck className="w-3.5 h-3.5" />}
              onClick={() => onUpdate(user.id, { isActive: true })}
            >
              Freigeben
            </Button>
          ) : !isSelf ? (
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<UserX className="w-3.5 h-3.5" />}
              onClick={() => onUpdate(user.id, { isActive: false })}
            >
              Sperren
            </Button>
          ) : null}

          {!isSelf && (
            <IconButton
              icon={<Trash2 />}
              aria-label="Benutzer löschen"
              variant="danger"
              size="sm"
              onClick={() => onDelete(user.id, user.name)}
            />
          )}
        </div>
      </div>

      {showMemberships && (
        <UserTenantMembershipsPanel userId={user.id} userName={user.name} />
      )}

      {showModules && (
        <UserModulePermissionsPanel userId={user.id} userRole={user.role} userName={user.name} />
      )}
    </div>
  )
}

// ─── User Tenant Memberships Panel ───────────────────────────────────────────
//
// Lists all company assignments for a user — one row per company with:
//   * Per-tenant role (overrides system role)
//   * SAP access mode
//   * TANSS employee ID (in that company's TANSS instance)
//   * TANSS credentials (username/password/2FA — stored encrypted)
//
// Rationale: Each tenant has its own TANSS instance; the same SysMan
// user can have different identities in each. The per-company role
// allows e.g. "Sales in Company A, Technician in Company B".

interface MembershipRow {
  tenantId: string
  role: string | null
  sapAccessMode: 'none' | 'tenant' | 'own'
  tanssEmployeeId: string | null
  tanss: {
    tanssUser: string
    tanss2faCode: string
    hasPassword: boolean
  }
}

function UserTenantMembershipsPanel({
  userId,
  userName,
}: {
  userId: string
  userName: string
}) {
  const [memberships, setMemberships] = useState<MembershipRow[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [savingTenantId, setSavingTenantId] = useState<string | null>(null)
  const [addTenantId, setAddTenantId] = useState<string>('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [memberRes, tenantsRes] = await Promise.all([
        getUserTenantMemberships(userId),
        getTenants(),
      ])
      setMemberships(memberRes.data)
      setTenants(tenantsRes.data)
    } catch (err) {
      setError(extractErrorMessage(err) || 'Mitgliedschaften konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load])

  const updateMembership = async (
    tenantId: string,
    updates: { sapAccessMode?: 'none' | 'tenant' | 'own'; tanssEmployeeId?: string | null },
  ) => {
    setSavingTenantId(tenantId)
    try {
      await updateUserTenantMembership(userId, tenantId, updates)
      setMemberships((prev) =>
        prev.map((m) =>
          m.tenantId === tenantId
            ? {
                ...m,
                ...(updates.sapAccessMode !== undefined ? { sapAccessMode: updates.sapAccessMode } : {}),
                ...(updates.tanssEmployeeId !== undefined ? { tanssEmployeeId: updates.tanssEmployeeId } : {}),
              }
            : m,
        ),
      )
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSavingTenantId(null)
    }
  }

  const addMembership = async () => {
    if (!addTenantId) return
    setSavingTenantId(addTenantId)
    try {
      await addUserTenantMembership(userId, addTenantId)
      setAddTenantId('')
      await load()
    } catch (err) {
      setError(extractErrorMessage(err) || 'Zuordnung fehlgeschlagen.')
    } finally {
      setSavingTenantId(null)
    }
  }

  const removeMembership = async (tenantId: string) => {
    if (!confirm('Diese Firmen-Zuordnung wirklich entfernen? TANSS-Zugangsdaten gehen dabei verloren.')) return
    setSavingTenantId(tenantId)
    try {
      await deleteUserTenantMembership(userId, tenantId)
      setMemberships((prev) => prev.filter((m) => m.tenantId !== tenantId))
    } catch (err) {
      setError(extractErrorMessage(err) || 'Entfernen fehlgeschlagen.')
    } finally {
      setSavingTenantId(null)
    }
  }

  if (loading) {
    return (
      <div className="px-4 pb-4">
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-caption text-gray-500">
          Firmen-Zuordnungen werden geladen…
        </div>
      </div>
    )
  }

  const memberTenantIds = new Set(memberships.map((m) => m.tenantId))
  const availableTenants = tenants.filter((t) => !memberTenantIds.has(t.id))

  return (
    <div className="px-4 pb-4">
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
        <div>
          <p className="text-xs font-semibold text-gray-700">
            Firmen-Zuordnungen für {userName}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Pro Firma können SAP-Zugriff und TANSS-Identität individuell hinterlegt werden.
            Modul-Berechtigungen kommen aus den Rechte-Gruppen.
          </p>
        </div>

        {error && <StatusBanner type="error" text={error} />}

        {memberships.length === 0 ? (
          <p className="text-xs text-gray-500">Keine Firmen-Zuordnungen vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {memberships.map((m) => {
              const tenant = tenants.find((t) => t.id === m.tenantId)
              const tenantLabel = tenant?.name ?? m.tenantId
              return (
                <MembershipCard
                  key={m.tenantId}
                  userId={userId}
                  membership={m}
                  tenantLabel={tenantLabel}
                  isSaving={savingTenantId === m.tenantId}
                  onUpdate={(updates) => updateMembership(m.tenantId, updates)}
                  onRemove={() => removeMembership(m.tenantId)}
                  onTanssCredsChanged={() => void load()}
                />
              )
            })}
          </div>
        )}

        {availableTenants.length > 0 && (
          <div className="flex items-end gap-2 pt-2 border-t border-gray-200">
            <div className="flex-1">
              <Select
                label="Weitere Firma zuordnen"
                value={addTenantId}
                onValueChange={setAddTenantId}
                options={[
                  { value: '', label: '— Firma auswählen —' },
                  ...availableTenants.map((t) => ({ value: t.id, label: t.name })),
                ]}
              />
            </div>
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Plus className="w-3.5 h-3.5" />}
              disabled={!addTenantId || savingTenantId !== null}
              onClick={() => void addMembership()}
            >
              Zuordnen
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function MembershipCard({
  userId,
  membership,
  tenantLabel,
  isSaving,
  onUpdate,
  onRemove,
  onTanssCredsChanged,
}: {
  userId: string
  membership: MembershipRow
  tenantLabel: string
  isSaving: boolean
  onUpdate: (updates: { sapAccessMode?: 'none' | 'tenant' | 'own'; tanssEmployeeId?: string | null }) => void
  onRemove: () => void
  onTanssCredsChanged: () => void
}) {
  const [tanssEmpId, setTanssEmpId] = useState(membership.tanssEmployeeId ?? '')
  const [showCreds, setShowCreds] = useState(false)

  useEffect(() => {
    setTanssEmpId(membership.tanssEmployeeId ?? '')
  }, [membership.tanssEmployeeId])

  return (
    <div className={cn('bg-white border border-gray-200 rounded-md p-3 space-y-2', isSaving && 'opacity-60 pointer-events-none')}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-small font-medium text-gray-900">{tenantLabel}</p>
        <IconButton
          icon={<Trash />}
          aria-label="Firmen-Zuordnung entfernen"
          variant="danger"
          size="xs"
          onClick={onRemove}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select
          label="SAP-Zugriff"
          value={membership.sapAccessMode}
          onValueChange={(v) => onUpdate({ sapAccessMode: v as 'none' | 'tenant' | 'own' })}
          options={(['none', 'tenant', 'own'] as const).map((m) => ({
            value: m,
            label: SAP_ACCESS_MODE_LABELS[m],
          }))}
        />
        <SettingsInput
          label="TANSS Employee-ID"
          value={tanssEmpId}
          onChange={setTanssEmpId}
          placeholder="z.B. 1234"
          onBlur={() => {
            const next = tanssEmpId.trim() || null
            if (next !== (membership.tanssEmployeeId ?? null)) {
              onUpdate({ tanssEmployeeId: next })
            }
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-caption text-gray-500">
          TANSS-Login für diese Firma:
          {membership.tanss.tanssUser ? (
            <span className="ml-1 font-medium text-gray-700">{membership.tanss.tanssUser}</span>
          ) : (
            <span className="ml-1 text-gray-400">— nicht hinterlegt —</span>
          )}
          {membership.tanss.hasPassword && <span className="ml-2 text-green-600">●&nbsp;Passwort gespeichert</span>}
        </p>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => setShowCreds((v) => !v)}
        >
          {showCreds ? 'Schließen' : 'TANSS-Zugangsdaten setzen'}
        </Button>
      </div>

      {showCreds && (
        <MembershipTanssCredsEditor
          userId={userId}
          tenantId={membership.tenantId}
          initialTanssUser={membership.tanss.tanssUser}
          initialTanss2faCode={membership.tanss.tanss2faCode}
          hasPassword={membership.tanss.hasPassword}
          onSaved={() => {
            setShowCreds(false)
            onTanssCredsChanged()
          }}
        />
      )}
    </div>
  )
}

function MembershipTanssCredsEditor({
  userId,
  tenantId,
  initialTanssUser,
  initialTanss2faCode,
  hasPassword,
  onSaved,
}: {
  userId: string
  tenantId: string
  initialTanssUser: string
  initialTanss2faCode: string
  hasPassword: boolean
  onSaved: () => void
}) {
  const [tanssUser, setTanssUser] = useState(initialTanssUser)
  const [tanssPassword, setTanssPassword] = useState('')
  const [tanss2faCode, setTanss2faCode] = useState(initialTanss2faCode)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setError('')
    if (!tanssUser.trim()) {
      setError('Benutzername fehlt.')
      return
    }
    if (!hasPassword && !tanssPassword) {
      setError('Beim ersten Setzen muss ein Passwort angegeben werden.')
      return
    }
    setSaving(true)
    try {
      await setUserTanssCredentials(userId, tenantId, {
        tanssUser: tanssUser.trim(),
        tanssPassword: tanssPassword || undefined,
        tanss2faCode: tanss2faCode || undefined,
      })
      onSaved()
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-t border-gray-200 pt-2 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <SettingsInput label="TANSS-Benutzername" value={tanssUser} onChange={setTanssUser} />
        <SettingsInput
          label={hasPassword ? 'Passwort (leer lassen = unverändert)' : 'Passwort'}
          type="password"
          value={tanssPassword}
          onChange={setTanssPassword}
        />
        <SettingsInput
          label="2FA-Secret (optional)"
          value={tanss2faCode}
          onChange={setTanss2faCode}
          placeholder="Base32-Secret"
        />
      </div>
      {error && <StatusBanner type="error" text={error} />}
      <div className="flex justify-end gap-2">
        <Button size="xs" variant="primary" disabled={saving} onClick={() => void submit()}>
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </div>
    </div>
  )
}

// ─── Special permissions tied to specific modules ────────────────────────────

const SPECIAL_PERMISSIONS: Array<{
  resource: string
  action: string
  moduleId: string
  label: string
  description: string
}> = [
  {
    resource: 'infrastructure',
    action: 'update',
    moduleId: 'it-strukturen',
    label: 'Globale Standards bearbeiten',
    description: 'Darf Standard-OUs, Gruppen und Freigaben unter /infrastruktur/strukturen/global ändern.',
  },
]

// ─── User Module Permissions Panel ───────────────────────────────────────────

type ModuleAccessState = 'inherit' | 'allow' | 'deny'

interface PermissionGroupSummary {
  id: string
  name: string
  description: string | null
  moduleCount: number
  memberCount: number
}

function UserModulePermissionsPanel({
  userId,
  userRole,
  userName,
}: {
  userId: string
  userRole: string
  userName: string
}) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  const [groupAllowed, setGroupAllowed] = useState<Record<string, boolean>>({})
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [allGroups, setAllGroups] = useState<PermissionGroupSummary[]>([])
  const [permOverrides, setPermOverrides] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [groupsSaving, setGroupsSaving] = useState(false)
  const [error, setError] = useState('')

  const isCustomerUser = userRole === 'customer'

  useEffect(() => {
    setLoading(true)
    const groupListPromise = isCustomerUser
      ? Promise.resolve<PermissionGroupSummary[]>([])
      : getPermissionGroups()
          .then(({ data }) => data)
          .catch(() => [] as PermissionGroupSummary[])
    Promise.all([
      getUserModules(userId).then(({ data }) => data ?? {}),
      getPermissions().then(({ data }) => {
        const map: Record<string, boolean> = {}
        for (const o of (data.overrides ?? []) as Array<{ userId: string; resource: string; action: string; allowed: boolean }>) {
          if (o.userId === userId) map[`${o.resource}:${o.action}`] = o.allowed
        }
        return map
      }),
      groupListPromise,
    ])
      .then(([mods, perms, groups]) => {
        // Backend returns { overrides, groupAllowed, groupIds } for internal users;
        // for customer users it still returns a flat map (backwards compat).
        if (mods && typeof mods === 'object' && 'overrides' in mods) {
          const m = mods as {
            overrides?: Record<string, boolean>
            groupAllowed?: Record<string, boolean>
            groupIds?: string[]
          }
          setOverrides(m.overrides ?? {})
          setGroupAllowed(m.groupAllowed ?? {})
          setGroupIds(m.groupIds ?? [])
        } else {
          setOverrides((mods ?? {}) as Record<string, boolean>)
          setGroupAllowed({})
          setGroupIds([])
        }
        setPermOverrides(perms)
        setAllGroups(groups)
      })
      .catch((err) => {
        const msg = extractErrorMessage(err)
        setError(`Berechtigungen konnten nicht geladen werden.${msg ? ' ' + msg : ''}`)
      })
      .finally(() => setLoading(false))
  }, [userId, isCustomerUser])

  const grouped = getModulesByCategory()
  const isAdminUser = userRole === 'admin'

  // Collect all module IDs from the registry
  const allModuleIds = Object.values(grouped).flatMap((mods) => mods.map((m) => m.id))

  // Tri-state from override + group inheritance. Effectively allowed =
  // override === true OR (override !== false AND group allows).
  const stateFor = (moduleId: string): ModuleAccessState => {
    const ov = overrides[moduleId]
    if (ov === true) return 'allow'
    if (ov === false) return 'deny'
    return 'inherit'
  }

  const isEffectivelyAllowed = (moduleId: string): boolean => {
    const ov = overrides[moduleId]
    if (ov === false) return false
    if (ov === true) return true
    return groupAllowed[moduleId] === true
  }

  const setModuleState = async (moduleId: string, next: ModuleAccessState) => {
    setSaving(moduleId)
    setError('')
    try {
      if (next === 'inherit') {
        // Remove override → group inheritance applies again.
        await deleteUserModule(userId, moduleId)
        setOverrides((prev) => {
          const copy = { ...prev }
          delete copy[moduleId]
          return copy
        })
      } else {
        const allowed = next === 'allow'
        const { data } = await setUserModules(userId, { [moduleId]: allowed })
        setOverrides((data ?? {}) as Record<string, boolean>)
      }
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Speichern fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(null)
    }
  }

  const setBulk = async (allowed: boolean) => {
    setBulkSaving(true)
    setError('')
    try {
      const modules: Record<string, boolean> = {}
      for (const id of allModuleIds) modules[id] = allowed
      const { data } = await setUserModules(userId, modules)
      setOverrides((data ?? {}) as Record<string, boolean>)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Speichern fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setBulkSaving(false)
    }
  }

  /**
   * "Alle Erben": entfernt alle persoenlichen Overrides → die Module-
   * Erlaubnis ergibt sich wieder ausschliesslich aus den Rechte-Gruppen
   * des Users. Iteriert DELETE pro Modul (parallel) und laedt anschliessend
   * die Override-Map neu.
   */
  const setBulkInherit = async () => {
    setBulkSaving(true)
    setError('')
    try {
      const overriddenIds = Object.keys(overrides)
      if (overriddenIds.length > 0) {
        await Promise.all(
          overriddenIds.map((id) => deleteUserModule(userId, id)),
        )
      }
      const { data } = await getUserModules(userId)
      const next = (data && typeof data === 'object' && 'overrides' in data
        ? (data as { overrides?: Record<string, boolean> }).overrides
        : (data as Record<string, boolean>)) ?? {}
      setOverrides(next)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Speichern fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setBulkSaving(false)
    }
  }

  const toggleGroup = async (groupId: string) => {
    if (isAdminUser || isCustomerUser) return
    const next = groupIds.includes(groupId)
      ? groupIds.filter((g) => g !== groupId)
      : [...groupIds, groupId]
    setGroupsSaving(true)
    setError('')
    try {
      await setUserPermissionGroups(userId, next)
      setGroupIds(next)
      // Reload group allow-map (via /users/:id/modules)
      const { data } = await getUserModules(userId)
      if (data && typeof data === 'object' && 'groupAllowed' in data) {
        setGroupAllowed((data as { groupAllowed?: Record<string, boolean> }).groupAllowed ?? {})
      }
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Gruppen-Zuordnung fehlgeschlagen.${msg ? ' ' + msg : ''}`)
    } finally {
      setGroupsSaving(false)
    }
  }

  const togglePerm = async (resource: string, action: string, allowed: boolean) => {
    const key = `${resource}:${action}`
    setSaving(key)
    setError('')
    try {
      await setPermissionOverride([{ userId, resource, action, allowed }])
      setPermOverrides((prev) => ({ ...prev, [key]: allowed }))
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="px-4 pb-4">
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-caption text-gray-500">
          Modulberechtigungen werden geladen…
        </div>
      </div>
    )
  }

  const effectiveAllowedCount = allModuleIds.filter((id) => isEffectivelyAllowed(id)).length
  const totalCount = allModuleIds.length
  const allowedFromGroupCount = allModuleIds.filter(
    (id) => groupAllowed[id] === true && overrides[id] !== false,
  ).length

  return (
    <div className="px-4 pb-4">
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-gray-700">
              Modulberechtigungen für {userName}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Effektiv erlaubt: {effectiveAllowedCount}/{totalCount} Module
              {!isCustomerUser && allowedFromGroupCount > 0 && (
                <span className="ml-1 text-gray-400">
                  (davon {allowedFromGroupCount} per Gruppe)
                </span>
              )}
              <span className="ml-1 text-gray-400">— Deny gewinnt vor Allow.</span>
            </p>
          </div>
          {!isAdminUser && (
            <div className="flex gap-1.5 flex-shrink-0">
              <Button
                variant="secondary"
                size="xs"
                disabled={bulkSaving}
                onClick={() => void setBulkInherit()}
                title="Alle persoenlichen Overrides entfernen — Module richten sich ausschliesslich nach Rechte-Gruppen"
              >
                Alle Erben
              </Button>
              <Button
                variant="secondary"
                size="xs"
                disabled={bulkSaving}
                onClick={() => void setBulk(true)}
                title="Alle Module explizit erlauben (ueberschreibt Gruppen-Deny)"
              >
                Alle Allow
              </Button>
              <Button
                variant="secondary"
                size="xs"
                disabled={bulkSaving}
                onClick={() => void setBulk(false)}
                title="Alle Module explizit verbieten"
              >
                Alle Deny
              </Button>
            </div>
          )}
        </div>

        {isAdminUser && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-md px-3 py-2">
            Administratoren haben immer Zugriff auf alle Module – Einstellungen hier haben keine
            Wirkung.
          </div>
        )}

        {error && <StatusBanner type="error" text={error} />}

        {!isAdminUser && !isCustomerUser && (
          <div className="bg-white border border-gray-200 rounded-md p-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Rechte-Gruppen
            </p>
            {allGroups.length === 0 ? (
              <p className="text-caption text-gray-500 italic">
                Noch keine Gruppen angelegt. Lege unter Einstellungen → Rechte-Gruppen welche an,
                um Modulpakete (z.B. „Techniker") zentral zu verwalten.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {allGroups.map((g) => {
                  const active = groupIds.includes(g.id)
                  return (
                    <button
                      key={g.id}
                      type="button"
                      disabled={groupsSaving}
                      onClick={() => toggleGroup(g.id)}
                      className={cn(
                        'text-caption px-2 py-1 rounded border transition-colors',
                        active
                          ? 'bg-brand-50 border-brand-300 text-brand-800'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                        groupsSaving && 'opacity-60 cursor-not-allowed',
                      )}
                      title={g.description ?? undefined}
                    >
                      {active ? '✓ ' : ''}
                      {g.name}
                      <span className="ml-1 text-gray-400">({g.moduleCount})</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {Object.entries(grouped).map(([category, mods]) => {
            if (!mods || mods.length === 0) return null
            return (
              <div key={category}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {category}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {mods.map((mod) => {
                    const state = stateFor(mod.id)
                    const inheritedAllowed = groupAllowed[mod.id] === true
                    const effectiveAllowed = state === 'allow' || (state === 'inherit' && inheritedAllowed)
                    const isSaving = saving === mod.id || bulkSaving
                    const modPerms = SPECIAL_PERMISSIONS.filter((p) => p.moduleId === mod.id)
                    const hasPerms = modPerms.length > 0
                    const showTriState = !isCustomerUser && !isAdminUser
                    return (
                      <div
                        key={mod.id}
                        className={cn(
                          'flex flex-col bg-white border border-gray-200 rounded overflow-hidden transition-opacity',
                          hasPerms && 'sm:col-span-2',
                          showTriState && !effectiveAllowed && 'opacity-50',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                        <div className="min-w-0">
                          <p className="text-caption font-medium text-gray-800 truncate">{mod.name}</p>
                          <p className="text-caption text-gray-500 truncate">{mod.description}</p>
                          {showTriState && state === 'inherit' && (
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {inheritedAllowed ? 'erlaubt durch Gruppe' : 'kein Zugriff (keine Gruppe erlaubt)'}
                            </p>
                          )}
                        </div>
                        {showTriState ? (
                          <div className="inline-flex rounded border border-gray-200 overflow-hidden text-caption flex-shrink-0">
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => setModuleState(mod.id, 'inherit')}
                              className={cn(
                                'px-2 py-0.5 transition-colors',
                                state === 'inherit'
                                  ? inheritedAllowed
                                    ? 'bg-emerald-100 text-emerald-800 font-semibold'
                                    : 'bg-gray-100 text-gray-500 font-semibold'
                                  : 'bg-white text-gray-500 hover:bg-gray-50',
                              )}
                              title={
                                state === 'inherit' && !inheritedAllowed
                                  ? 'Erben — aktuell ohne Zugriff (keine Gruppe enthält das Modul)'
                                  : 'Erben (aus Gruppen)'
                              }
                            >
                              Erben
                            </button>
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => setModuleState(mod.id, 'allow')}
                              className={cn(
                                'px-2 py-0.5 border-l border-gray-200 transition-colors',
                                state === 'allow'
                                  ? 'bg-emerald-100 text-emerald-800 font-semibold'
                                  : 'bg-white text-gray-500 hover:bg-emerald-50',
                              )}
                              title="Explizit erlauben"
                            >
                              Allow
                            </button>
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => setModuleState(mod.id, 'deny')}
                              className={cn(
                                'px-2 py-0.5 border-l border-gray-200 transition-colors',
                                state === 'deny'
                                  ? 'bg-red-100 text-red-800 font-semibold'
                                  : 'bg-white text-gray-500 hover:bg-red-50',
                              )}
                              title="Explizit sperren (Deny gewinnt)"
                            >
                              Deny
                            </button>
                          </div>
                        ) : (
                          <Switch
                            checked={state === 'allow'}
                            disabled={isSaving || isAdminUser}
                            onChange={() => setModuleState(mod.id, state === 'allow' ? 'inherit' : 'allow')}
                            title={state === 'allow' ? 'Erlaubt' : 'Gesperrt'}
                          />
                        )}
                        </div>
                        {hasPerms && (
                          <div className="border-t border-gray-100 divide-y divide-gray-100">
                            {modPerms.map(({ resource, action, label, description }) => {
                              const key = `${resource}:${action}`
                              const isOn = permOverrides[key] === true
                              const isSavingPerm = saving === key
                              return (
                                <div key={key} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-gray-50">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <Shield className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-caption font-medium text-gray-700 truncate">{label}</p>
                                      <p className="text-caption text-gray-400 truncate">{description}</p>
                                    </div>
                                  </div>
                                  <Switch
                                    checked={isOn}
                                    disabled={isSavingPerm || isAdminUser}
                                    onChange={() => togglePerm(resource, action, !isOn)}
                                    title={isOn ? 'Erlaubt' : 'Gesperrt'}
                                  />
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Tenant Bank Accounts (GoCardless + manueller Import) ──────────────────

interface BankAccountRow {
  id: string
  name: string
  bankName: string | null
  iban: string | null
  accountType: 'giro' | 'credit_card' | 'loan'
  source: 'gocardless' | 'manual_import' | 'sap'
  gocardlessAccountId: string | null
  consentExpiresAt: string | null
  isActive: boolean
  autoBook: boolean
}

interface InstitutionRow { id: string; name: string; bic: string; logo: string }

function TenantBankAccountsSection({ tenantId }: { tenantId: string }) {
  const [accounts, setAccounts] = useState<BankAccountRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showBatch, setShowBatch] = useState(false)
  const [sapImporting, setSapImporting] = useState(false)
  const [sapResult, setSapResult] = useState<{ imported: number; duplicates: number; noIban: number; total: number } | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    getBankAccounts(tenantId)
      .then((r) => setAccounts(r.data))
      .catch((e) => setError(extractErrorMessage(e) || 'Konten konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [tenantId])

  useEffect(() => { load() }, [load])

  // Finalize callback nach GoCardless-Redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('gc_finalize')) {
      const pending = localStorage.getItem('gocardless-pending')
      if (pending) {
        const { accountId } = JSON.parse(pending) as { accountId: string }
        finalizeRequisition(accountId)
          .then(() => { localStorage.removeItem('gocardless-pending'); load() })
          .catch((e) => setError(extractErrorMessage(e) || 'Finalize fehlgeschlagen'))
          .finally(() => {
            window.history.replaceState({}, '', window.location.pathname)
          })
      }
    }
  }, [load])

  const [pendingDeleteAccount, setPendingDeleteAccount] = useState<string | null>(null)

  async function deleteAccount(id: string) {
    try {
      await deleteBankAccount(id)
      setPendingDeleteAccount(null)
      load()
    } catch (e) {
      setError(extractErrorMessage(e) || 'Löschen fehlgeschlagen')
    }
  }

  async function importFromSap() {
    setSapImporting(true)
    setError(null)
    setSapResult(null)
    try {
      const r = await importBankAccountsFromSap(tenantId)
      setSapResult(r.data)
      load()
    } catch (e) {
      setError(extractErrorMessage(e) || 'SAP-Import fehlgeschlagen')
    } finally {
      setSapImporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-h4 text-gray-900">Bankkonten</h3>
          <p className="text-caption text-gray-500">PSD2-Anbindung via GoCardless oder manueller MT940/CAMT-Import</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={sapImporting}
            onClick={importFromSap}
          >
            {sapImporting ? 'Lade aus SAP…' : 'Aus SAP laden'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowBatch(true)}>
            Stapel-Import (SFirm)
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            Bankkonto hinzufügen
          </Button>
        </div>
      </div>

      {error && <StatusBanner type="error" text={error} />}

      {sapResult && (
        <StatusBanner
          type="success"
          text={`SAP-Import: ${sapResult.imported} importiert${sapResult.duplicates ? `, ${sapResult.duplicates} bereits vorhanden` : ''}${sapResult.noIban ? `, ${sapResult.noIban} ohne IBAN` : ''} (${sapResult.total} Konten in SAP)`}
        />
      )}

      <p className="text-caption text-gray-500 italic">
        Hinweis: GoCardless-Zugangsdaten (Secret ID/Key) werden im Tab „Adapter" als Adapter <code>gocardless</code> hinterlegt.
      </p>

      {loading ? (
        <LoadingState />
      ) : accounts.length === 0 ? (
        <EmptyState
          title="Noch keine Bankkonten verbunden"
          size="sm"
        />
      ) : (
        <div className="space-y-2">
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center justify-between p-3 border border-gray-200 rounded">
              <div className="flex-1 min-w-0">
                <div className="text-small font-medium text-gray-900">{a.name}</div>
                <div className="text-caption text-gray-500">
                  {a.bankName ?? '–'} · {a.iban ?? 'keine IBAN'} ·{' '}
                  {a.accountType === 'giro' && 'Girokonto'}
                  {a.accountType === 'credit_card' && 'Kreditkarte'}
                  {a.accountType === 'loan' && 'Darlehen'}
                  {' · '}{a.source === 'gocardless' ? 'PSD2/GoCardless' : a.source === 'sap' ? 'SAP' : 'Manueller Import'}
                </div>
                {a.consentExpiresAt && (
                  <div className="text-caption text-gray-400 mt-0.5">
                    Consent gültig bis {new Date(a.consentExpiresAt).toLocaleDateString('de-DE')}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={a.isActive ? 'success' : 'neutral'} size="sm">
                  {a.isActive ? 'Aktiv' : 'Inaktiv'}
                </Badge>
                <Button
                  variant="link"
                  size="xs"
                  className="text-red-600 hover:text-red-800"
                  onClick={() => setPendingDeleteAccount(a.id)}
                >
                  Entfernen
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddBankModal
        open={showAdd}
        tenantId={tenantId}
        onClose={() => setShowAdd(false)}
        onAdded={() => { setShowAdd(false); load() }}
      />
      <BatchImportModal
        open={showBatch}
        tenantId={tenantId}
        onClose={() => setShowBatch(false)}
        onDone={load}
      />
      <ConfirmDialog
        open={pendingDeleteAccount !== null}
        title="Bankkonto entfernen"
        message="Bankkonto wirklich entfernen?"
        confirmLabel="Entfernen"
        variant="danger"
        onConfirm={() => { if (pendingDeleteAccount) deleteAccount(pendingDeleteAccount) }}
        onCancel={() => setPendingDeleteAccount(null)}
      />
    </div>
  )
}

// ─── Stapel-Import (mehrere MT940/CAMT-Dateien gleichzeitig) ────────────────

interface BatchResult {
  filename: string
  format: string
  detectedIban: string | null
  transactions: number
  status: 'created' | 'duplicate' | 'no-iban' | 'unknown-account' | 'error'
  sapAbsoluteEntry?: number
  sapStatementNumber?: string
  message?: string
}

function BatchImportModal({ open, tenantId, onClose, onDone }: { open: boolean; tenantId: string; onClose: () => void; onDone: () => void }) {
  const [files, setFiles] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<BatchResult[] | null>(null)
  const [totals, setTotals] = useState<{ files: number; created: number; duplicates: number; errors: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  function addFiles(list: FileList | null) {
    if (!list) return
    const newOnes = Array.from(list)
    setFiles((prev) => [...prev, ...newOnes].slice(0, 50))
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  async function startImport() {
    if (files.length === 0) return
    setUploading(true)
    setError(null)
    setResults(null)
    try {
      const payload = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await f.text() })),
      )
      // Push direkt nach SAP B1 BSP-Modul (Single Source of Truth)
      const res = await importBatchTransactions(tenantId, payload)
      setResults(res.data.results)
      setTotals(res.data.totals)
      onDone()
    } catch (e) {
      setError(extractErrorMessage(e) || 'Stapel-Import fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => { if (!next) onClose() }}
      size="xl"
      title="Stapel-Import (SFirm)"
      description="MT940/CAMT.053-Dateien aus SFirm hochladen – Konten werden automatisch via IBAN zugeordnet"
      footer={
        results ? (
          <Button onClick={onClose}>Schließen</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              loading={uploading}
              disabled={files.length === 0}
              onClick={startImport}
            >
              {uploading ? 'Importiere…' : `${files.length} Datei${files.length === 1 ? '' : 'en'} importieren`}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {!results && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragging(false)
                  addFiles(e.dataTransfer.files)
                }}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50'
                }`}
              >
                <p className="text-sm text-gray-700 font-medium">
                  Dateien hierhin ziehen oder
                </p>
                <label className="inline-block mt-2 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
                  Dateien auswählen
                  <input
                    type="file"
                    multiple
                    accept=".sta,.txt,.xml,.mt940,.camt,.cmt,.053"
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">
                  MT940 (.sta/.txt/.mt940) oder CAMT.053 (.xml) · max. 50 Dateien
                </p>
              </div>

              {files.length > 0 && (
                <div className="border border-gray-200 rounded">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-600">
                    {files.length} Datei{files.length === 1 ? '' : 'en'} bereit
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 text-sm">
                        <div className="flex-1 min-w-0 truncate">
                          <span className="text-gray-900">{f.name}</span>
                          <span className="text-xs text-gray-400 ml-2">({(f.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button onClick={() => removeFile(i)} className="text-xs text-red-600 hover:text-red-800 ml-2">
                          Entfernen
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && <StatusBanner type="error" text={error} />}
            </>
          )}

        {results && totals && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-h2 font-semibold text-gray-900">{totals.files}</div>
                <div className="text-caption text-gray-500">Dateien</div>
              </div>
              <div className="bg-green-50 rounded p-3">
                <div className="text-h2 font-semibold text-green-700">{totals.created}</div>
                <div className="text-caption text-green-600">In SAP angelegt</div>
              </div>
              <div className="bg-yellow-50 rounded p-3">
                <div className="text-h2 font-semibold text-yellow-700">{totals.duplicates}</div>
                <div className="text-caption text-yellow-600">Bereits in SAP</div>
              </div>
              <div className="bg-red-50 rounded p-3">
                <div className="text-h2 font-semibold text-red-700">{totals.errors}</div>
                <div className="text-caption text-red-600">Fehler</div>
              </div>
            </div>

            <div className="border border-gray-200 rounded overflow-hidden">
              <DataTable<BatchResult>
                data={results}
                getRowId={(_r, i) => String(i)}
                enableSorting={false}
                columns={[
                  {
                    accessorKey: 'filename',
                    header: 'Datei',
                    cell: ({ row }) => (
                      <span className="truncate max-w-[200px] block" title={row.original.message}>
                        {row.original.filename}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'detectedIban',
                    header: 'IBAN',
                    cell: ({ row }) => (
                      <span className="text-caption text-gray-500 font-mono">{row.original.detectedIban ?? '–'}</span>
                    ),
                  },
                  {
                    accessorKey: 'transactions',
                    header: 'Zeilen',
                    cell: ({ row }) => (
                      <span className="block text-right text-gray-700 tabular-nums">{row.original.transactions}</span>
                    ),
                  },
                  {
                    accessorKey: 'status',
                    header: 'Status',
                    cell: ({ row }) => {
                      const statusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
                        created: { label: 'Angelegt', variant: 'success' },
                        duplicate: { label: 'Bereits vorhanden', variant: 'warning' },
                        'no-iban': { label: 'Keine IBAN', variant: 'danger' },
                        'unknown-account': { label: 'Konto fehlt in SAP', variant: 'danger' },
                        error: { label: 'Fehler', variant: 'danger' },
                      }
                      const sm = statusMap[row.original.status] ?? { label: row.original.status, variant: 'neutral' as const }
                      return (
                        <div>
                          <Badge variant={sm.variant} size="sm">{sm.label}</Badge>
                          {row.original.message && (row.original.status === 'error' || row.original.status === 'unknown-account' || row.original.status === 'no-iban') && (
                            <div className="text-caption text-red-600 mt-1">{row.original.message}</div>
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    id: 'sapRef',
                    header: 'SAP-Auszug',
                    cell: ({ row }) => (
                      <span className="text-caption text-gray-500 font-mono">
                        {row.original.sapStatementNumber ?? (row.original.sapAbsoluteEntry ? `#${row.original.sapAbsoluteEntry}` : '–')}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}


function AddBankModal({ open, tenantId, onClose, onAdded }: { open: boolean; tenantId: string; onClose: () => void; onAdded: () => void }) {
  const [mode, setMode] = useState<'gocardless' | 'manual'>('gocardless')
  const [institutions, setInstitutions] = useState<InstitutionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [accountName, setAccountName] = useState('')
  const [accountType, setAccountType] = useState<'giro' | 'credit_card' | 'loan'>('giro')
  const [bankName, setBankName] = useState('')
  const [iban, setIban] = useState('')

  useEffect(() => {
    if (mode !== 'gocardless') return
    setLoading(true)
    setError(null)
    getInstitutions(tenantId, 'de')
      .then((r) => setInstitutions(r.data))
      .catch((e) => setError(extractErrorMessage(e) || 'Banken konnten nicht geladen werden – ist GoCardless im Adapter-Tab konfiguriert?'))
      .finally(() => setLoading(false))
  }, [mode, tenantId])

  async function selectInstitution(inst: InstitutionRow) {
    if (!accountName) { alert('Bitte erst eine Konto-Bezeichnung vergeben'); return }
    try {
      const res = await createRequisition({
        tenantId,
        institutionId: inst.id,
        accountName,
        accountType,
        redirectUrl: window.location.origin + window.location.pathname + '?gc_finalize=1',
      })
      localStorage.setItem('gocardless-pending', JSON.stringify({ accountId: res.data.accountId }))
      window.location.href = res.data.link
    } catch (e) {
      setError(extractErrorMessage(e) || 'Anbindung fehlgeschlagen')
    }
  }

  async function createManual() {
    if (!accountName) { setError('Konto-Bezeichnung fehlt'); return }
    try {
      await createBankAccount({
        tenantId,
        name: accountName,
        bankName: bankName || undefined,
        iban: iban || undefined,
        accountType,
        source: 'manual_import',
      })
      onAdded()
    } catch (e) {
      setError(extractErrorMessage(e) || 'Anlegen fehlgeschlagen')
    }
  }

  const filtered = institutions.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <Modal
      open={open}
      onOpenChange={(next) => { if (!next) onClose() }}
      size="lg"
      title="Bankkonto hinzufügen"
      footer={
        mode === 'manual' ? (
          <>
            <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
            <Button onClick={createManual}>Anlegen</Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            variant={mode === 'gocardless' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setMode('gocardless')}
          >
            PSD2 (GoCardless)
          </Button>
          <Button
            variant={mode === 'manual' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setMode('manual')}
          >
            Manuell (für SFirm-Import / Darlehen)
          </Button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Konto-Bezeichnung"
              required
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="z.B. Hypovereinsbank Geschäftskonto"
            />
            <Select
              label="Konto-Typ"
              value={accountType}
              onValueChange={(v) => setAccountType(v as 'giro' | 'credit_card' | 'loan')}
              options={[
                { value: 'giro', label: 'Girokonto' },
                { value: 'credit_card', label: 'Kreditkarte' },
                { value: 'loan', label: 'Darlehen' },
              ]}
            />
          </div>

          {mode === 'manual' && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Bank-Name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />
              <Input
                label="IBAN"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                className="font-mono"
              />
            </div>
          )}

          {mode === 'gocardless' && (
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Bank suchen..."
            />
          )}

          {error && <StatusBanner type="error" text={error} />}
        </div>

        {mode === 'gocardless' && (
          loading ? (
            <LoadingState />
          ) : (
            <div className="space-y-1">
              {filtered.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => selectInstitution(inst)}
                  className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded text-left"
                >
                  {inst.logo && <img src={inst.logo} alt="" className="w-8 h-8 object-contain" />}
                  <div className="flex-1">
                    <div className="text-small font-medium text-gray-900">{inst.name}</div>
                    <div className="text-caption text-gray-500">{inst.bic}</div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </Modal>
  )
}

// ─── NinjaOne Auto-Sync Settings ─────────────────────────────────────────────

function NinjaAutoSyncSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [status, setStatus] = useState<NinjaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const loadStatus = () => {
    return getNinjaStatus()
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null))
  }

  useEffect(() => {
    void Promise.all([
      getNinjaAutoSync()
        .then(({ data }) => setEnabled(data.enabled))
        .catch(() => setError('NinjaOne Auto-Sync-Status konnte nicht geladen werden.')),
      loadStatus(),
    ]).finally(() => setLoading(false))
  }, [])

  const handleToggle = async (value: boolean) => {
    setSaving(true)
    setError('')
    try {
      const { data } = await setNinjaAutoSync(value)
      setEnabled(data.enabled)
    } catch {
      setError('Auto-Sync konnte nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setError('')
    try {
      await loadStatus()
    } catch {
      setError('Verbindungstest fehlgeschlagen.')
    } finally {
      setTesting(false)
    }
  }

  if (loading) return null

  return (
    <Card>
      <Card.Header>
        <h3 className="text-h3">NinjaOne Einstellungen</h3>
        <Button variant="secondary" size="sm" onClick={() => void handleTest()} disabled={testing}>
          {testing ? 'Teste…' : 'Verbindung testen'}
        </Button>
      </Card.Header>
      <Card.Body className="space-y-4">
        {status && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {!status.configured
                ? <StatusBadge status="neutral" label="Nicht konfiguriert" />
                : status.connected
                  ? <StatusBadge status="success" label="Verbunden" />
                  : <StatusBadge status="danger" label="Verbindung fehlgeschlagen" />}
              {status.tokenValidForMs != null && status.tokenValidForMs > 0 && (
                <span className="text-caption text-gray-400">
                  Token gültig noch {Math.round(status.tokenValidForMs / 60000)} min
                </span>
              )}
            </div>
            {!status.connected && status.connectionError && (
              <p className="text-caption text-red-600 font-mono break-all">{status.connectionError}</p>
            )}
          </div>
        )}
        {error && <ErrorState title={error} />}
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-body font-medium">Auto-Sync bei Neuanlage</div>
            <div className="text-sm text-muted-foreground">
              Neukunde in SysMan → wird automatisch als NinjaOne-Organisation angelegt
            </div>
          </div>
          <Switch
            checked={enabled ?? false}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={saving}
          />
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Global Adapter Configurations ─────────────────────────────────────────

function GlobalAdaptersOverview() {
  const [configs, setConfigs] = useState<AdapterConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [pendingDeleteAdapter, setPendingDeleteAdapter] = useState<string | null>(null)

  const [editingAdapter, setEditingAdapter] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Record<string, unknown>>({})
  const [editLabel, setEditLabel] = useState('')
  const [editActive, setEditActive] = useState(true)

  const [showAdd, setShowAdd] = useState(false)
  const [newAdapterType, setNewAdapterType] = useState('')
  const [newForm, setNewForm] = useState<Record<string, unknown>>({})
  const [newLabel, setNewLabel] = useState('')

  const loadConfigs = useCallback(() => {
    setLoading(true)
    getGlobalAdapters()
      .then(({ data }) => setConfigs(data))
      .catch(() => setError('Globale Adapter konnten nicht geladen werden.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { loadConfigs() }, [loadConfigs])

  const configuredAdapters = configs.map((c) => c.adapter)
  const availableAdapters = ADAPTER_OPTIONS.filter((a) => !configuredAdapters.includes(a))

  const startEdit = (cfg: AdapterConfig) => {
    setEditingAdapter(cfg.adapter)
    setEditForm(stripMaskedConfig(cfg.config))
    setEditLabel(cfg.label ?? '')
    setEditActive(cfg.isActive)
    setMessage('')
  }

  const cancelEdit = () => {
    setEditingAdapter(null)
    setEditForm({})
  }

  const saveAdapter = async (adapter: string, config: Record<string, unknown>, label: string, isActive: boolean) => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await saveGlobalAdapter({
        adapter,
        label: label || null,
        config,
        isActive,
      })
      setMessage('Globale Adapter-Konfiguration gespeichert.')
      setEditingAdapter(null)
      setShowAdd(false)
      setNewAdapterType('')
      setNewForm({})
      setNewLabel('')
      loadConfigs()
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const deleteAdapter = async (adapter: string) => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await deleteGlobalAdapter(adapter)
      setMessage('Globaler Adapter entfernt.')
      setEditingAdapter(null)
      setPendingDeleteAdapter(null)
      loadConfigs()
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Löschen.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const startAdd = () => {
    setShowAdd(true)
    const firstAdapter = availableAdapters[0] ?? ''
    setNewAdapterType(firstAdapter)
    setNewForm(defaultsForAdapter(firstAdapter))
    setNewLabel('')
    setMessage('')
    setEditingAdapter(null)
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-6">
      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <SettingsSection title="Globale Adapter-Konfigurationen" icon={Settings}>
        <p className="text-caption text-gray-500 mb-3">
          Adapter-Konfigurationen, die systemweit gelten (statt pro Firma). Firmen können im Firma-Tab
          für jeden Adapter wählen, ob sie ihre eigene Konfiguration oder die globale verwenden.
        </p>

        {configs.length === 0 && !showAdd && (
          <p className="text-small text-gray-400 italic mb-3">Keine globalen Adapter konfiguriert.</p>
        )}

        <div className="space-y-2">
          {configs.map((cfg) => (
            <div key={cfg.id}>
              {editingAdapter === cfg.adapter ? (
                <AdapterForm
                  adapter={cfg.adapter}
                  fields={ADAPTER_FIELDS[cfg.adapter] ?? []}
                  values={editForm}
                  onChange={setEditForm}
                  label={editLabel}
                  onLabelChange={setEditLabel}
                  isActive={editActive}
                  onActiveChange={setEditActive}
                  onSave={() => saveAdapter(cfg.adapter, editForm, editLabel, editActive)}
                  onCancel={cancelEdit}
                  onDelete={() => setPendingDeleteAdapter(cfg.adapter)}
                  saving={saving}
                  isNew={false}
                />
              ) : (
                <div
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => startEdit(cfg)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-small font-medium">{ADAPTER_LABELS[cfg.adapter] ?? cfg.adapter}</span>
                    {cfg.label && <span className="text-small text-gray-400">({cfg.label})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={cfg.isActive ? 'success' : 'neutral'} size="sm">
                      {cfg.isActive ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    <Code2 className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showAdd && (
          <div className="mt-4">
            <div className="mb-3">
              {availableAdapters.length === 0 ? (
                <p className="text-small text-gray-400 italic">Alle Adapter bereits konfiguriert</p>
              ) : (
                <Select
                  label="Adapter-Typ"
                  value={newAdapterType}
                  onValueChange={(v) => { setNewAdapterType(v); setNewForm(defaultsForAdapter(v)) }}
                  options={availableAdapters.map((a) => ({
                    value: a,
                    label: ADAPTER_LABELS[a] ?? a,
                  }))}
                  searchable
                />
              )}
            </div>
            {newAdapterType && (
              <AdapterForm
                adapter={newAdapterType}
                fields={ADAPTER_FIELDS[newAdapterType] ?? []}
                values={newForm}
                onChange={setNewForm}
                label={newLabel}
                onLabelChange={setNewLabel}
                isActive={true}
                onActiveChange={() => {}}
                onSave={() => saveAdapter(newAdapterType, newForm, newLabel, true)}
                onCancel={() => { setShowAdd(false); setNewAdapterType(''); setNewForm({}); setNewLabel('') }}
                saving={saving}
                isNew={true}
              />
            )}
          </div>
        )}

        {!showAdd && availableAdapters.length > 0 && (
          <Button
            className="mt-3 border border-brand-200"
            variant="ghost"
            size="sm"
            leadingIcon={<Plus className="w-3.5 h-3.5" />}
            onClick={startAdd}
          >
            Globalen Adapter hinzufügen
          </Button>
        )}
      </SettingsSection>

      <ConfirmDialog
        open={pendingDeleteAdapter !== null}
        title="Adapter entfernen"
        message={
          pendingDeleteAdapter
            ? `Globalen Adapter "${ADAPTER_LABELS[pendingDeleteAdapter] ?? pendingDeleteAdapter}" wirklich entfernen?`
            : ''
        }
        confirmLabel="Entfernen"
        variant="danger"
        loading={saving}
        onConfirm={() => { if (pendingDeleteAdapter) deleteAdapter(pendingDeleteAdapter) }}
        onCancel={() => setPendingDeleteAdapter(null)}
      />
    </div>
  )
}

// ─── TANSS Adapter Extensions ─────────────────────────────────────────────
//
// Rendered directly below the TANSS adapter form. Contains the
// company-specific TANSS fields (company ID + customer types) that are not
// part of the generic adapter config. They are stored in the tenants table
// and the dedicated tanss-categories endpoint respectively.

function TanssAdapterExtensions({ tenantId }: { tenantId: string }) {
  const [tanssCompanyId, setTanssCompanyId] = useState<string>('')
  const [tanssCompanyName, setTanssCompanyName] = useState<string>('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [tanssCategories, setTanssCategories] = useState<{ id: number; name: string; categoryName?: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    Promise.all([
      getTenant(tenantId).then(({ data }) => data),
      getTenantTanssCategories(tenantId).then(({ data }) => Array.isArray(data) ? data : []).catch(() => []),
      getTanssCompanyCategoryTypes().then(({ data }) => {
        if (Array.isArray(data)) return data
        if (data?.types) return data.types
        return []
      }).catch(() => []),
    ])
      .then(([t, assigned, available]) => {
        setTanssCompanyId(t.tanssCompanyId ?? '')
        setTanssCompanyName(t.tanssCompanyName ?? '')
        setCategoryIds(assigned)
        setTanssCategories(available)
      })
      .catch(() => setError('TANSS-Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false))
  }, [tenantId])

  const save = async () => {
    setSaving(true)
    setMessage('')
    setError('')
    try {
      // 1) Save company ID + name in tenants
      const { data: tenant } = await getTenant(tenantId)
      await updateTenant(tenantId, {
        code: tenant.code,
        name: tenant.name,
        description: tenant.description,
        tanssCompanyId: tanssCompanyId || undefined,
        tanssCompanyName: tanssCompanyName || undefined,
        isPrimary: tenant.isPrimary,
        isActive: tenant.isActive,
      })
      // 2) Customer type assignment
      await updateTenantTanssCategories(tenantId, categoryIds)
      setMessage('TANSS-Verknüpfung gespeichert.')
    } catch (err) {
      const msg = extractErrorMessage(err)
      setError(`Fehler beim Speichern.${msg ? ' ' + msg : ''}`)
    } finally {
      setSaving(false)
    }
  }

  const toggleCategory = (id: string) => {
    setCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  const filtered = search
    ? tanssCategories.filter((c) => {
        const q = search.toLowerCase()
        return c.name.toLowerCase().includes(q) || (c.categoryName ?? '').toLowerCase().includes(q)
      })
    : tanssCategories

  if (loading) return <p className="text-caption text-gray-500 italic px-3">TANSS-Daten werden geladen…</p>

  return (
    <div className="border border-brand-100 rounded-lg p-4 bg-white space-y-4">
      <div>
        <h4 className="text-small font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Link className="w-4 h-4 text-brand-500" />
          TANSS-Verknüpfung
        </h4>
        <p className="text-caption text-gray-500 mb-2">
          Diese Felder sind firmen-spezifisch und werden auch dann pro Firma gespeichert, wenn oben „Globale Konfiguration verwenden" aktiv ist.
        </p>
        <TanssCompanySearch
          selectedId={tanssCompanyId}
          selectedName={tanssCompanyName}
          onSelect={(id, name) => { setTanssCompanyId(id); setTanssCompanyName(name) }}
        />
      </div>

      <div>
        <h4 className="text-small font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Link className="w-4 h-4 text-brand-500" />
          TANSS-Kundentypen-Zuordnung
        </h4>
        <p className="text-caption text-gray-500 mb-2">
          Kunden mit diesen Typen werden automatisch dieser Firma zugeordnet.
        </p>

        {tanssCategories.length > 0 ? (
          <>
            {tanssCategories.length > 8 && (
              <div className="mb-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kundentypen durchsuchen..."
                />
              </div>
            )}
            <div className="space-y-1 max-h-60 overflow-y-auto border border-gray-100 rounded p-1">
              {filtered.map((cat) => {
                const checked = categoryIds.includes(String(cat.id))
                return (
                  <label
                    key={cat.id}
                    className={cn(
                      'flex items-center gap-3 px-2 py-1.5 rounded cursor-pointer text-caption',
                      checked ? 'bg-brand-50' : 'hover:bg-gray-50',
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleCategory(String(cat.id))}
                    />
                    <span>
                      {cat.name}
                      {cat.categoryName && <span className="text-gray-400 ml-1">({cat.categoryName})</span>}
                    </span>
                    <span className="text-caption text-gray-400 ml-auto">ID {cat.id}</span>
                  </label>
                )
              })}
            </div>
            <p className="text-caption text-gray-400 mt-1">{categoryIds.length} von {tanssCategories.length} Typen zugeordnet</p>
          </>
        ) : (
          <p className="text-caption text-gray-400 italic">Keine Kundentypen aus TANSS verfügbar.</p>
        )}
      </div>

      {error && <StatusBanner type="error" text={error} />}
      {message && <StatusBanner type="success" text={message} />}

      <Button
        size="sm"
        loading={saving}
        leadingIcon={<Save className="w-3 h-3" />}
        onClick={save}
      >
        TANSS-Verknüpfung speichern
      </Button>
    </div>
  )
}

// ─── EGIS Connection Test ─────────────────────────────────────────────────────

function EgisConnectionTest() {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<null | {
    success: boolean
    baseUrl: string
    operationUrl?: string
    login: string
    erpName: string
    httpStatus: number
    xmlSent: string
    responseBody: string
  }>(null)
  const [showXml, setShowXml] = useState(false)

  const runTest = async () => {
    setLoading(true)
    setResult(null)
    setShowXml(false)
    try {
      const { data } = await testEgisConnection()
      setResult(data)
    } catch (err) {
      const msg = extractErrorMessage(err)
      setResult({
        success: false,
        baseUrl: '',
        login: '',
        erpName: '',
        httpStatus: 0,
        xmlSent: '',
        responseBody: msg || 'Unbekannter Fehler',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="border-t border-brand-200 pt-3 mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={runTest}
          loading={loading}
          leadingIcon={<Link className="w-3.5 h-3.5" />}
        >
          Verbindung testen
        </Button>
        <span className="text-caption text-gray-500">
          Sendet eine Test-Suche an EGIS und zeigt die Server-Antwort.
        </span>
      </div>
      {result && (
        <div className={`rounded border p-3 text-caption space-y-1.5 ${result.success ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          <div className="font-medium flex items-center gap-2">
            {!result.success && <AlertTriangle className="w-3.5 h-3.5" />}
            {result.success ? 'Verbindung erfolgreich' : `Verbindung fehlgeschlagen (HTTP ${result.httpStatus || '—'})`}
          </div>
          {(result.operationUrl || result.baseUrl) && (
            <div className="space-y-0.5 text-inherit opacity-80">
              <div>URL: <code className="break-all">{result.operationUrl || result.baseUrl}</code></div>
            </div>
          )}
          {result.login && (
            <div className="space-y-0.5 text-inherit opacity-80">
              <div>Login: <code>{result.login}</code> · ERP: <code>{result.erpName}</code></div>
            </div>
          )}
          {!result.success && result.httpStatus === 404 && (
            <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-xs space-y-1">
              <div className="font-semibold">HTTP 404 — Mögliche Ursachen:</div>
              <ol className="list-decimal list-inside space-y-0.5 pl-1">
                <li>Die Base-URL zeigt auf die EGIS-Website, nicht den API-Server. Bitte erfragen Sie die korrekte API-URL bei Ihrem EGIS-Anbieter.</li>
                <li>Die IP-Adresse dieses Servers ist bei EGIS nicht freigeschaltet (IP-Whitelist). EGIS-Support kontaktieren.</li>
              </ol>
            </div>
          )}
          {!result.success && (result.httpStatus === 401 || result.httpStatus === 403) && (
            <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-xs">
              Login oder Passwort ungültig — bitte in den EGIS-Einstellungen prüfen.
            </div>
          )}
          <button
            className="text-xs underline opacity-70"
            onClick={() => setShowXml((v) => !v)}
          >
            {showXml ? 'Details ausblenden' : 'XML + Response anzeigen'}
          </button>
          {showXml && (
            <div className="space-y-2 mt-1">
              <div>
                <div className="text-xs font-medium opacity-60 mb-1">Gesendetes XML:</div>
                <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap text-gray-800">{result.xmlSent}</pre>
              </div>
              <div>
                <div className="text-xs font-medium opacity-60 mb-1">Server-Antwort:</div>
                <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap text-gray-800">{result.responseBody}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

